import { cachedPlacesFetch, PLACES_TTL } from "./places-http-cache.server";
import { filterSpecificPlaces } from "@/lib/place-term-classification";
import { createServerFn } from "@tanstack/react-start";
import { runWithGoogleCallContext } from "./google-call-context.server";
import { setGenerationBudgetForDays } from "./google-provider-gateway.server";

import { z } from "zod";
import {
  boundsRadiusMeters,
  areaContains,
  type Bounds,
  type ActiveSearchArea,
} from "./active-search-area";
import {
  generationAreaRadiusMeters,
  resolveGenerationArea,
  type DestinationIdentity,
} from "./generation-area";
import { createRouteTrace } from "./route-trace.server";
import {
  classifyGenerationOutcome,
  type GenerationResultLike,
  type TechnicalFailure,
} from "./generation-outcome";
import { collectDiversifiedCandidates, type DiversifiedCollectionResult } from "./area-candidate-collection.server";
import {
  createPlacesBudget,
  isPlacesKillSwitchActive,
  type PlacesGenerationBudget,
} from "./places-cost-guard.server";
import { isDiversifiedCollectionAllowed } from "./area-candidate-collection";
import {
  StopResolutionIndex,
  dedupeByResolutionKey,
  resolutionKey,
} from "./stop-resolution-index";
import { readCatalogPlaceByName } from "./place-catalog.server";
import type { CatalogBounds } from "./place-catalog";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  parseMandatoryPlacesFromNotes,
  parseNamedRouteFromNotes,
  parseRouteScopeFromNotes,
  routeScopePolicy,
} from "./route-scope-policy";
import { isOverlyGenericPlaceName, minimumTotalStopCount, minimumUsefulStopCount, minimumViableStopsForDay, scoreRouteLegs, selectCompactRouteCandidates, targetItineraryStopsForDay, targetTotalStopCount, type RouteOrderMode } from "./route-order-policy";
import { tripPlanningWindow } from "./trip-duration-policy";
import { isSameRoutePlace, placeNamesMatch, trimOptionalStopsToBudget } from "./route-constraint-policy";
import { planDurationFill } from "./duration-fill";
import { comparableCityName } from "./city-endonym";
import { categoryFocusDisplayLabel } from "./category-focus-labels";
import { shouldSuppressUnresolvedCategory } from "./unresolved-category-policy";
import { isChargeableItineraryResult } from "./quota-reservation-policy";
import { computeEffectiveInterests } from "./effective-interests";
import { placesSearchQuery, tagTourismFamilyConstraints } from "./required-constraints";
import { matchConstraintsToStops, stopSatisfiesConstraint } from "./constraint-satisfaction";
import { computeFoodIntent, isFoodPlace } from "./food-intent";
import { evaluateVisitDiversity } from "./visit-diversity";
import { acceptsCalmSmallPlaceRoute, assessGenericRouteCompleteness, classifyScarcityCause } from "./generic-route-completeness";
import { assembleDeterministicRoute } from "./deterministic-route-assembly";
import { assignStopsAcrossDays, type DayAnchor } from "./day-geo-assignment";
import { candidateFitsVisitingWindow } from "./place-availability";


function safeFailureCode(error: unknown): string {
  const base =
    error instanceof z.ZodError
      ? "validation_failed"
      : error instanceof Error
        ? error.name.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase()
        : "exception";
  const message =
    error instanceof z.ZodError
      ? error.issues
          .map((issue) => `${issue.path.join(".")}:${issue.code}`)
          .join(",")
      : error instanceof Error
        ? error.message.replace(/[^a-zA-Z0-9_\s]/g, " ").slice(0, 60)
        : "";
  const code = message ? `${base}:${message}` : base;
  return code.slice(0, 80);
}

const Input = z.object({
  city: z.string().min(1).max(120),
  country: z.string().max(120).default(""),
  lat: z.number().finite(),
  lng: z.number().finite(),
  language: z.string().min(2).max(8),
  currency: z.string().min(3).max(4),
  durationHours: z.number().finite().min(0.5).max(200).default(3),
  requestedDays: z.number().int().finite().min(1).max(14).optional(),
  mode: z.enum(["walk", "scooter", "bike", "car", "transit"]).default("walk"),
  localMode: z.enum(["walk", "scooter", "bike", "car", "transit"]).optional(),
  interests: z.array(z.string().min(1).max(40)).max(10).default([]),
  /** Texto original do pedido, com prioridade sobre interesses/chips. */
  requestText: z.string().max(600).default(""),
  /** Interesses detetados no texto deste pedido. */
  textInterests: z.array(z.string().min(1).max(40)).max(10).default([]),
  /** Interesses explicitamente selecionados nos chips deste pedido. */
  explicitInterests: z.array(z.string().min(1).max(40)).max(10).default([]),
  /** Ação "Ver menos paragens por dia" do cartão de escassez de visita. */
  reducedStopsPerDay: z.boolean().default(false),
  notes: z.string().max(1200).default(""),
  startPlace: z.string().min(2).max(160).optional(),
  startLat: z.number().finite().min(-90).max(90).optional(),
  startLng: z.number().finite().min(-180).max(180).optional(),
  namedRoute: z.string().min(2).max(160).optional(),
  requiredPlaces: z.array(z.string().min(2).max(160)).max(12).default([]),
  requiredStopConstraints: z.array(z.object({
    kind: z.enum(["restaurant", "activity", "cafe", "bakery", "shop", "museum", "park", "viewpoint", "hotel"]),
    cuisine: z.string().max(40).optional(),
    activityType: z.string().max(40).optional(),
    query: z.string().min(1).max(160),
    source: z.enum(["tourism_family", "named_place", "food", "text"]),
    tourismFamily: z.enum([
      "museums_culture", "viewpoints_panoramas", "monuments_heritage", "nature_parks",
      "water_beaches", "neighbourhoods_markets", "stadiums_architecture",
      "activities_attractions", "special_places",
    ]).optional(),
  })).max(12).default([]),
  /**
   * Paragens fixas escolhidas pela pessoa ("Escolher este local"): apenas
   * identidade. O servidor re-confirma via Place Details e injeta como
   * paragem obrigatória, sujeita à geometria da área alargada confirmada.
   */
  pinnedStops: z.array(z.object({
    constraintId: z.string().min(1).max(80),
    placeId: z.string().min(1).max(200),
  })).max(6).default([]),
  geographicScope: z.enum(["city", "regional", "named_route", "multi_city"]).optional(),
  /**
   * Área alargada confirmada pelo utilizador: concreta, nomeada e com raio
   * limitado. Nunca é um simples "alargar tudo".
   */
  expandedArea: z
    .object({
      label: z.string().min(1).max(160),
      localities: z.array(z.string().min(1).max(120)).max(6).default([]),
      center: z
        .object({
          lat: z.number().min(-90).max(90).finite(),
          lng: z.number().min(-180).max(180).finite(),
        })
        .optional(),
      // O alargamento genérico fica em 3–8 km; a etapa de escolha fixa de um
      // candidato próximo ("Escolher este local") pode ir até 25 km.
      radiusMeters: z.number().int().min(500).max(25000),
    })
    .nullish(),
  anonId: z.string().max(80).default(""),
  requestId: z.string().min(16).max(80).optional(),
  /** Identificador criado pelo cliente antes da chamada (correlação de logs). */
  generationRequestId: z.string().min(4).max(80).optional(),
  /** Origem da continuação (cartão de escassez): initial, widen_area, shorten_duration, retry. */
  generationContinuation: z.enum(["initial", "widen_area", "shorten_duration", "retry"]).optional(),
  // Optional schedule context so targetCount can subtract meal pauses and
  // average transit time from the user's total available window.
  startTime: z.string().regex(/^\d{1,2}:\d{2}$/).optional(),
  // Optional user GPS — when present and inside the city, day 1 starts here.
  userLat: z.number().finite().min(-90).max(90).optional(),
  userLng: z.number().finite().min(-180).max(180).optional(),
  mealPrefs: z.object({
    lunchStart: z.string(),
    lunchEnd: z.string(),
    dinnerStart: z.string(),
    dinnerEnd: z.string(),
    eatMinutes: z.number().finite().min(0).max(240),
  }).partial().optional(),
  /**
   * Logística de chegada confirmada pelo cliente (cidade/hotel/aeroporto + hora).
   * Consumida no planeamento: o dia 1 perde os minutos de desembarque,
   * transferência e check-in antes da primeira visita.
   */
  arrivalLogistics: z.object({
    kind: z.enum(["city", "hotel", "airport"]),
    timeMinutes: z.number().int().min(0).max(1440),
    includeCheckin: z.boolean().optional(),
  }).optional(),
  exhaustive: z.boolean().optional().default(false),
  categoryFocus: z.enum([
    "cafe","restaurant","hotel","viewpoint","museum","monument","church","park","nature","water","shopping","nightlife","market","sweets","breakfast",
  ]).optional(),
  requestedStopCategory: z.enum([
    "cafe","restaurant","hotel","viewpoint","museum","monument","church","park","nature","water","shopping","nightlife","market","sweets","breakfast",
  ]).optional(),
  requestedStopQuery: z.string().trim().min(2).max(100).optional(),
  categoryFocusQuery: z.string().trim().min(2).max(100).optional(),
  /** Cozinha explícita pedida ("restaurantes japoneses"): condição vinculativa. */
  cuisine: z.string().trim().min(3).max(60).optional(),
  /** Localidade explícita escrita pelo utilizador ("Leça da Palmeira"). */
  explicitLocality: z.string().trim().min(2).max(120).optional(),
  /**
   * Decisão vinculativa do cliente (src/lib/food-intent.ts): dois sinais
   * SEPARADOS. `foodIntent` (alias exato de `foodStopIntent`) controla
   * comida como paragem de visita — nunca deriva do rádio de refeições.
   * `genericVisitMode` ativa a diversidade/escassez de visita.
   */
  foodIntent: z.boolean().optional(),
  foodStopIntent: z.boolean().optional(),
  /** Sinal independente de almoço/jantar — NUNCA ativa paragens de comida. */
  mealMode: z.enum(["restaurant_suggestions", "free_break", "none"]).optional(),
  genericVisitMode: z.boolean().optional(),
  /**
   * Identidade geográfica do destino resolvida pelo geocoder (providerId +
   * geometria). A correspondência por texto nunca define a área: é apenas
   * diagnóstico depois de a geometria decidir.
   */
  destinationIdentity: z
    .object({
      provider: z.enum(["osm", "google"]).optional(),
      providerId: z.string().max(200).optional(),
      requestedName: z.string().max(160),
      displayName: z.string().max(160).optional(),
      countryCode: z.string().max(4).optional(),
      center: z.object({ lat: z.number().finite(), lng: z.number().finite() }).optional(),
      bounds: z
        .object({
          north: z.number().finite(),
          south: z.number().finite(),
          east: z.number().finite(),
          west: z.number().finite(),
        })
        .optional(),
      precision: z.enum(["rooftop", "precise", "locality_center", "approximate"]).optional(),
      aliases: z.array(z.string().max(160)).max(12).default([]),
    })
    .nullish(),
});


const hhmm = (s: string, fallback: number) => {
  const [h, m] = (s || "").split(":").map((n) => Number.parseInt(n, 10));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return fallback;
  return h * 60 + m;
};

export type AIStop = {
  name: string;
  category: string;
  blurb: string;
  priceLocal: number;
  rating: number;
  day?: number;
  placeId?: string;
  photoUrl?: string;
  lat?: number;
  lng?: number;
  /** Produto confirmado com prova específica (nunca por inferência). */
  specialtyLabel?: string;
  /** Razão legível da prova. */
  specialtyEvidence?: string;
  specialtyEvidenceLevel?: "product" | "category" | "none";
  /** Alvo específico pedido não confirmado neste local. */
  specialtyAlternative?: boolean;
  /** Prova do Places transportada até à validação final. */
  placeTypes?: string[];
  placePrimaryType?: string;
  placeLocalityNames?: string[];
  /**
   * Paragem escolhida como experiência noturna do dia (só modo genérico):
   * movida para o fim do dia, com o jantar ancorado antes dela na agenda.
   */
  evening?: boolean;
  /** Horários habituais do Places, usados pela barreira final do horário. */
  openingPeriods?: OpeningPeriod[] | null;
  /** Paragem obrigatória (pedido explícito / local fixado). */
  required?: boolean;
};


import {
  detectExplicitTourismFamilies,
  filterStopsByFocus,
  filterStopsByTourismFamilies,
  SINGLE_INTEREST_FOCUS,
  type FocusCategory,
} from "@/lib/exclusive-category";
import {
  collectExplicitFamilyCandidates,
  type FamilyCollectionResult,
} from "./family-collection.server";
import {
  classifyPlaceForFamily,
  placeMatchesAnyTourismFamily,
  tourismFamilyLabel,
  type TourismFamilyId,
} from "@/lib/tourism-taxonomy";
import {
  parseNegativePreferences,
  isCategoryExcluded,
  guessCategoryFromText,
  type NegativePreferences,
} from "@/lib/negative-preferences";
import { detectLocalSpecialtyIntent } from "@/lib/local-specialty-intent";
import {
  isBreakfastEligible,
  matchesBreakfastCategory,
  type BreakfastCandidate,
  type OpenInterval,
} from "@/lib/breakfast-eligibility";
import {
  DEFAULT_BREAKFAST_WINDOW,
  DEFAULT_BRUNCH_WINDOW,
  type BreakfastWindow,
} from "@/lib/breakfast-intent";
import { annotateStopsWithSpecialty } from "@/lib/local-specialty";
import {
  pickEveningStop,
  type OpeningPeriod,
  type EveningCandidate,
  type EveningSuitability,
} from "@/lib/evening-suitability";
import { rebalanceDayQuality } from "@/lib/day-quality";
import {
  LANDING_BAGGAGE_MIN,
  AIRPORT_TRANSFER_DEFAULT_MIN,
  HOTEL_CHECKIN_MIN,
} from "@/lib/arrival-window";
import { detectCuisineIntent, cuisineSearchQuery, type CuisineIntent } from "@/lib/cuisine-intent";
import { placeMatchesCuisine, requestsMarket } from "@/lib/cuisine-filter";
import {
  detectRestaurantsOnlyRequest,
  mentionsAttractions,
  validateFinalPlaces,
} from "@/lib/route-final-validation";



const PLACES_BASE = "https://places.googleapis.com/v1";
const MAPS_BASE = "https://maps.googleapis.com";

const languageInstruction = (language: string) => {
  if (language.toLowerCase().startsWith("pt")) {
    return 'Português de Portugal (pt-PT). Não uses português do Brasil: evita "você", "ônibus", "trem"; usa "tu", "autocarro", "comboio" quando aplicável.';
  }
  return `language code "${language}"`;
};

const placesServerKey = () => process.env.GOOGLE_MAPS_SERVER_API_KEY || process.env.GOOGLE_MAPS_API_KEY || "";

const haversineMeters = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
};

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const locationRectangle = (lat: number, lng: number, radiusMeters: number) => {
  const latDelta = radiusMeters / 111_320;
  const lngDelta = radiusMeters / (111_320 * Math.max(0.2, Math.cos((lat * Math.PI) / 180)));
  return {
    low: {
      latitude: clamp(lat - latDelta, -90, 90),
      longitude: clamp(lng - lngDelta, -180, 180),
    },
    high: {
      latitude: clamp(lat + latDelta, -90, 90),
      longitude: clamp(lng + lngDelta, -180, 180),
    },
  };
};

const normalizeName = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

type GoogleAddressComponent = {
  longText?: string;
  shortText?: string;
  long_name?: string;
  short_name?: string;
  types?: string[];
};

const ADMIN_LOCALITY_TYPES = new Set([
  "locality",
  "postal_town",
  "administrative_area_level_3",
  "administrative_area_level_4",
  "administrative_area_level_5",
  "sublocality",
  "sublocality_level_1",
  "sublocality_level_2",
  "neighborhood",
]);

/**
 * Níveis administrativos AMPLOS. Normalmente não podem validar uma paragem
 * (uma região inteira não é a cidade pedida), mas há cidades que SÃO o próprio
 * nível administrativo: municípios sob governo central (Pequim, Xangai,
 * Tianjin, Chongqing), cidades-estado (Singapura, Hong Kong, Macau) e
 * cidades-estado federais (Berlim, Viena, Hamburgo). Nesses casos a Google
 * devolve o nome da cidade em administrative_area_level_1/2 e os níveis
 * inferiores são distritos — sem esta excepção todas as paragens seriam
 * rejeitadas.
 */
const BROAD_ADMIN_TYPES = new Set([
  "administrative_area_level_1",
  "administrative_area_level_2",
]);

/** Perfil administrativo da localidade pedida (resolvido pelo geocoder). */
type LocalityProfile = { extraTypes: Set<string>; aliases: string[] };

const localityProfiles = new Map<string, LocalityProfile>();

const localityProfileKey = (city: string, country: string) =>
  `${normalizeName(city)}|${normalizeName(country)}`;

/**
 * Aliases aceitáveis para a localidade PEDIDA: apenas nomes ao nível da própria
 * localidade/freguesia. O concelho (administrative_area_level_3, ex. Matosinhos)
 * é uma área maior e nunca pode servir de alias de "Leça da Palmeira".
 */
const NARROW_LOCALITY_TYPES = new Set([
  "locality",
  "postal_town",
  "sublocality",
  "sublocality_level_1",
  "sublocality_level_2",
  "neighborhood",
  "administrative_area_level_5",
]);


const stripLocalityQualifiers = (value: string) =>
  normalizeName(value)
    .replace(
      /\b(?:municipio|municipality|concelho|county|district|distrito|province|provincia|region|regiao|cidade|city|vila|town|aldeia|village|freguesia|parish)\b/g,
      " ",
    )
    .replace(/\b(?:de|da|do|das|dos|of|the)\b/g, " ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const componentNames = (component: GoogleAddressComponent) => [
  component.longText,
  component.shortText,
  component.long_name,
  component.short_name,
].filter((v): v is string => !!v && v.trim().length > 0);

const componentHasType = (component: GoogleAddressComponent, types: Set<string>) =>
  (component.types ?? []).some((type) => types.has(type));

function localityNameMatches(candidate: string, requestedCity: string) {
  const a = stripLocalityQualifiers(comparableCityName(candidate));
  const b = stripLocalityQualifiers(comparableCityName(requestedCity));
  if (!a || !b) return false;
  if (a === b) return true;
  // Allow harmless prefixes/suffixes like "Município de X", but never match
  // against broad level-1 regions (we do not include those component types).
  if (b.length >= 5 && (a.includes(b) || b.includes(a))) return true;
  return false;
}

/**
 * Comparação de países entre geocoders diferentes. Os nomes mudam de língua
 * ("Estados Unidos da América" vs "United States"), por isso a comparação
 * fiável é pelo CÓDIGO ISO (shortText "US"). Uma diferença apenas de nome
 * NUNCA pode invalidar todas as paragens de uma cidade — nesse caso ficamos
 * pela verificação de localidade, que já é restritiva.
 */
function countryComponentContradicts(
  component: GoogleAddressComponent,
  requestedCountryCode: string,
) {
  if (!requestedCountryCode) return false;
  const codes = componentNames(component)
    .map((name) => name.trim().toUpperCase())
    .filter((name) => /^[A-Z]{2}$/.test(name));
  if (!codes.length) return false;
  return !codes.includes(requestedCountryCode.toUpperCase());
}

export function registerLocalityProfile(
  city: string,
  country: string,
  profile: { extraTypes: string[]; aliases: string[] },
): void {
  localityProfiles.set(localityProfileKey(city, country), {
    extraTypes: new Set(profile.extraTypes),
    aliases: profile.aliases,
  });
}

export function addressComponentsInsideRequestedLocality(
  components: GoogleAddressComponent[],
  context: { city: string; country: string; countryCode?: string },
) {
  const countryComponents = components.filter((c) => (c.types ?? []).includes("country"));
  if (
    context.countryCode &&
    countryComponents.length &&
    countryComponents.every((component) => countryComponentContradicts(component, context.countryCode!))
  ) {
    return false;
  }

  // Quando a própria cidade pedida é um nível administrativo amplo (município
  // sob governo central, cidade-estado), aceitamos esse nível — só para ela.
  const profile = localityProfiles.get(localityProfileKey(context.city, context.country));
  const acceptedTypes = profile?.extraTypes.size
    ? new Set([...ADMIN_LOCALITY_TYPES, ...profile.extraTypes])
    : ADMIN_LOCALITY_TYPES;
  const matchesRequested = (name: string) =>
    localityNameMatches(name, context.city) ||
    (profile?.aliases ?? []).some((alias) => localityNameMatches(name, alias));

  return components
    .filter((component) => componentHasType(component, acceptedTypes))
    .some((component) => componentNames(component).some(matchesRequested));
}

const adminBoundaryCache = new Map<string, boolean>();

/**
 * Verificações de localidade que não puderam correr (bloqueadas por teto,
 * erro ou timeout). Serve para o diagnóstico distinguir "a zona não tem
 * locais" de "não conseguimos confirmar a localidade".
 */
let localityCheckUnavailable = 0;

/** Paragens realmente rejeitadas pela verificação de localidade. */
let localityRejections = 0;

export function resetLocalityCheckDiagnostics(): void {
  localityCheckUnavailable = 0;
  localityRejections = 0;
}

export function localityCheckUnavailableCount(): number {
  return localityCheckUnavailable;
}

export function localityRejectionCount(): number {
  return localityRejections;
}

async function isCoordinateInsideRequestedLocality(
  coord: { lat: number; lng: number },
  context: { city: string; country: string; countryCode?: string; language?: string },
) {
  const key = placesServerKey();
  if (!key) return true;
  // Grelha de ~100 m: coordenadas vizinhas pertencem à mesma localidade
  // administrativa, por isso reutilizam a resposta em vez de pagar outra
  // geocodificação inversa por cada paragem.
  const cacheKey = `${normalizeName(context.city)}|${normalizeName(context.country)}|${coord.lat.toFixed(3)},${coord.lng.toFixed(3)}`;
  const cached = adminBoundaryCache.get(cacheKey);
  if (typeof cached === "boolean") return cached;

  // Atalho sem custo: se a coordenada já está dentro dos limites geocodificados
  // da cidade pedida, não é preciso pagar uma geocodificação inversa. Resolve
  // também o perfil administrativo da cidade (usado na comparação abaixo).
  const area = await resolveLocalityArea(context.city, context.country, context.language);
  if (area?.bounds && insideLocalityBounds(coord, area.bounds)) {
    adminBoundaryCache.set(cacheKey, true);
    return true;
  }

  try {
    const url = new URL(`${MAPS_BASE}/maps/api/geocode/json`);
    url.searchParams.set("latlng", `${coord.lat},${coord.lng}`);
    url.searchParams.set("key", key);
    if (context.language) {
      url.searchParams.set("language", context.language.toLowerCase().startsWith("pt") ? "pt-PT" : context.language);
    }
    const res = await cachedPlacesFetch(url.toString(), { method: "GET" }, {
      kind: "geocodeReverse",
      ttlMs: PLACES_TTL.geocodeReverse,
      origin: "geocode_reverse",
      sku: "reverse_geocoding",
    });
    if (!res.ok) {
      // A verificação não pôde correr (teto de custo, erro ou indisponibilidade
      // da Google). Uma verificação que não correu NUNCA rejeita a paragem: ela
      // já foi validada por estar dentro da área de pesquisa ativa. Também não
      // entra em cache — senão contaminava todas as paragens vizinhas.
      localityCheckUnavailable += 1;
      console.warn("Reverse geocode locality check unavailable", res.status);
      return true;
    }
    const json = (await res.json()) as {
      results?: Array<{ address_components?: GoogleAddressComponent[] }>;
      status?: string;
    };
    const inside = (json.results ?? []).some((result) =>
      addressComponentsInsideRequestedLocality(result.address_components ?? [], context),
    );
    if (!inside) {
      localityRejections += 1;
      console.warn(`Rejected coordinate outside administrative locality ${context.city}: ${coord.lat.toFixed(5)},${coord.lng.toFixed(5)}`);
    }
    adminBoundaryCache.set(cacheKey, inside);
    return inside;
  } catch (e) {
    // Erro/timeout: igual ao bloqueio — não rejeita nem cacheia.
    localityCheckUnavailable += 1;
    console.warn("Reverse geocode locality check errored", e);
    return true;
  }
}

/* ---- Área geocodificada da localidade explícita (validação final) ---- */

type LocalityArea = {
  /** Viewport devolvido pelo geocoder para a localidade pedida. */
  bounds: { north: number; south: number; east: number; west: number } | null;
  /** Nomes/aliases administrativos devolvidos pelo geocoder. */
  aliases: string[];
};

const localityAreaCache = new Map<string, LocalityArea | null>();

async function resolveLocalityArea(
  locality: string,
  country: string,
  language?: string,
): Promise<LocalityArea | null> {
  const key = placesServerKey();
  if (!key || !locality.trim()) return null;
  const cacheKey = `${normalizeName(locality)}|${normalizeName(country)}`;
  if (localityAreaCache.has(cacheKey)) return localityAreaCache.get(cacheKey) ?? null;
  try {
    const url = new URL(`${MAPS_BASE}/maps/api/geocode/json`);
    url.searchParams.set("address", country ? `${locality}, ${country}` : locality);
    url.searchParams.set("key", key);
    if (language) {
      url.searchParams.set("language", language.toLowerCase().startsWith("pt") ? "pt-PT" : language);
    }
    const res = await cachedPlacesFetch(url.toString(), { method: "GET" }, {
      kind: "geocodeForward",
      ttlMs: PLACES_TTL.geocodeForward,
      origin: "geocode_forward",
      sku: "geocoding",
    });
    if (!res.ok) {
      // Falha técnica: não cacheia (senão a área fica por resolver o resto da
      // geração). Sem área resolvida a validação segue sem restrição extra.
      localityCheckUnavailable += 1;
      return null;
    }
    const json = (await res.json()) as {
      results?: Array<{
        address_components?: GoogleAddressComponent[];
        geometry?: {
          viewport?: { northeast?: { lat: number; lng: number }; southwest?: { lat: number; lng: number } };
          bounds?: { northeast?: { lat: number; lng: number }; southwest?: { lat: number; lng: number } };
        };
      }>;
    };
    const first = json.results?.[0];
    if (!first) {
      localityAreaCache.set(cacheKey, null);
      return null;
    }
    const box = first.geometry?.bounds ?? first.geometry?.viewport;
    const bounds =
      box?.northeast && box?.southwest
        ? {
            north: box.northeast.lat,
            south: box.southwest.lat,
            east: box.northeast.lng,
            west: box.southwest.lng,
          }
        : null;
    // Só entram nomes que são VARIANTES do nome pedido (ex. "Leca da Palmeira").
    // Um nome diferente (o concelho "Matosinhos") nunca é alias da localidade.
    const requestedKey = normalizeName(locality);
    const aliases = (first.address_components ?? [])
      .filter((component) => componentHasType(component, NARROW_LOCALITY_TYPES))
      .flatMap((component) => componentNames(component))
      .filter((name) => {
        const k = normalizeName(name);
        return k === requestedKey || k.includes(requestedKey) || requestedKey.includes(k);
      });
    // Perfil administrativo: em que nível é que a Google devolve o NOME da
    // cidade pedida. Se for um nível amplo (Pequim → administrative_area_level_1),
    // esse nível passa a ser aceite na validação — apenas para esta cidade.
    const matchingComponents = (first.address_components ?? []).filter((component) =>
      componentNames(component).some((name) => localityNameMatches(name, locality)),
    );
    const extraTypes = new Set(
      matchingComponents.flatMap((component) => component.types ?? []).filter((type) => BROAD_ADMIN_TYPES.has(type)),
    );
    const officialAliases = Array.from(
      new Set(matchingComponents.flatMap((component) => componentNames(component))),
    );
    localityProfiles.set(localityProfileKey(locality, country), {
      extraTypes,
      aliases: officialAliases,
    });

    const area: LocalityArea = {
      bounds,
      aliases: Array.from(new Set([locality, ...aliases, ...officialAliases])),
    };

    localityAreaCache.set(cacheKey, area);
    return area;
  } catch (e) {
    console.warn("resolveLocalityArea failed", e);
    localityAreaCache.set(cacheKey, null);
    return null;
  }
}

const insideLocalityBounds = (
  coord: { lat: number; lng: number },
  bounds: LocalityArea["bounds"],
) =>
  !!bounds &&
  coord.lat >= bounds.south &&
  coord.lat <= bounds.north &&
  coord.lng >= bounds.west &&
  coord.lng <= bounds.east;

/** Tipos/administrativos do Places para validação final (só quando é preciso). */
async function fetchPlaceEvidence(placeId: string, language?: string): Promise<{
  primaryType?: string;
  types?: string[];
  localityNames?: string[];
} | null> {
  const key = placesServerKey();
  if (!key) return null;
  try {
    const res = await cachedPlacesFetch(
      `${PLACES_BASE}/places/${encodeURIComponent(placeId)}`,
      {
        method: "GET",
        headers: {
          "X-Goog-Api-Key": key,
          "X-Goog-FieldMask": "types,primaryType,addressComponents",
          ...(language ? { "Accept-Language": language } : {}),
        },
      },
      { kind: "attractions", ttlMs: PLACES_TTL.attractions, origin: "route_place_details", sku: "place_details" },
    );
    if (!res.ok) return null;
    const json = (await res.json()) as {
      types?: string[];
      primaryType?: string;
      addressComponents?: GoogleAddressComponent[];
    };
    return {
      primaryType: json.primaryType,
      types: json.types ?? [],
      localityNames: (json.addressComponents ?? [])
        .filter((component) => componentHasType(component, ADMIN_LOCALITY_TYPES))
        .flatMap((component) => componentNames(component)),
    };
  } catch (e) {
    console.warn("fetchPlaceEvidence failed", e);
    return null;
  }
}



const isFoodPlaceType = (types: string[] = []) =>
  types.some((t) => /restaurant|cafe|bar|bakery|food|meal_|coffee|pub|brewery|winery|ice_cream/.test(t.toLowerCase()));

const isFoodLikeCategory = (category = "") =>
  /restaurante|restaurant|cafe|café|bar|food|comida|pastelaria|bakery|tasca|brewery|pub/i.test(category);

const isClearlyWrongGlobalLandmark = (name: string, context: { city: string; country: string }) => {
  const n = normalizeName(name);
  const city = normalizeName(context.city);
  const country = normalizeName(context.country);
  const inParis = city.includes("paris") || country.includes("france") || country.includes("franca");
  if (!inParis && /(rue cler|torre eiffel|eiffel tower|\beiffel\b|champ de mars|campo de marte|museu do louvre|louvre|arc de triomphe|arco do triunfo|montmartre|sacre[- ]?coeur|notre dame)/.test(n)) {
    return true;
  }
  if (!city.includes("lond") && !country.includes("united kingdom") && !country.includes("reino unido") && /(big ben|tower bridge|buckingham)/.test(n)) return true;
  if (!city.includes("roma") && !city.includes("rome") && !country.includes("ital") && /(coliseu|colosseum|fontana di trevi|trevi fountain)/.test(n)) return true;
  if (!city.includes("new york") && !country.includes("united states") && !country.includes("estados unidos") && /(statue of liberty|estatua da liberdade|times square|central park)/.test(n)) return true;
  if (!city.includes("barcelona") && !country.includes("spain") && !country.includes("espanha") && /(sagrada familia|park guell|parc guell)/.test(n)) return true;
  return false;
};

const fallbackCategory = (types: string[] = [], language = "pt") => {
  const pt = language.toLowerCase().startsWith("pt");
  if (types.some((t) => t.includes("cafe") || t.includes("coffee") || t.includes("bakery") || t.includes("confectionery"))) {
    return pt ? "Café / pastelaria" : "Café / pastry shop";
  }
  if (types.some((t) => t.includes("restaurant") || t.includes("food") || t.includes("meal"))) return pt ? "Restaurante" : "Food";
  if (types.some((t) => t.includes("museum"))) return pt ? "Museu" : "Museum";
  if (types.some((t) => t.includes("park") || t.includes("garden"))) return pt ? "Jardim" : "Nature";
  if (types.some((t) => t.includes("church") || t.includes("place_of_worship"))) return pt ? "Património" : "Heritage";
  if (types.some((t) => t.includes("viewpoint") || t.includes("tourist_attraction"))) return pt ? "Ponto de interesse" : "Sight";
  return pt ? "Ponto de interesse" : "Sight";
};

// Reject "places" returned by Google that are actually just addresses,
// street segments or administrative areas — never a real thing to visit.
const NON_TOURIST_ONLY_TYPES = new Set([
  "street_address", "route", "premise", "subpremise", "plus_code", "geocode",
  "postal_code", "locality", "sublocality", "sublocality_level_1", "sublocality_level_2",
  "neighborhood", "political", "intersection", "administrative_area_level_1",
  "administrative_area_level_2", "administrative_area_level_3",
]);
const TOURIST_WORTH_TYPES = new Set([
  "tourist_attraction", "museum", "art_gallery", "park", "garden", "church",
  "cathedral", "mosque", "synagogue", "hindu_temple", "place_of_worship",
  "landmark", "historical_landmark", "monument", "natural_feature", "zoo",
  "aquarium", "amusement_park", "stadium", "plaza", "square", "market",
  "library", "casino", "theater", "movie_theater", "shopping_mall",
  "viewpoint", "lodging", "restaurant", "cafe", "bar", "bakery",
  "ice_cream_shop", "book_store",
]);
const isRealTouristPlace = (name: string, types: string[] = []) => {
  if (isOverlyGenericPlaceName(name)) return false;
  const ts = (types ?? []).map((t) => String(t).toLowerCase());
  const looksLikeStreetAddress = /^\s*[\p{L}\s.'-]+\s+\d+[a-z]?\s*$/iu.test(name);
  if (!ts.length) return !looksLikeStreetAddress;
  if (ts.some((t) => TOURIST_WORTH_TYPES.has(t))) return true;
  const onlyNoise = ts.every((t) => NON_TOURIST_ONLY_TYPES.has(t) || t === "point_of_interest" || t === "establishment");
  if (onlyNoise) return false;
  return !looksLikeStreetAddress;
};

const localizeCategory = (category = "", types: string[] = [], language = "pt") => {
  if (!language.toLowerCase().startsWith("pt")) return category || fallbackCategory(types, language);
  const c = normalizeName(category);
  if (c.includes("food") || c.includes("restaurant") || c.includes("restaurante") || c.includes("comida")) return "Restaurante";
  if (c.includes("view") || c.includes("miradouro") || c.includes("vista")) return "Miradouro";
  if (c.includes("museum") || c.includes("museu")) return "Museu";
  if (c.includes("park") || c.includes("nature") || c.includes("jardim") || c.includes("parque")) return "Jardim";
  if (c.includes("church") || c.includes("cathedral") || c.includes("igreja") || c.includes("patrimonio")) return "Património";
  return category && !/^[A-Z_ -]+$/.test(category) ? category : fallbackCategory(types, language);
};

const sanitizeBlurb = (blurb: string, language = "pt") => {
  if (!language.toLowerCase().startsWith("pt")) return blurb;
  return blurb
    .replace(/\bvocê\b/gi, "tu")
    .replace(/\bvocês\b/gi, "vocês")
    .replace(/\bônibus\b/gi, "autocarro")
    .replace(/\btrem\b/gi, "comboio")
    .replace(/\bmetrô\b/gi, "metro")
    .replace(/\bparada\b/gi, "paragem");
};

const fallbackBlurb = (name: string, city: string, language = "pt") => {
  const l = language.toLowerCase();
  if (l.startsWith("pt")) return `${name} é uma paragem real em ${city}, validada por mapa, escolhida para manter o roteiro dentro da cidade e do tempo disponível.`;
  if (l.startsWith("es")) return `${name} es una parada real en ${city}, validada en el mapa para mantener la ruta dentro de la ciudad.`;
  if (l.startsWith("fr")) return `${name} est une vraie étape à ${city}, vérifiée sur la carte pour garder l’itinéraire dans la ville.`;
  return `${name} is a real stop in ${city}, map-verified to keep the itinerary inside the city.`;
};

type Coord = { lat: number; lng: number };

type EndConstraint = {
  query: string;
  name: string;
  lat: number;
  lng: number;
  types: string[];
};

const isAirportEndpoint = (endpoint: Pick<EndConstraint, "types" | "query" | "name">) => {
  const text = normalizeName(`${endpoint.query} ${endpoint.name}`);
  return endpoint.types.some((t) => t.toLowerCase() === "airport") || /aeroporto|airport/.test(text);
};

const isTransitEndpoint = (endpoint: Pick<EndConstraint, "types" | "query" | "name">) => {
  const text = normalizeName(`${endpoint.query} ${endpoint.name}`);
  return endpoint.types.some((t) => /train_station|transit_station|subway_station|light_rail_station/.test(t.toLowerCase())) ||
    /estacao|station|comboio|train|rail|metro|subway/.test(text);
};

function canonicalEndConstraint(query: string, context: { city: string; country: string }): EndConstraint | null {
  const q = normalizeName(query);
  const city = normalizeName(context.city);
  const country = normalizeName(context.country);
  const inLisbon = /lisboa|lisbon/.test(city) || (/portugal/.test(country) && /lisboa|lisbon/.test(q));
  const inPorto = /porto|oporto/.test(city) || (/portugal/.test(country) && /porto/.test(q));

  if (inLisbon && /\boriente\b/.test(q)) {
    return { query, name: "Estação do Oriente", lat: 38.76783, lng: -9.09914, types: ["train_station", "transit_station"] };
  }
  if (inPorto && /campanha|campanhã/.test(q)) {
    return { query, name: "Estação de Campanhã", lat: 41.15063, lng: -8.58531, types: ["train_station", "transit_station"] };
  }
  if (inPorto && /sao bento|são bento/.test(q)) {
    return { query, name: "Estação de São Bento", lat: 41.14563, lng: -8.61099, types: ["train_station", "transit_station"] };
  }
  if (/aeroporto|airport/.test(q)) {
    if (inLisbon) return { query, name: "Aeroporto Humberto Delgado", lat: 38.77420, lng: -9.13420, types: ["airport"] };
    if (inPorto) return { query, name: "Aeroporto Francisco Sá Carneiro", lat: 41.24810, lng: -8.68139, types: ["airport"] };
  }
  return null;
}

const cleanEndpointQuery = (raw: string) => raw
  .replace(/[.!?;]+$/g, "")
  .replace(/^\s*(?:d[aoe]s?|a|o|as|os|the)\s+/i, "")
  .replace(/\s+(?:às|as|at)\s+\d{1,2}(?::\d{2})?\s*(?:h|am|pm)?\b.*$/i, "")
  .replace(/\s+(?:para|pra|to)\s+(?:ver|apanhar|pegar|ir|catch|take)\b.*$/i, "")
  .replace(/\s+(?:por favor|sff|please|pls)\b.*$/i, "")
  .trim()
  .slice(0, 90);

function extractEndConstraintQuery(notes: string): string | null {
  const text = notes.trim();
  if (!text) return null;
  const routeWords = String.raw`(?:a\s+rota|o\s+roteiro|o\s+dia|a\s+visita|route|trip|itinerary|day)`;
  const connector = String.raw`(?:perto\s+d[aoe]s?|perto\s+de|pr[oó]ximo\s+d[aoe]s?|pr[oó]ximo\s+de|junto\s+d[aoe]s?|junto\s+de|near|close\s+to|at|em|no|na|ao|à)`;
  const patterns = [
    new RegExp(String.raw`(?:acaba(?:r)?|acabe|term(?:ina|inar|ine)|termina|termine|finaliza(?:r)?|chega(?:r)?|chegue|finish|end|arrive)(?:\s+${routeWords})?\s+${connector}\s+([^.,;\n]+)`, "i"),
    new RegExp(String.raw`(?:fim|final|last\s+stop)\s+${connector}\s+([^.,;\n]+)`, "i"),
  ];
  for (const pattern of patterns) {
    const match = text.match(pattern);
    const cleaned = match?.[1] ? cleanEndpointQuery(match[1]) : "";
    if (/^\d{1,2}(?::\d{2})?\s*(?:h|am|pm)?$/i.test(cleaned)) continue;
    if (cleaned.length >= 3) return cleaned;
  }
  return null;
}

async function resolveEndConstraintFromNotes(
  notes: string,
  context: { city: string; country: string; lat: number; lng: number },
): Promise<EndConstraint | null> {
  const query = extractEndConstraintQuery(notes);
  const key = placesServerKey();
  if (!query) return null;

  const canonical = canonicalEndConstraint(query, context);
  // Prefer the same live Places validation in every country. The small
  // canonical table remains only as an offline/failure fallback.
  if (!key) return canonical;

  const cacheKey = cacheKeyFor(context.city, context.country, `route-end:${query}`);
  const cached = await getCachedPlace(cacheKey);
  if (cached) {
    const dist = haversineMeters({ lat: context.lat, lng: context.lng }, { lat: cached.lat, lng: cached.lng });
    if (dist <= 120000) {
      return { query, name: cached.name, lat: cached.lat, lng: cached.lng, types: cached.types ?? [] };
    }
  }

  try {
    const qn = normalizeName(query);
    const wantsAirport = /aeroporto|airport/.test(qn);
    const wantsTrain = /oriente|campanha|sao bento|são bento|estacao|estação|comboio|train|rail|station/.test(qn);
    const r = await cachedPlacesFetch(`${PLACES_BASE}/places:searchText`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": key,
        "X-Goog-FieldMask": "places.id,places.location,places.displayName,places.types",
      },
      body: JSON.stringify({
        textQuery: `${query}, ${context.city}, ${context.country}`,
        maxResultCount: 5,
        locationBias: {
          circle: {
            center: { latitude: context.lat, longitude: context.lng },
            radius: 80000,
          },
        },
      }),
    }, { kind: "attractions", ttlMs: PLACES_TTL.attractions, origin: "route_stop_validation", sku: "places_text_search" });
    if (!r.ok) return canonical;
    const j = (await r.json()) as {
      places?: Array<{
        id?: string;
        displayName?: { text?: string };
        location?: { latitude?: number; longitude?: number };
        types?: string[];
      }>;
    };
    const best = (j.places ?? [])
      .filter((p) => p.location?.latitude != null && p.location?.longitude != null)
      .map((p) => {
        const lat = p.location!.latitude!;
        const lng = p.location!.longitude!;
        const dist = haversineMeters({ lat: context.lat, lng: context.lng }, { lat, lng });
        const types = p.types ?? [];
        let score = dist;
        if (placeNamesMatch(query, p.displayName?.text ?? "")) score -= 50000;
        if (wantsAirport && types.some((t) => t.toLowerCase() === "airport")) score -= 50000;
        if (wantsTrain && types.some((t) => /train_station|transit_station|subway_station|light_rail_station/.test(t.toLowerCase()))) score -= 30000;
        if (normalizeName(p.displayName?.text ?? "").includes(qn.split(/\s+/).at(-1) ?? qn)) score -= 10000;
        return { p, lat, lng, dist, score };
      })
      .filter((p) => p.dist <= 120000)
      .sort((a, b) => a.score - b.score)[0];
    if (!best) return canonical;
    const name = best.p.displayName?.text || query;
    const types = best.p.types ?? [];
    if (best.p.id) {
      void setCachedPlace(cacheKey, context.city, context.country, {
        place_id: best.p.id,
        name,
        lat: best.lat,
        lng: best.lng,
        rating: null,
        types,
        photo_name: null,
      });
    }
    return { query, name, lat: best.lat, lng: best.lng, types };
  } catch (e) {
    console.warn("resolveEndConstraintFromNotes failed", e);
    return canonical;
  }
}

async function resolveStartConstraint(
  startPlace: string,
  context: { city: string; country: string; lat: number; lng: number },
): Promise<EndConstraint | null> {
  const query = cleanEndpointQuery(startPlace);
  if (query.length < 3) return null;
  // Reuse the same canonical/Google resolver as route endings. The synthetic
  // sentence is internal only; the returned coordinates become the route
  // origin and the station/hotel/airport is not added as a sightseeing stop.
  return resolveEndConstraintFromNotes(`termina em ${query}`, context);
}

/**
 * URL estável da foto — resolvido no momento em que o cartão a mostra.
 *
 * Nenhuma chamada paga acontece aqui: devolve o endereço do proxy da própria
 * aplicação (`/api/public/place-photo`). Assim a rota guardada nunca fica com
 * um URL Google expirado e só as fotos efetivamente vistas são compradas.
 */
async function fetchPlacePhotoUrl(photoName?: string): Promise<string | undefined> {
  if (!photoName) return undefined;
  return `/api/public/place-photo?ref=${encodeURIComponent(photoName)}&w=800`;
}


// ---------------- Places cache (reduces Text Search calls) ----------------
// Text Search is the most expensive Places call (~$32/1k). We cache the
// resolved placeId + coords + photo_name forever per (city, normalized name),
// since these values never change. Photos still re-fetch from Google because
// photoUri URLs expire after ~1h, but Photo API ($7/1k) is much cheaper.
function cacheKeyFor(city: string, country: string, name: string) {
  return `${normalizeName(city)}|${normalizeName(country)}|${normalizeName(name)}`;
}

type CachedPlace = {
  place_id: string;
  name: string;
  lat: number;
  lng: number;
  rating: number | null;
  types: string[] | null;
  photo_name: string | null;
};

async function getCachedPlace(queryKey: string): Promise<CachedPlace | null> {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("places_cache")
      .select("place_id,name,lat,lng,rating,types,photo_name,expires_at")
      .eq("query_key", queryKey)
      .maybeSingle();
    if (!data) return null;
    // Skip expired entries — treat as cache miss so we refresh from Google.
    const exp = (data as any).expires_at as string | null;
    if (exp && new Date(exp).getTime() < Date.now()) return null;
    // Fire-and-forget update of usage stats — don't await.
    void supabaseAdmin
      .from("places_cache")
      .update({ last_used_at: new Date().toISOString(), hits: ((data as any).hits ?? 0) + 1 })
      .eq("query_key", queryKey);
    return data as CachedPlace;
  } catch (e) {
    console.warn("places_cache read failed", e);
    return null;
  }
}

async function setCachedPlace(
  queryKey: string,
  city: string,
  country: string,
  payload: CachedPlace,
) {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // 60 days — coordinates are stable, but businesses can close.
    const expiresAt = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString();
    await supabaseAdmin.from("places_cache").upsert(
      {
        query_key: queryKey,
        city,
        country,
        expires_at: expiresAt,
        ...payload,
      },
      { onConflict: "query_key" },
    );
  } catch (e) {
    console.warn("places_cache write failed", e);
  }
}


type StopValidationContext = {
  city: string;
  country: string;
  lat: number;
  lng: number;
  language?: string;
  allowFoodStops?: boolean;
  requireNameMatch?: boolean;
  radiusMeters: number;
  requireAdministrativeLocalityMatch: boolean;
  namedRoute?: string;
  /**
   * Índice de resolução da geração: locais já pagos nesta geração e memória
   * de nomes que não resolveram. Presente = menos pesquisas de texto.
   */
  resolution?: StopResolutionIndex;
  /** Área ativa, para consultar o catálogo geográfico antes de pagar. */
  catalogBounds?: CatalogBounds;
  /** Orçamento único da geração: a validação também gasta dele. */
  budget?: PlacesGenerationBudget;
  /** Locais obrigatórios/pedidos explicitamente passam o teto à frente. */
  highPriority?: boolean;
  /** Telemetria da fase (opcional). */
  onPhase?: (name: string, details?: Record<string, unknown>) => void;
};


type StopSearchContext = StopValidationContext & {
  language: string;
  interests: string[];
  /** Barreira geométrica da área ativa (quando disponível). */
  areaContains?: (point: { lat: number; lng: number }) => boolean;
  /** Recolha diversificada — só preenchido no modo genérico de visita. */
  diversified?: {
    area: ActiveSearchArea;
    days: number;
    stopsPerDay: number;
    generationId?: string;
    /** Orçamento único da geração (Parte G) — partilhado com as famílias. */
    budget?: PlacesGenerationBudget;
    onPhase?: (name: string, details?: Record<string, unknown>) => void;
    onResult?: (result: DiversifiedCollectionResult) => void;
  };
  /** Recolha por famílias explícitas — candidatos injetados pelo mesmo canal
   * canónico (absorbItem), sem chamadas adicionais ao fornecedor. */
  families?: {
    families: TourismFamilyId[];
    candidates: Array<{
      placeId?: string | null;
      name: string;
      lat: number;
      lng: number;
      rating?: number | null;
      types?: string[] | null;
      photoName?: string | null;
      addressComponents?: unknown[] | null;
    }>;
    /** Relatório por família, para o trace de satisfação. */
    reports?: Array<{ family: TourismFamilyId; accepted: number; technicalFailure: boolean }>;
    onPhase?: (name: string, details?: Record<string, unknown>) => void;
  };
};

/**
 * Aceita (ou não) um local JÁ CONHECIDO — cache, índice da geração ou
 * catálogo geográfico — aplicando exatamente os mesmos filtros do caminho de
 * rede: geometria, localidade administrativa, nome, comida e nome genérico.
 *
 * `miss` significa "não serve, tenta a rede"; `rejected` significa "este
 * local está excluído por regra do pedido" e não deve gastar chamada nenhuma.
 */
async function acceptKnownPlace(
  stop: AIStop,
  context: StopValidationContext,
  known: CachedPlace,
): Promise<{ kind: "accepted"; stop: AIStop } | { kind: "rejected" } | { kind: "miss" }> {
  const dist = haversineMeters(
    { lat: context.lat, lng: context.lng },
    { lat: known.lat, lng: known.lng },
  );
  const insideLocality = !context.requireAdministrativeLocalityMatch ||
    await isCoordinateInsideRequestedLocality({ lat: known.lat, lng: known.lng }, context);
  if (
    !(
      dist <= context.radiusMeters &&
      insideLocality &&
      !isClearlyWrongGlobalLandmark(known.name, context) &&
      !isOverlyGenericPlaceName(known.name) &&
      (!context.requireNameMatch || placeNamesMatch(stop.name, known.name))
    )
  ) {
    return { kind: "miss" };
  }
  if (!context.allowFoodStops && (isFoodPlaceType(known.types || []) || isFoodLikeCategory(`${known.name} ${stop.category}`))) {
    console.warn(`Rejected food place as sightseeing stop for ${context.city}: ${known.name}`);
    return { kind: "rejected" };
  }
  const photoUrl = await fetchPlacePhotoUrl(known.photo_name || undefined);
  const originalNorm = normalizeName(stop.name);
  const displayNorm = normalizeName(known.name);
  const samePlaceName = displayNorm.includes(originalNorm) || originalNorm.includes(displayNorm);
  return {
    kind: "accepted",
    stop: {
      ...stop,
      name: known.name,
      category: localizeCategory(samePlaceName ? stop.category : "", known.types || [], context.language),
      blurb: samePlaceName ? sanitizeBlurb(stop.blurb, context.language) : fallbackBlurb(known.name, context.city, context.language),
      rating: typeof known.rating === "number" ? Math.max(4, Math.min(5, known.rating)) : stop.rating,
      placeId: known.place_id,
      photoUrl: photoUrl || stop.photoUrl,
      lat: known.lat,
      lng: known.lng,
    },
  };
}

async function resolveStopInsideCity(
  stop: AIStop,
  context: StopValidationContext,
): Promise<AIStop | null> {
  if (isOverlyGenericPlaceName(stop.name)) {
    console.warn(`Rejected generic stop name for ${context.city}: ${stop.name}`);
    return null;
  }
  const key = placesServerKey();
  // If Places is not configured, keep the AI output rather than breaking the app,
  // but production should validate through Google Places before anything is shown.
  if (!key) return stop;

  const radius = context.radiusMeters;
  const query = context.requireAdministrativeLocalityMatch
    ? `${stop.name}, ${context.city}, ${context.country}`
    : `${stop.name}, ${context.country}`;
  if (isClearlyWrongGlobalLandmark(stop.name, context)) {
    console.warn(`Rejected globally mismatched AI stop for ${context.city}: ${stop.name}`);
    return null;
  }

  // 0) Memória negativa da geração: este nome já não resolveu antes. Repetir
  // a pesquisa custaria dinheiro para chegar ao mesmo resultado.
  if (context.resolution?.isUnresolved(stop.name)) return null;

  // 0b) Índice da geração: locais que ESTA geração já pagou (recolha
  // diversificada, famílias, validações anteriores). Zero chamadas.
  const indexed = context.resolution?.match(stop.name);
  if (indexed?.placeId) {
    const reuse = await acceptKnownPlace(stop, context, {
      place_id: indexed.placeId,
      name: indexed.name,
      lat: indexed.lat,
      lng: indexed.lng,
      rating: indexed.rating ?? null,
      types: indexed.types ?? null,
      photo_name: indexed.photoName ?? null,
    });
    if (reuse.kind === "accepted") {
      context.onPhase?.("stop_resolved_from_generation", { name: indexed.name });
      return reuse.stop;
    }
    if (reuse.kind === "rejected") return null;
  }

  // 1) Try cache first — saves a Places Text Search call (~$0.032).
  const queryKey = cacheKeyFor(context.city, context.country, stop.name);
  const cached = await getCachedPlace(queryKey);
  if (cached) {
    const fromCache = await acceptKnownPlace(stop, context, cached);
    if (fromCache.kind === "accepted") {
      context.resolution?.register({
        placeId: cached.place_id,
        name: cached.name,
        lat: cached.lat,
        lng: cached.lng,
        rating: cached.rating,
        types: cached.types,
        photoName: cached.photo_name,
      });
      return fromCache.stop;
    }
    if (fromCache.kind === "rejected") return null;
  }

  // 1b) Catálogo geográfico: o local pode ter sido descoberto por outra
  // geração nesta mesma zona. Continua a ser mais barato que pesquisar.
  if (context.catalogBounds) {
    const catalogHits = await readCatalogPlaceByName({
      bounds: context.catalogBounds,
      name: stop.name,
      limit: 3,
    });
    for (const hit of catalogHits) {
      const fromCatalog = await acceptKnownPlace(stop, context, {
        place_id: hit.placeId,
        name: hit.name,
        lat: hit.lat,
        lng: hit.lng,
        rating: hit.rating,
        types: hit.types,
        photo_name: hit.photoName,
      });
      if (fromCatalog.kind === "accepted") {
        context.resolution?.register({
          placeId: hit.placeId,
          name: hit.name,
          lat: hit.lat,
          lng: hit.lng,
          rating: hit.rating,
          types: hit.types,
          photoName: hit.photoName,
        });
        context.onPhase?.("stop_resolved_from_catalog", { name: hit.name });
        return fromCatalog.stop;
      }
      if (fromCatalog.kind === "rejected") return null;
    }
  }

  // 2) Teto da geração: a validação gasta do mesmo orçamento das recolhas.
  // Sem espaço, a paragem não é resolvida por rede — o preenchimento usa os
  // candidatos já recolhidos, nunca um erro nem uma rota vazia.
  if (!context.highPriority && context.budget && !context.budget.trySpend("text_search")) {
    context.onPhase?.("stop_validation_budget_exhausted", { name: stop.name });
    context.resolution?.markUnresolved(stop.name);
    return null;
  }


  try {
    const r = await cachedPlacesFetch(`${PLACES_BASE}/places:searchText`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": key,
        "X-Goog-FieldMask": "places.id,places.location,places.photos,places.displayName,places.types,places.addressComponents",
      },
      body: JSON.stringify({
        textQuery: query,
        maxResultCount: 3,
        locationRestriction: { rectangle: locationRectangle(context.lat, context.lng, radius) },
        languageCode: context.language?.toLowerCase().startsWith("pt") ? "pt-PT" : context.language,
      }),
    }, { kind: "attractions", ttlMs: PLACES_TTL.attractions, origin: "route_stop_validation", sku: "places_text_search" });
    if (!r.ok) {
      console.error("AI stop validation failed", r.status, await r.text().catch(() => ""));
      return null;
    }
    const j = (await r.json()) as {
      places?: Array<{
        id?: string;
        displayName?: { text?: string };
        location?: { latitude?: number; longitude?: number };
        photos?: Array<{ name?: string }>;
        rating?: number;
        types?: string[];
        addressComponents?: GoogleAddressComponent[];
      }>;
    };
    const candidates = (j.places ?? [])
      .filter((p) => p.location?.latitude != null && p.location?.longitude != null)
      .map((p) => ({
        place: p,
        dist: haversineMeters(
          { lat: context.lat, lng: context.lng },
          { lat: p.location!.latitude!, lng: p.location!.longitude! },
        ),
      }))
      .filter((p) => p.dist <= radius)
      .sort((a, b) => a.dist - b.dist);
    let place: typeof candidates[number]["place"] | undefined;
    for (const candidate of candidates) {
      const candidateName = candidate.place.displayName?.text || "";
      if (context.requireNameMatch && !placeNamesMatch(stop.name, candidateName)) continue;
      const lat = candidate.place.location!.latitude!;
      const lng = candidate.place.location!.longitude!;
      const hasAdminMatch = addressComponentsInsideRequestedLocality(candidate.place.addressComponents ?? [], context);
      if (
        !context.requireAdministrativeLocalityMatch ||
        hasAdminMatch ||
        await isCoordinateInsideRequestedLocality({ lat, lng }, context)
      ) {
        place = candidate.place;
        break;
      }
    }
    if (place?.location?.latitude == null || place.location.longitude == null) {
      console.warn(`Rejected AI stop outside ${context.city}: ${stop.name}`);
      // Memória negativa: não voltar a pagar por este nome nesta geração.
      context.resolution?.markUnresolved(stop.name);
      return null;
    }

    const displayName = place.displayName?.text || stop.name;
    if (isClearlyWrongGlobalLandmark(displayName, context)) {
      console.warn(`Rejected globally mismatched resolved stop for ${context.city}: ${displayName}`);
      return null;
    }
    if (!context.allowFoodStops && (isFoodPlaceType(place.types || []) || isFoodLikeCategory(`${displayName} ${stop.category}`))) {
      console.warn(`Rejected food place as sightseeing stop for ${context.city}: ${displayName}`);
      return null;
    }
    if (!isRealTouristPlace(displayName, place.types || [])) {
      console.warn(`Rejected non-tourist place (street/address) for ${context.city}: ${displayName}`);
      return null;
    }
    // A foto só é pedida depois de o local passar todas as validações: pagar
    // uma foto para uma paragem que vai ser rejeitada é dinheiro deitado fora.
    const photoUrl = await fetchPlacePhotoUrl(place.photos?.[0]?.name);
    const originalNorm = normalizeName(stop.name);
    const displayNorm = normalizeName(displayName);
    const samePlaceName = displayNorm.includes(originalNorm) || originalNorm.includes(displayNorm);

    // Persist to cache so future routes asking for this stop skip Text Search.
    if (place.id) {
      void setCachedPlace(queryKey, context.city, context.country, {
        place_id: place.id,
        name: displayName,
        lat: place.location.latitude,
        lng: place.location.longitude,
        rating: typeof place.rating === "number" ? place.rating : null,
        types: place.types ?? null,
        photo_name: place.photos?.[0]?.name ?? null,
      });
    }

    // Índice da geração: qualquer outro nome equivalente (obrigatório ou
    // sugerido) reutiliza este local sem nova pesquisa.
    context.resolution?.register({
      placeId: place.id ?? null,
      name: displayName,
      lat: place.location.latitude,
      lng: place.location.longitude,
      rating: typeof place.rating === "number" ? place.rating : null,
      types: place.types ?? null,
      photoName: place.photos?.[0]?.name ?? null,
    });
    if (resolutionKey(stop.name) !== resolutionKey(displayName)) {
      context.resolution?.register({
        placeId: place.id ?? null,
        name: stop.name,
        lat: place.location.latitude,
        lng: place.location.longitude,
        rating: typeof place.rating === "number" ? place.rating : null,
        types: place.types ?? null,
        photoName: place.photos?.[0]?.name ?? null,
      });
    }



    return {
      ...stop,
      name: displayName,
      category: localizeCategory(samePlaceName ? stop.category : "", place.types, context.language),
      blurb: samePlaceName ? sanitizeBlurb(stop.blurb, context.language) : fallbackBlurb(displayName, context.city, context.language),
      rating: typeof place.rating === "number" ? Math.max(4, Math.min(5, place.rating)) : stop.rating,
      placeId: place.id || stop.placeId,
      photoUrl: photoUrl || stop.photoUrl,
      lat: place.location.latitude,
      lng: place.location.longitude,
    };
  } catch (e) {
    console.error("resolveStopInsideCity failed", e);
    return null;
  }
}

async function validateStopsInsideCity(
  stops: AIStop[],
  context: StopValidationContext,
) {
  // Deduplicar ANTES de pesquisar: dois nomes iguais custavam duas pesquisas
  // porque a eliminação de duplicados só acontecia depois de resolvidos.
  const { unique, duplicates } = dedupeByResolutionKey(stops, (s) => s.name);
  if (duplicates) {
    context.resolution?.countDedupe(duplicates);
    context.onPhase?.("stop_validation_dedupe", { requested: stops.length, unique: unique.length });
  }
  // Sequencial: a resolução de uma paragem alimenta o índice que pode poupar
  // a pesquisa da seguinte. Em paralelo, esse ganho perdia-se.
  const resolved: (AIStop | null)[] = [];
  for (const stop of unique) resolved.push(await resolveStopInsideCity(stop, context));
  const seen = new Set<string>();
  return resolved.filter((s): s is AIStop => {
    if (!s) return false;
    const key = s.placeId || s.name.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}


async function searchLocalFallbackStops(
  context: StopSearchContext,
  needed: number,
  exclude: AIStop[],
): Promise<AIStop[]> {
  const key = placesServerKey();
  if (!key || needed <= 0) return [];
  const radius = context.radiusMeters;
  const seen = new Set(exclude.flatMap((s) => [s.placeId, normalizeName(s.name)].filter(Boolean) as string[]));
  const wantsViews = context.interests.includes("viewpoint");
  const wantsNature = context.interests.includes("nature");
  const searchArea = context.namedRoute
    ? `${context.namedRoute}, ${context.country}`
    : context.requireAdministrativeLocalityMatch
      ? `${context.city}, ${context.country}`
      : `${context.city} region, ${context.country}`;
  const queries = [
    context.namedRoute
      ? `official points of interest along ${searchArea}`
      : `top tourist attractions in ${searchArea}`,
    `historic landmarks in ${searchArea}`,
    wantsViews ? `viewpoints in ${searchArea}` : `museums in ${searchArea}`,
    wantsNature ? `parks and gardens in ${searchArea}` : `local attractions in ${searchArea}`,
    context.allowFoodStops ? `iconic gastronomic landmarks in ${searchArea}` : `famous places in ${searchArea}`,
  ];
  const out: AIStop[] = [];

  // Helpers for the multi-result query cache (saves Text Search calls on
  // popular cities — the same "top tourist attractions in Porto" query
  // returns the same landmarks for everyone, indefinitely).
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server").catch(() => ({ supabaseAdmin: null as any }));
  type CachedQueryItem = {
    id?: string;
    name: string;
    lat: number;
    lng: number;
    rating?: number | null;
    types?: string[] | null;
    photo_name?: string | null;
    addressComponents?: GoogleAddressComponent[] | null;
  };
  const readQueryCache = async (qKey: string): Promise<CachedQueryItem[] | null> => {
    if (!supabaseAdmin) return null;
    try {
      const { data } = await supabaseAdmin
        .from("places_query_cache")
        .select("results,hits,expires_at")
        .eq("query_key", qKey)
        .maybeSingle();
      if (!data) return null;
      const exp = (data as any).expires_at as string | null;
      if (exp && new Date(exp).getTime() < Date.now()) return null;
      void supabaseAdmin
        .from("places_query_cache")
        .update({ last_used_at: new Date().toISOString(), hits: ((data as any).hits ?? 0) + 1 })
        .eq("query_key", qKey);
      return (data as any).results as CachedQueryItem[];
    } catch { return null; }
  };
  const writeQueryCache = async (qKey: string, queryText: string, items: CachedQueryItem[]) => {
    if (!supabaseAdmin || items.length === 0) return;
    try {
      // 90 days — top attraction lists for a city change slowly.
      const expiresAt = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString();
      await supabaseAdmin.from("places_query_cache").upsert({
        query_key: qKey,
        city: context.city,
        country: context.country,
        query_text: queryText,
        results: items,
        expires_at: expiresAt,
      }, { onConflict: "query_key" });
    } catch {}
  };

  const queryKeyFor = (q: string) => `${normalizeName(context.city)}|${normalizeName(context.country)}|${normalizeName(q)}`;

  /**
   * Aceita (ou descarta) um candidato aplicando exatamente os mesmos filtros
   * usados pelo caminho clássico: tipo real de local, comida, geometria e
   * localidade administrativa. Partilhado com a recolha diversificada.
   */
  const absorbItem = async (p: CachedQueryItem): Promise<"full" | "scope_type" | "food" | "geometry" | "locality_admin" | "dupe" | null> => {
    if (out.length >= needed) return "full";
    if (isClearlyWrongGlobalLandmark(p.name, context)) return "scope_type";
    if (!isRealTouristPlace(p.name, p.types ?? [])) return "scope_type";
    if (!context.allowFoodStops && (isFoodPlaceType(p.types ?? []) || isFoodLikeCategory(p.name))) return "food";
    const inArea = context.areaContains
      ? context.areaContains({ lat: p.lat, lng: p.lng })
      : haversineMeters({ lat: context.lat, lng: context.lng }, { lat: p.lat, lng: p.lng }) <= radius;
    if (!inArea) return "geometry";
    // Recolha genérica: a área ativa (geometria resolvida pelo fornecedor) é
    // a autoridade territorial. A prova administrativa por nome falha com
    // exónimos ("Zurique" vs "Zürich") e não decide nada que a geometria já
    // não tenha decidido — candidatos dentro da área nunca são eliminados
    // por comparação textual de localidade.
    const insideLocality = context.diversified || context.families
      ? true
      : !context.requireAdministrativeLocalityMatch ||
        addressComponentsInsideRequestedLocality((p.addressComponents ?? []) as GoogleAddressComponent[], context) ||
        await isCoordinateInsideRequestedLocality({ lat: p.lat, lng: p.lng }, context);
    if (!insideLocality) return "locality_admin";
    const keyName = normalizeName(p.name);
    if ((p.id && seen.has(p.id)) || seen.has(keyName)) return "dupe";
    seen.add(keyName);
    if (p.id) seen.add(p.id);
    out.push({
      name: p.name,
      category: fallbackCategory(p.types ?? [], context.language),
      blurb: fallbackBlurb(p.name, context.city, context.language),
      priceLocal: 0,
      rating: typeof p.rating === "number" ? Math.max(4, Math.min(5, p.rating)) : 4.5,
      placeId: p.id,
      photoUrl: await fetchPlacePhotoUrl(p.photo_name ?? undefined),
      lat: p.lat,
      lng: p.lng,
      // Tipos reais do fornecedor: a satisfação de restrições turísticas lê
      // estes campos — sem eles o pool fica "cego" e o gate antigo vence.
      placeTypes: p.types ?? undefined,
      placePrimaryType: p.types?.[0] ?? undefined,
      // Horários reais: a barreira final do horário depende deles.
      openingPeriods:
        (p as { openingPeriods?: OpeningPeriod[] | null }).openingPeriods ?? null,
    });
    // Índice da geração: este candidato já foi pago. Se a IA sugerir o mesmo
    // local por nome, a validação resolve-o sem nova pesquisa de texto.
    context.resolution?.register({
      placeId: p.id ?? null,
      name: p.name,
      lat: p.lat,
      lng: p.lng,
      rating: typeof p.rating === "number" ? p.rating : null,
      types: p.types ?? null,
      photoName: p.photo_name ?? null,
    });
    return null;

  };

  /* ---- Injeção canónica de candidatos de famílias explícitas ----
   * Corre ANTES da recolha diversificada e usa o MESMO absorbItem do pool
   * principal (dedupe por placeId, geometria da área ativa, filtros de
   * tipo/comida): nunca append direto, nunca um fallback paralelo, ZERO
   * chamadas ao fornecedor. A área ativa é a autoridade territorial (os
   * candidatos já nasceram presos ao locationRestriction da área); a
   * validação final vinculativa continua a aplicar-se a jusante. Sem famílias
   * no contexto, nada muda.
   */
  const familyInjection: Record<string, number> = {};
  if (context.families?.candidates?.length) {
    const familyContext = context.families;
    const injectionStats = { injected: 0, dupe: 0, geometry: 0, rejected: 0 };
    for (const candidate of familyContext.candidates) {
      if (out.length >= needed) break;
      // Prova de família no pool: o MESMO predicado da taxonomia que decide a
      // satisfação — sem uma segunda definição de relevância.
      const matchingFamily = familyContext.families.find((family) =>
        classifyPlaceForFamily(family, {
          name: candidate.name,
          types: candidate.types,
          primaryType: candidate.types?.[0] ?? null,
        }).accepted,
      );
      if (!matchingFamily) {
        injectionStats.rejected += 1;
        continue;
      }
      const reason = await absorbItem({
        id: candidate.placeId ?? undefined,
        name: candidate.name,
        lat: candidate.lat,
        lng: candidate.lng,
        rating: candidate.rating ?? null,
        types: candidate.types ?? null,
        photo_name: candidate.photoName ?? null,
        addressComponents: (candidate.addressComponents ?? null) as GoogleAddressComponent[] | null,
      });
      if (reason === null) {
        injectionStats.injected += 1;
        familyInjection[matchingFamily] = (familyInjection[matchingFamily] ?? 0) + 1;
      } else if (reason === "dupe") injectionStats.dupe += 1;
      else if (reason === "geometry") injectionStats.geometry += 1;
      else injectionStats.rejected += 1;
    }
    familyContext.onPhase?.("family_pool_injection", {
      ...injectionStats,
      byFamily: familyInjection,
      total: familyContext.candidates.length,
    });
  }

  /* ---- Recolha diversificada (apenas modo genérico de visita) ----
   * Geometria vinculativa da área ativa, temas por tipo oficial do Places +
   * termo no idioma do pedido, dedupe por placeId (fallback nome+coords),
   * cache por consulta/página/idioma/geometria e teto de consultas.
   */
  if (context.diversified) {
    const diversified = context.diversified;
    const collected = await collectDiversifiedCandidates({
      area: diversified.area,
      apiKey: key,
      language: context.language,
      days: diversified.days,
      stopsPerDay: diversified.stopsPerDay,
      needed,
      generationId: diversified.generationId,
      budget: diversified.budget,
      onPhase: diversified.onPhase,
    });
    // Recontagem por fase intencional: quantos candidatos chegaram do Places
    // (raw), quantos ficaram após dedupe/tipo/comida/geometria e quantos uma
    // barreira administrativa eliminou. Sem estes números um filtro interno
    // a eliminar candidatos elegíveis parece "escassez da área".
    const stats = { geometry: 0, localityAdmin: 0, scopeType: 0, food: 0, dupe: 0 };
    const acceptedCandidates: typeof collected.candidates = [];
    for (const candidate of collected.candidates) {
      if (out.length >= needed) break;
      const reason = await absorbItem({
        id: candidate.placeId ?? undefined,
        name: candidate.name,
        lat: candidate.lat,
        lng: candidate.lng,
        rating: candidate.rating ?? null,
        types: candidate.types ?? null,
        photo_name: candidate.photoName ?? null,
        addressComponents: (candidate.addressComponents ?? null) as GoogleAddressComponent[] | null,
      });
      if (reason === "geometry") stats.geometry += 1;
      else if (reason === "locality_admin") stats.localityAdmin += 1;
      else if (reason === "scope_type") stats.scopeType += 1;
      else if (reason === "food") stats.food += 1;
      else if (reason === "dupe") stats.dupe += 1;
      else acceptedCandidates.push(candidate);
    }
    collected.filterRejections = {
      raw: collected.rawCount,
      unique: collected.candidates.length,
      accepted: out.length,
      rejectedGeometry: stats.geometry,
      rejectedLocalityAdmin: stats.localityAdmin,
      rejectedScopeType: stats.scopeType,
      rejectedFood: stats.food,
    };
    collected.acceptedCandidates = acceptedCandidates;
    diversified.onPhase?.("collect_accepted", {
      candidates: collected.candidates.length,
      accepted: out.length,
      needed,
      filterRejections: collected.filterRejections,
    });
    // Resultado completo (com recontagem e aceites) para o trace e a
    // classificação da causa no chamador.
    diversified.onResult?.(collected);
    return out;
  }


  for (const query of queries) {
    if (out.length >= needed) break;
    const qKey = queryKeyFor(query);
    let items: CachedQueryItem[] | null = await readQueryCache(qKey);

    if (!items) {
      try {
        const r = await cachedPlacesFetch(`${PLACES_BASE}/places:searchText`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": key,
            "X-Goog-FieldMask": "places.id,places.location,places.photos,places.displayName,places.types,places.addressComponents",
          },
          body: JSON.stringify({
            textQuery: query,
            maxResultCount: 8,
            locationRestriction: { rectangle: locationRectangle(context.lat, context.lng, radius) },
            languageCode: context.language.toLowerCase().startsWith("pt") ? "pt-PT" : context.language,
          }),
        }, { kind: "attractions", ttlMs: PLACES_TTL.attractions, origin: "route_stop_validation", sku: "places_text_search" });
        if (!r.ok) continue;
        const j = (await r.json()) as {
          places?: Array<{
            id?: string;
            displayName?: { text?: string };
            location?: { latitude?: number; longitude?: number };
            photos?: Array<{ name?: string }>;
            rating?: number;
            types?: string[];
            addressComponents?: GoogleAddressComponent[];
          }>;
        };
        items = [];
        for (const p of j.places ?? []) {
          const name = p.displayName?.text?.trim();
          const lat = p.location?.latitude;
          const lng = p.location?.longitude;
          if (!name || lat == null || lng == null) continue;
          items.push({
            id: p.id,
            name,
            lat,
            lng,
            rating: typeof p.rating === "number" ? p.rating : null,
            types: p.types ?? null,
            photo_name: p.photos?.[0]?.name ?? null,
            addressComponents: p.addressComponents ?? null,
          });
        }
        void writeQueryCache(qKey, query, items);
      } catch (e) {
        console.error("searchLocalFallbackStops failed", e);
        continue;
      }
    }

    for (const p of items) {
      if (out.length >= needed) break;
      await absorbItem(p);
    }
  }

  return out;
}


type RequestedStopCategory = NonNullable<z.infer<typeof Input>["requestedStopCategory"]>;

const REQUESTED_CATEGORY_QUERY: Record<RequestedStopCategory, string> = {
  cafe: "best pastry shops bakeries cafés",
  restaurant: "best local restaurants",
  hotel: "best hotels",
  viewpoint: "best viewpoints",
  museum: "best museums",
  monument: "best monuments and landmarks",
  church: "best churches and cathedrals",
  park: "best parks and gardens",
  nature: "best nature attractions",
  water: "best waterfront beaches or lakes",
  shopping: "best notable shops and shopping centres",
  nightlife: "best bars and nightlife venues",
  market: "best local markets",
  sweets: "best traditional sweets pastry shops bakeries",
  breakfast: "best breakfast cafes bakeries brunch spots",
};

const requestedCategoryMatchesTypes = (category: RequestedStopCategory, types: string[] = []) => {
  const joined = types.map((type) => type.toLowerCase()).join(" ");
  const patterns: Record<RequestedStopCategory, RegExp> = {
    cafe: /bakery|cafe|coffee_shop|confectionery|dessert|tea_house/,
    restaurant: /restaurant|food|meal_takeaway|meal_delivery/,
    hotel: /lodging|hotel|hostel|guest_house|bed_and_breakfast/,
    viewpoint: /viewpoint|tourist_attraction|observation_deck/,
    museum: /museum|art_gallery/,
    monument: /monument|historical_landmark|landmark|tourist_attraction/,
    church: /church|cathedral|place_of_worship|chapel/,
    park: /park|garden|botanical_garden/,
    nature: /natural_feature|national_park|hiking_area|wildlife_park/,
    water: /beach|marina|lake|river|waterfront/,
    shopping: /shopping_mall|store|shop/,
    nightlife: /bar|night_club|pub/,
    market: /market/,
    sweets: /bakery|cafe|coffee_shop|confectionery|dessert|tea_house|food|store|shop/,
    breakfast: /bakery|cafe|coffee_shop|breakfast_restaurant|brunch_restaurant|tea_house|cafeteria|sandwich_shop|donut_shop/,
  };
  return patterns[category].test(joined);
};

type CategorySearchSpec = {
  category?: RequestedStopCategory;
  query: string;
  /**
   * Rótulo já localizado para mostrar no cartão da paragem. A query interna
   * ("best traditional sweets pastry shops bakeries") nunca vai para o ecrã.
   */
  label?: string;
  /** Cozinha explícita: condição vinculativa, sem fallback genérico. */
  cuisine?: CuisineIntent | null;
  /** O utilizador pediu explicitamente um mercado. */
  allowMarket?: boolean;
};


async function searchRequestedCategoryStops(
  context: StopValidationContext,
  spec: CategorySearchSpec,
  exclude: AIStop[],
  maxResults = 1,
): Promise<AIStop[]> {
  const key = placesServerKey();
  const query = spec.query.trim().slice(0, 100);
  if (!key || !query || maxResults <= 0) return [];

  const radius = Math.min(context.radiusMeters, 6000);
  const seen = new Set(exclude.flatMap((stop) => [
    stop.placeId,
    normalizeName(stop.name),
  ].filter(Boolean) as string[]));

  try {
    const response = await cachedPlacesFetch(`${PLACES_BASE}/places:searchText`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": key,
        "X-Goog-FieldMask": "places.id,places.location,places.photos,places.displayName,places.types,places.primaryType,places.addressComponents",
      },
      body: JSON.stringify({
        textQuery: `best ${query} near ${context.city}, ${context.country}`,
        maxResultCount: Math.min(20, Math.max(8, maxResults * 2)),
        locationRestriction: {
          rectangle: locationRectangle(context.lat, context.lng, radius),
        },
        languageCode: context.language?.toLowerCase().startsWith("pt") ? "pt-PT" : context.language,
      }),
    }, { kind: "attractions", ttlMs: PLACES_TTL.attractions, origin: "route_stop_validation", sku: "places_text_search" });
    if (!response.ok) {
      console.warn("Requested category validation failed", response.status);
      return [];
    }

    const json = (await response.json()) as {
      places?: Array<{
        id?: string;
        displayName?: { text?: string };
        location?: { latitude?: number; longitude?: number };
        photos?: Array<{ name?: string }>;
        rating?: number;
        types?: string[];
        primaryType?: string;
        addressComponents?: GoogleAddressComponent[];
      }>;
    };

    const candidates = (json.places ?? [])
      .filter((place) =>
        place.location?.latitude != null &&
        place.location?.longitude != null &&
        (!spec.category || requestedCategoryMatchesTypes(spec.category, place.types ?? [])) &&
        // Cozinha explícita: primaryType/types do Places são prova obrigatória.
        (!spec.cuisine ||
          placeMatchesCuisine(
            { name: place.displayName?.text, primaryType: place.primaryType, types: place.types },
            spec.cuisine,
            { allowMarket: spec.allowMarket },
          )),
      )

      .map((place) => {
        const lat = place.location!.latitude!;
        const lng = place.location!.longitude!;
        return {
          place,
          lat,
          lng,
          distance: haversineMeters({ lat: context.lat, lng: context.lng }, { lat, lng }),
        };
      })
      .filter(({ place, distance }) => {
        const name = place.displayName?.text?.trim();
        if (!name || distance > radius) return false;
        if ((place.id && seen.has(place.id)) || seen.has(normalizeName(name))) return false;
        if (!isRealTouristPlace(name, place.types ?? [])) return false;
        return !context.requireAdministrativeLocalityMatch ||
          addressComponentsInsideRequestedLocality(place.addressComponents ?? [], context);
      })
      .sort((a, b) => {
        const aScore = a.distance - (a.place.rating ?? 0) * 250;
        const bScore = b.distance - (b.place.rating ?? 0) * 250;
        return aScore - bScore;
      })
      .slice(0, maxResults);

    return Promise.all(candidates.map(async (candidate) => {
      const name = candidate.place.displayName?.text?.trim() ?? query;
      return {
        name,
        category: spec.label?.trim()
          ? spec.label.trim()
          : localizeCategory(query, candidate.place.types ?? [], context.language),
        blurb: fallbackBlurb(name, context.city, context.language),
        priceLocal: 0,
        rating: typeof candidate.place.rating === "number"
          ? Math.max(4, Math.min(5, candidate.place.rating))
          : 4.5,
        placeId: candidate.place.id,
        photoUrl: await fetchPlacePhotoUrl(candidate.place.photos?.[0]?.name),
        lat: candidate.lat,
        lng: candidate.lng,
        placeTypes: candidate.place.types ?? [],
        placePrimaryType: candidate.place.primaryType,
        openingPeriods:
          (candidate.place as { regularOpeningHours?: { periods?: OpeningPeriod[] } })
            .regularOpeningHours?.periods ?? null,
        placeLocalityNames: (candidate.place.addressComponents ?? [])
          .filter((component) => componentHasType(component, ADMIN_LOCALITY_TYPES))
          .flatMap((component) => componentNames(component)),
      };
    }));
  } catch (error) {
    console.warn("searchRequestedCategoryStops failed", error);
    return [];
  }
}

async function searchStopsNearEndpoint(
  context: { city: string; country: string; lat: number; lng: number; language: string; allowFoodStops?: boolean },
  endpoint: EndConstraint,
  needed: number,
  exclude: AIStop[],
): Promise<AIStop[]> {
  const key = placesServerKey();
  const seen = new Set(exclude.flatMap((s) => [s.placeId, normalizeName(s.name)].filter(Boolean) as string[]));
  const out: AIStop[] = [];
  const endpointRadius = isAirportEndpoint(endpoint) ? 10000 : isTransitEndpoint(endpoint) ? 2200 : 3500;
  if (!key || needed <= 0 || out.length >= needed) return out.slice(0, needed);
  const queries = [
    `tourist attractions near ${endpoint.name}, ${context.city}, ${context.country}`,
    `landmarks near ${endpoint.name}, ${context.city}, ${context.country}`,
    `museums parks monuments near ${endpoint.name}, ${context.city}, ${context.country}`,
  ];

  for (const query of queries) {
    if (out.length >= needed) break;
    try {
      const r = await cachedPlacesFetch(`${PLACES_BASE}/places:searchText`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": key,
          "X-Goog-FieldMask": "places.id,places.location,places.photos,places.displayName,places.types,places.addressComponents",
        },
        body: JSON.stringify({
          textQuery: query,
          maxResultCount: 8,
          locationBias: {
            circle: {
              center: { latitude: endpoint.lat, longitude: endpoint.lng },
              radius: endpointRadius,
            },
          },
          languageCode: context.language.toLowerCase().startsWith("pt") ? "pt-PT" : context.language,
        }),
      }, { kind: "attractions", ttlMs: PLACES_TTL.attractions, origin: "route_stop_validation", sku: "places_text_search" });
      if (!r.ok) continue;
      const j = (await r.json()) as {
        places?: Array<{
          id?: string;
          displayName?: { text?: string };
          location?: { latitude?: number; longitude?: number };
          photos?: Array<{ name?: string }>;
          rating?: number;
          types?: string[];
          addressComponents?: GoogleAddressComponent[];
        }>;
      };
      const candidates = (j.places ?? [])
        .filter((p) => p.location?.latitude != null && p.location?.longitude != null)
        .map((p) => ({
          p,
          lat: p.location!.latitude!,
          lng: p.location!.longitude!,
          endDist: haversineMeters({ lat: endpoint.lat, lng: endpoint.lng }, { lat: p.location!.latitude!, lng: p.location!.longitude! }),
          cityDist: haversineMeters({ lat: context.lat, lng: context.lng }, { lat: p.location!.latitude!, lng: p.location!.longitude! }),
        }))
        .filter(({ p, cityDist, endDist }) => {
          const name = p.displayName?.text?.trim() ?? "";
          if (!name || cityDist > 25000 || endDist > endpointRadius) return false;
          if (!addressComponentsInsideRequestedLocality(p.addressComponents ?? [], context)) return false;
          if ((isTransitEndpoint(endpoint) || isAirportEndpoint(endpoint)) && endDist < 180) return false;
          const candidateName = normalizeName(name);
          const endpointName = normalizeName(endpoint.name);
          if (candidateName.includes(endpointName) || endpointName.includes(candidateName)) return false;
          if (!isRealTouristPlace(name, p.types ?? [])) return false;
          if (!context.allowFoodStops && (isFoodPlaceType(p.types ?? []) || isFoodLikeCategory(name))) return false;
          const keyName = candidateName;
          return !((p.id && seen.has(p.id)) || seen.has(keyName));
        })
        .sort((a, b) => a.endDist - b.endDist || (b.p.rating ?? 0) - (a.p.rating ?? 0));

      for (const c of candidates) {
        if (out.length >= needed) break;
        const name = c.p.displayName?.text?.trim();
        if (!name) continue;
        const keyName = normalizeName(name);
        seen.add(keyName);
        if (c.p.id) seen.add(c.p.id);
        out.push({
          name,
          category: fallbackCategory(c.p.types ?? [], context.language),
          blurb: fallbackBlurb(name, context.city, context.language),
          priceLocal: 0,
          rating: typeof c.p.rating === "number" ? Math.max(4, Math.min(5, c.p.rating)) : 4.5,
          placeId: c.p.id,
          photoUrl: await fetchPlacePhotoUrl(c.p.photos?.[0]?.name),
          lat: c.lat,
          lng: c.lng,
        });
      }
    } catch (e) {
      console.warn("searchStopsNearEndpoint failed", e);
    }
  }
  return out;
}

function orderNearest<T extends { lat?: number; lng?: number }>(
  items: T[],
  start: Coord,
  forcedEnd?: Coord,
  mode?: RouteOrderMode,
): T[] {
  const withCoords = items.filter((s) => typeof s.lat === "number" && typeof s.lng === "number") as Array<T & Coord>;
  const withoutCoords = items.filter((s) => !(typeof s.lat === "number" && typeof s.lng === "number"));
  if (withCoords.length < 2) return items;
  let finalStop: (T & Coord) | null = null;
  let pool = [...withCoords];
  if (forcedEnd && pool.length > 1) {
    let finalIdx = 0;
    let best = Infinity;
    pool.forEach((s, i) => {
      const d = haversineMeters(s, forcedEnd);
      if (d < best) { best = d; finalIdx = i; }
    });
    finalStop = pool.splice(finalIdx, 1)[0];
  }
  const ordered: Array<T & Coord> = [];
  let current = start;
  while (pool.length) {
    let bestIdx = 0;
    let bestD = Infinity;
    for (let i = 0; i < pool.length; i++) {
      const d = haversineMeters(pool[i], current);
      if (d < bestD) { bestD = d; bestIdx = i; }
    }
    const picked = pool.splice(bestIdx, 1)[0];
    ordered.push(picked);
    current = picked;
  }
  const fixedEnd = finalStop ? { lat: finalStop.lat, lng: finalStop.lng } : forcedEnd;
  const optimized = optimizePath2Opt(ordered, start, fixedEnd, mode);
  ordered.splice(0, ordered.length, ...optimized);
  if (finalStop) ordered.push(finalStop);
  return [...ordered, ...withoutCoords];
}

function pathScoreMeters<T extends Coord>(
  items: T[],
  start: Coord,
  fixedEnd?: Coord,
  mode?: RouteOrderMode,
) {
  const legs: number[] = [];
  let current = start;
  for (const item of items) {
    legs.push(haversineMeters(current, item));
    current = item;
  }
  if (fixedEnd && items.length) legs.push(haversineMeters(current, fixedEnd));
  return scoreRouteLegs(legs, mode);
}

function optimizePath2Opt<T extends Coord>(
  items: T[],
  start: Coord,
  fixedEnd?: Coord,
  mode?: RouteOrderMode,
): T[] {
  if (items.length < 4) return items;
  let best = [...items];
  let bestDistance = pathScoreMeters(best, start, fixedEnd, mode);
  let improved = true;
  let guard = 0;
  while (improved && guard++ < 20) {
    improved = false;
    for (let i = 0; i < best.length - 1; i++) {
      for (let k = i + 1; k < best.length; k++) {
        const candidate = [...best.slice(0, i), ...best.slice(i, k + 1).reverse(), ...best.slice(k + 1)];
        const distance = pathScoreMeters(candidate, start, fixedEnd, mode);
        if (distance + 25 < bestDistance) {
          best = candidate;
          bestDistance = distance;
          improved = true;
        }
      }
    }
  }
  return best;
}

function dedupeStops(stops: AIStop[]): AIStop[] {
  const seen = new Set<string>();
  const out: AIStop[] = [];
  for (const stop of stops) {
    const key = stop.placeId || normalizeName(stop.name);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(stop);
  }
  return out;
}

function estimateRouteMinutes(stops: AIStop[], visitMin: number, mode: z.infer<typeof Input>["mode"]): number {
  const speedKmh = mode === "walk" ? 4.5 : mode === "bike" ? 15 : mode === "scooter" ? 18 : mode === "transit" ? 20 : 30;
  let used = 0;
  let prev: AIStop | null = null;
  for (const stop of stops) {
    let transit = 0;
    if (prev && typeof prev.lat === "number" && typeof prev.lng === "number" && typeof stop.lat === "number" && typeof stop.lng === "number") {
      const km = haversineMeters({ lat: prev.lat, lng: prev.lng }, { lat: stop.lat, lng: stop.lng }) / 1000;
      transit = Math.round((km * 1.35 / speedKmh) * 60);
    }
    used += transit + visitMin;
    prev = stop;
  }
  return used;
}

/**
 * Re-assign `day` to stops by geographic clustering so each day focuses on
 * one zone of the city, instead of zig-zagging back and forth across days.
 *
 * Splits stops along the city's main geographic axis into balanced compact
 * day chunks, then orders each day by nearest-neighbour. If the user asked
 * to finish near a specific endpoint, the final day naturally progresses
 * toward the closest sightseeing stop to that endpoint.
 */
function clusterStopsByDay<T extends { lat?: number; lng?: number; day?: number }>(
  stops: T[],
  days: number,
  anchor: { lat: number; lng: number },
  endConstraint?: Coord | null,
  mode?: RouteOrderMode,
): T[] {
  if (stops.length <= days || days < 2) return stops;
  const withCoords: { s: T; idx: number; lat: number; lng: number }[] = [];
  const withoutCoords: { s: T; idx: number }[] = [];
  stops.forEach((s, idx) => {
    if (typeof s.lat === "number" && typeof s.lng === "number") withCoords.push({ s, idx, lat: s.lat, lng: s.lng });
    else withoutCoords.push({ s, idx });
  });
  if (withCoords.length < days) return stops;

  const meanLat = withCoords.reduce((sum, p) => sum + p.lat, 0) / withCoords.length;
  const meanLng = withCoords.reduce((sum, p) => sum + p.lng, 0) / withCoords.length;
  const scale = Math.max(0.2, Math.cos((meanLat * Math.PI) / 180));
  let axis = endConstraint
    ? { x: (endConstraint.lng - anchor.lng) * scale, y: endConstraint.lat - anchor.lat }
    : null;
  if (!axis || Math.hypot(axis.x, axis.y) < 0.0001) {
    // Principal-axis split: compact city zones instead of a single nearest-neighbour
    // chain that can run east→west→east and make day 2 zig-zag across town.
    const pts = withCoords.map((p) => ({ x: (p.lng - meanLng) * scale, y: p.lat - meanLat }));
    const sxx = pts.reduce((sum, p) => sum + p.x * p.x, 0) / pts.length;
    const syy = pts.reduce((sum, p) => sum + p.y * p.y, 0) / pts.length;
    const sxy = pts.reduce((sum, p) => sum + p.x * p.y, 0) / pts.length;
    const theta = 0.5 * Math.atan2(2 * sxy, sxx - syy);
    axis = { x: Math.cos(theta), y: Math.sin(theta) };
  }
  const project = (p: { lat: number; lng: number }) => ((p.lng - anchor.lng) * scale * axis!.x) + ((p.lat - anchor.lat) * axis!.y);
  const sorted = [...withCoords].sort((a, b) => project(a) - project(b));

  type Point = typeof withCoords[number];
  const centroidOf = (chunk: Point[]) => ({
    lat: chunk.reduce((sum, p) => sum + p.lat, 0) / Math.max(1, chunk.length),
    lng: chunk.reduce((sum, p) => sum + p.lng, 0) / Math.max(1, chunk.length),
  });
  const balancedGeoClusters = (): Point[][] => {
    const maxPerDay = Math.ceil(withCoords.length / days);
    let seeds = Array.from({ length: days }, (_, i) => {
      const idx = Math.min(sorted.length - 1, Math.max(0, Math.round(((i + 0.5) / days) * sorted.length - 0.5)));
      return { lat: sorted[idx].lat, lng: sorted[idx].lng };
    });
    let clusters: Point[][] = [];
    for (let iter = 0; iter < 8; iter++) {
      clusters = Array.from({ length: days }, () => [] as Point[]);
      const ranked = withCoords
        .map((p) => {
          const distances = seeds.map((seed, idx) => ({ idx, d: haversineMeters(p, seed) })).sort((a, b) => a.d - b.d);
          return { p, distances, confidence: (distances[1]?.d ?? distances[0].d) - distances[0].d };
        })
        .sort((a, b) => b.confidence - a.confidence);
      for (const item of ranked) {
        const chosen = item.distances.find((d) => clusters[d.idx].length < maxPerDay)?.idx ?? item.distances[0].idx;
        clusters[chosen].push(item.p);
      }
      seeds = clusters.map((chunk, idx) => chunk.length ? centroidOf(chunk) : seeds[idx]);
    }
    return clusters.filter((chunk) => chunk.length);
  };

  const orderChunks = (chunks: Point[][]) => {
    const remaining = chunks.map((chunk) => ({ chunk, centroid: centroidOf(chunk) }));
    const ordered: Point[][] = [];
    let current = anchor;
    let forcedLast: typeof remaining[number] | null = null;
    if (endConstraint && remaining.length > 1) {
      let idx = 0;
      let best = Infinity;
      remaining.forEach((c, i) => {
        const d = haversineMeters(c.centroid, endConstraint);
        if (d < best) { best = d; idx = i; }
      });
      forcedLast = remaining.splice(idx, 1)[0];
    }
    while (remaining.length) {
      let idx = 0;
      let best = Infinity;
      remaining.forEach((c, i) => {
        const d = haversineMeters(current, c.centroid);
        if (d < best) { best = d; idx = i; }
      });
      const [picked] = remaining.splice(idx, 1);
      ordered.push(picked.chunk);
      current = picked.centroid;
    }
    if (forcedLast) ordered.push(forcedLast.chunk);
    return ordered;
  };

  const chunks = orderChunks(balancedGeoClusters());

  const result: T[] = [];
  let current = anchor;
  for (let idx = 0; idx < chunks.length; idx++) {
    const day = idx + 1;
    const chunkStops = chunks[idx].map((m) => m.s);
    const ordered = orderNearest(
      chunkStops,
      current,
      endConstraint && day === days ? endConstraint : undefined,
      mode,
    );
    ordered.forEach((s) => result.push({ ...s, day }));
    const last = ordered[ordered.length - 1];
    if (last && typeof last.lat === "number" && typeof last.lng === "number") current = { lat: last.lat, lng: last.lng };
  }

  withoutCoords.forEach((w, i) => result.push({ ...w.s, day: (i % days) + 1 }));
  return result;
}

export const generateStops = createServerFn({ method: "POST" })

  .inputValidator((d: unknown) => {
    const data = Input.parse(d);
    // Autoridade da taxonomia: restrições cuja família foi explicitamente
    // pedida ("só museus e miradouros") passam a source "tourism_family" e
    // passam a ser satisfeitas pelo predicado único da taxonomia. Restrições
    // de local nomeado, comida ou atividade mantêm o caminho legado.
    const families = detectExplicitTourismFamilies(
      [data.requestText, data.notes, ...(data.textInterests ?? []), ...(data.explicitInterests ?? [])]
        .filter(Boolean)
        .join(" "),
    );
    return {
      ...data,
      requiredStopConstraints: tagTourismFamilyConstraints(
        data.requiredStopConstraints,
        families,
      ),
    };
  })
  .handler(async ({ data }) => {
    // Nenhum caminho de saída fica fora deste envelope: toda a geração acaba
    // em rota, cartão estruturado, falta de créditos ou falha técnica com IDs
    // de correlação.
    const generationRequestId =
      data.generationRequestId || data.requestId || `req_${Date.now().toString(36)}`;
    const traceRef: { id?: string } = {};
    try {
      // Todas as chamadas pagas feitas nesta geração ficam atribuídas a este
      // identificador — é assim que se mede o custo real por rota.
      // Uma rota de vários dias tem mais paragens: o teto acompanha a duração,
      // senão as rotas longas saem sem fotos nem restaurantes.
      resetLocalityCheckDiagnostics();
      setGenerationBudgetForDays(
        generationRequestId,
        Math.max(1, data.requestedDays ?? Math.ceil((data.durationHours ?? 3) / 10)),
      );
      const result = await runWithGoogleCallContext(
        { generationId: generationRequestId },
        () => generateStopsCore(data, traceRef),
      );

      const decision = classifyGenerationOutcome(result as GenerationResultLike);
      if (decision.kind !== "technical_failure") return result;
      // Diagnóstico honesto: sem paragens porque não conseguimos confirmar a
      // localidade é falha técnica de verificação, não escassez da zona.
      const localityChecksUnavailable = localityCheckUnavailableCount();
      const localityRejected = localityRejectionCount();
      if (localityChecksUnavailable > 0 || localityRejected > 0) {
        console.warn("generation_locality_diagnostics", {
          generationRequestId,
          localityChecksUnavailable,
          // Locais existem e foram pagos, mas a verificação administrativa
          // rejeitou-os: é rejeição de localidade, não escassez da zona.
          localityRejected,
        });
      }
      return {
        stops: [] as AIStop[],
        error: (result as { error?: string | null }).error ?? "generation_failed",
        technicalFailure: {
          origin: decision.origin ?? "server_exception",
          generationRequestId,
          ...(traceRef.id ? { traceId: traceRef.id } : {}),
        } as TechnicalFailure,
      };
    } catch (e) {
      console.error("generateStops escaped the handler", e);
      return {
        stops: [] as AIStop[],
        error: safeFailureCode(e),
        technicalFailure: {
          origin: "server_exception",
          generationRequestId,
          ...(traceRef.id ? { traceId: traceRef.id } : {}),
        } as TechnicalFailure,
      };
    }
  });

async function generateStopsCore(
  data: z.infer<typeof Input>,
  traceRef: { id?: string },
) {
    const apiKey = process.env.OPENAI_API_KEY;
    if (!apiKey) return { stops: [] as AIStop[], error: "AI not configured" };

    const geographicScope = data.geographicScope ?? parseRouteScopeFromNotes(data.notes);

    /* ---- Traçado por fases (funil de diagnóstico) ----
     * Nunca guarda o texto do pedido: apenas um hash não reversível e a
     * estrutura do pedido (âmbito, modo, interesses normalizados).
     */
    const trace = createRouteTrace([
      data.requestText || data.notes,
      data.city,
      data.country,
      data.language,
      data.mode,
      (data.interests ?? []).length,
      data.durationHours,
    ]);
    traceRef.id = trace.id;
    trace.phase("parse", {
      scope: geographicScope ?? "unspecified",
      mode: data.mode,
      interests: (data.interests ?? []).length,
      hasExpandedArea: !!data.expandedArea,
      hasIdentity: !!data.destinationIdentity,
      hasPinned: (data.pinnedStops ?? []).length,
      // Correlação com o cliente, mesmo quando a resposta se perde na rede.
      generationRequestId: data.generationRequestId ?? data.requestId ?? null,
      // Origem da continuação: pedido inicial, alargamento, redução de
      // duração ou nova tentativa — cada um é uma execução nova e isolada.
      continuation: data.generationContinuation ?? "initial",
    });

    /* ---- Identidade do destino → área ativa vinculativa ----
     * A geometria decide; o texto é apenas diagnóstico. Sem identidade
     * segura (providerId/center/bounds), nunca se pesquisa o globo:
     * devolve-se needs_locality_choice.
     */
    const generationArea = resolveGenerationArea({
      identity: data.destinationIdentity ?? null,
      expandedArea: data.expandedArea
        ? { label: data.expandedArea.label, radiusMeters: data.expandedArea.radiusMeters, center: data.expandedArea.center ?? null }
        : null,
      fallbackCenter: { lat: data.lat, lng: data.lng },
      fallbackCountryCode: "",
      fallbackRadiusMeters: routeScopePolicy(geographicScope, data.mode).radiusMeters,
      scope: geographicScope,
    });
    trace.phase("area", generationArea.status === "ok"
      ? {
          origin: generationArea.origin,
          mode: generationArea.area.mode,
          source: generationArea.area.source,
          radius: Math.round(generationAreaRadiusMeters(generationArea.area)),
        }
      : { requested: generationArea.requested });
    if (generationArea.status === "needs_locality_choice") {
      return {
        stops: [] as AIStop[],
        error: "needs_locality_choice",
        errorMeta: { requested: generationArea.requested },
      };
    }
    const activeArea = generationArea.area;
    const areaContainsStop = (stop: Pick<AIStop, "lat" | "lng">): boolean =>
      typeof stop.lat === "number" &&
      typeof stop.lng === "number" &&
      areaContains(activeArea, { lat: stop.lat, lng: stop.lng });

    const namedRoute = data.namedRoute ?? parseNamedRouteFromNotes(data.notes);
    const requiredPlaces = filterSpecificPlaces(
      Array.from(
        new Map(
          [...data.requiredPlaces, ...parseMandatoryPlacesFromNotes(data.notes)]
            .map((place) => place.trim())
            .filter(Boolean)
            .map((place) => [normalizeName(place), place]),
        ).values(),
      ),
    );
    const expandedArea = data.expandedArea ?? null;
    const basePolicy = routeScopePolicy(geographicScope, data.mode);
    // Com área alargada confirmada, o raio é o DA ÁREA — nunca o raio largo
    // por defeito do modo de transporte (que chega a 50 km de carro).
    const scopePolicy = expandedArea
      ? {
          ...basePolicy,
          radiusMeters: expandedArea.radiusMeters,
          requireAdministrativeLocalityMatch: false,
        }
      : basePolicy;

    const planningWindow = tripPlanningWindow(data.durationHours);
    const activeHours = planningWindow.activeHours;

    // Account for meal breaks that fall inside the user's window + average
    // transit per leg, so the AI does not return more stops than what fits.
    const eatMin = data.mealPrefs?.eatMinutes ?? 90;
    const lunchStart = hhmm(data.mealPrefs?.lunchStart ?? "12:00", 12 * 60);
    const lunchEnd = hhmm(data.mealPrefs?.lunchEnd ?? "14:00", 14 * 60);
    const dinnerStart = hhmm(data.mealPrefs?.dinnerStart ?? "19:00", 19 * 60);
    const dinnerEnd = hhmm(data.mealPrefs?.dinnerEnd ?? "21:00", 21 * 60);
    const startMin = hhmm(data.startTime ?? "09:00", 9 * 60);
    // "One day" means a daylight sightseeing window, not 24 continuous
    // hours. Only two or more days use multi-day ordering/grouping.
    // Os dias escritos no pedido são vinculativos e mandam sobre o cursor de
    // duração (que está limitado a 168h): "9 dias" significa mesmo 9 dias.
    const totalDays = Math.max(
      planningWindow.totalDays,
      Math.min(14, Math.max(1, data.requestedDays ?? 1)),
    );
    const isMulti = totalDays > 1;
    const perDayMin = Math.round(planningWindow.perDayHours * 60);
    const endMin = startMin + perDayMin;
    const overlaps = (ws: number, we: number) => Math.max(0, Math.min(we, endMin) - Math.max(ws, startMin)) > 0;
    const mealsPerDay =
      (overlaps(lunchStart, lunchEnd) ? eatMin : 0) +
      (overlaps(dinnerStart, dinnerEnd) ? eatMin : 0);
    const visitMin = data.mode === "walk" ? 35 : data.mode === "car" || data.mode === "transit" ? 45 : 20;
    const avgTransit = data.mode === "walk" ? 8 : data.mode === "car" ? 15 : data.mode === "transit" ? 12 : 6;
    const usablePerDay = Math.max(visitMin, perDayMin - mealsPerDay);
    const stopsPerDay = Math.max(1, Math.floor(usablePerDay / (visitMin + avgTransit)));
    // Um dia com uma única visita não é um roteiro: cada dia tem de receber
    // pelo menos três paragens reais, e o tecto total cresce com os dias.
    const MIN_STOPS_PER_DAY = 3;
    const stopCap = Math.min(45, Math.max(25, totalDays * 5));
    const budgetedTarget = Math.max(
      1,
      Math.min(stopCap, Math.max(stopsPerDay, MIN_STOPS_PER_DAY) * totalDays),
    );
    // When the user asks for "all" places (or narrows to a full category
    // listing), ignore the time budget and return as many real places as
    // possible — expanding to multiple days if needed.
    const wantsExhaustive = !!data.exhaustive;
    // "Ver menos paragens por dia" (cartão de escassez): reduz o teto por dia
    // para metade, mínimo 2/dia — menos paragens, mais tempo em cada uma.
    const budgetedTargetReduced = data.reducedStopsPerDay
      ? Math.min(
          budgetedTarget,
          Math.max(2, Math.floor(Math.max(stopsPerDay, MIN_STOPS_PER_DAY) / 2)) * totalDays,
        )
      : budgetedTarget;
    const targetCount = Math.min(
      stopCap,
      Math.max(wantsExhaustive ? stopCap : budgetedTargetReduced, requiredPlaces.length),
    );
    // Uma duração explícita é um contrato: "2 dias" continua a significar
    // exatamente dois dias, mesmo que o pedido também diga "ver tudo".
    const effectiveDays = wantsExhaustive && !data.requestedDays
      ? Math.max(totalDays, Math.min(14, Math.ceil(targetCount / Math.max(3, stopsPerDay))))
      : totalDays;
    const minimumUsefulStops = wantsExhaustive ? targetCount : minimumUsefulStopCount(targetCount);
    /* Alvo interno de candidatos (nunca apresentado ao utilizador) vs mínimos
     * e alvos de paragens finais do roteiro:
     * - minimumViableStops: segurança por dia (3 dia normal, 2 meio dia, 1 curta);
     * - minimumTotalStops: soma dos mínimos diários;
     * - targetItineraryStops: ritmo equilibrado por dia (5–6 dia completo,
     *   3–4 meio dia, 2–3 curto); soma = alvo de qualidade. */
    const targetCandidatePool = targetCount;
    const minimumViableStopsPerDay = minimumViableStopsForDay(usablePerDay, visitMin + avgTransit);
    /* ---- Logística de chegada: o dia 1 perde minutos reais ----
     * Desembarque+bagagem+transferência (aeroporto) e/ou check-in de hotel
     * são subtraídos à janela útil do dia 1 antes de calcular alvos e
     * mínimos por dia. Sem logística confirmada, nada muda. */
    const arrivalDeductionDay1 = !data.arrivalLogistics
      ? 0
      : (data.arrivalLogistics.kind === "airport"
          ? LANDING_BAGGAGE_MIN + AIRPORT_TRANSFER_DEFAULT_MIN
          : 0) +
        (data.arrivalLogistics.kind === "hotel" || data.arrivalLogistics.includeCheckin
          ? HOTEL_CHECKIN_MIN
          : 0);
    const usablePerDayByDay = Array.from({ length: totalDays }, (_, i) =>
      Math.max(visitMin, i === 0 ? usablePerDay - arrivalDeductionDay1 : usablePerDay),
    );
    const minimumViableStopsPerDayByDay = usablePerDayByDay.map((u) =>
      minimumViableStopsForDay(u, visitMin + avgTransit),
    );
    const targetItineraryStopsPerDayByDay = usablePerDayByDay.map((u) =>
      targetItineraryStopsForDay(u, visitMin + avgTransit),
    );
    // Alvo genérico por dia = média dos alvos por dia (o alvo com dedução de
    // chegada no dia 1 é menor); consumidores que esperam um número único
    // recebem a média arredondada.
    const targetItineraryStopsPerDay = Math.round(
      targetItineraryStopsPerDayByDay.reduce((a, b) => a + b, 0) / Math.max(1, targetItineraryStopsPerDayByDay.length),
    );
    const minimumViableStops = arrivalDeductionDay1 > 0
      ? minimumViableStopsPerDayByDay.reduce((a, b) => a + b, 0)
      : minimumTotalStopCount({
          totalDays,
          usableMinutesPerDay: usablePerDay,
          minutesPerStop: visitMin + avgTransit,
        });
    const targetItineraryStops = arrivalDeductionDay1 > 0
      ? targetItineraryStopsPerDayByDay.reduce((a, b) => a + b, 0)
      : targetTotalStopCount({
          totalDays,
          usableMinutesPerDay: usablePerDay,
          minutesPerStop: visitMin + avgTransit,
        });
    const minimumTotalStops = minimumViableStops;
    const targetTotalStops = targetItineraryStops;
    trace.phase("planning", {
      dayCount: totalDays,
      durationHours: data.durationHours,
      interpretedActiveHours: activeHours,
      availableMinutesPerDay: perDayMin,
      usableVisitMinutesPerDay: usablePerDay,
      arrivalDeductionDay1,
      usablePerDayByDay,
      targetCandidatePool,
      minimumViableStopsPerDay,
      minimumViableStopsPerDayByDay,
      minimumTotalStops,
      targetItineraryStopsPerDay,
      targetItineraryStopsPerDayByDay,
      targetTotalStops,
    });


    // Detect explicit food/restaurant intent ONLY from free-text notes. The
    // "food" interest means gastronomy can influence meal suggestions, but it
    // must not turn restaurants into sightseeing stops by itself.
    const notesLower = data.notes.toLowerCase();
    // Preferências negativas ("não quero restaurantes", "sem museus"...) —
    // exclusões DURAS aplicadas ao prompt/notas do pedido Free/Premium.
    const negativePrefs: NegativePreferences = parseNegativePreferences(data.notes);
    const isStopExcluded = (stop: { name: string; category?: string }) => {
      const guess = guessCategoryFromText(stop.category) ?? guessCategoryFromText(stop.name);
      return !!guess && isCategoryExcluded(negativePrefs, guess);
    };
    const visitFoodPatterns = [
      /visitar\s+(restaurantes|caf[eé]s|bares|tascas|pastelarias)/,
      /roteiro\s+(gastron[oó]mico|de\s+restaurantes|de\s+comida|de\s+tascas)/,
      /tour\s+(gastron[oó]mico|de\s+restaurantes|de\s+comida|de\s+tascas)/,
      /experi[eê]ncia\s+gastron[oó]mica/,
      /restaurant\s+(tour|crawl)/,
      /food\s+(tour|crawl)/,
    ];
    const notesWantsFood = visitFoodPatterns.some((re) => re.test(notesLower));

    // Recompute effective interests from the written request text + explicitly
    // selected chips. Text-derived interests never mutate visual chip state;
    // they only affect the current request.
    const { effectiveInterests, hasUsefulRequest } = computeEffectiveInterests({
      requestText: data.requestText,
      textInterests: data.textInterests,
      explicitInterests: data.explicitInterests,
      interests: data.interests,
      city: data.city,
      requiredPlaces: data.requiredPlaces,
      categoryFocus: data.categoryFocus,
      requestedStopCategory: data.requestedStopCategory,
      requestedStopQuery: data.requestedStopQuery,
      categoryFocusQuery: data.categoryFocusQuery,
    });

    // ---- Autoridade multi-família (taxonomia global de interesses) ----
    // Deteção a partir do texto do pedido + chips de interesses explícitos.
    // Quando a lista não está vazia, é a autoridade do caminho turístico: o
    // `categoryFocus` legado de uma única categoria NUNCA filtra a rota.
    const explicitTourismFamilies = detectExplicitTourismFamilies(
      [data.requestText, data.notes, ...(data.textInterests ?? []), ...(data.explicitInterests ?? [])]
        .filter(Boolean)
        .join(" "),
    );

    // Uma única categoria escolhida no formulário é tão vinculativa como
    // escrever "só restaurantes": o roteiro passa a ser exclusivo dessa
    // categoria (a menos que o pedido peça uma paragem avulsa ou a exclua).
    // Garantia A: modo de visita genérico NUNCA pode ganhar um foco único
    // derivado de interesses — o foco único é a fonte da rota de restaurantes.
    if (
      explicitTourismFamilies.length === 0 &&
      !data.genericVisitMode &&
      !data.categoryFocus &&
      !data.categoryFocusQuery &&
      !data.requestedStopCategory &&
      !data.requestedStopQuery &&
      data.requiredPlaces.length === 0 &&
      effectiveInterests.length === 1
    ) {
      const singleFocus = SINGLE_INTEREST_FOCUS[effectiveInterests[0]];
      const focusExcluded =
        singleFocus === "restaurant" && isCategoryExcluded(negativePrefs, "restaurant");
      if (singleFocus && !focusExcluded) {
        (data as { categoryFocus?: string }).categoryFocus = singleFocus;
      }
    }

    // Decisão vinculativa de intenção (server-side, mesma fonte: texto/chips
    // deste pedido). As flags do cliente são revalidadas aqui. `mealMode` é
    // apenas transportado para o log — NUNCA entra no cálculo da intenção
    // (regra de compatibilidade: foodIntent ≡ foodStopIntent).
    const foodIntentDecision = computeFoodIntent({
      requestText: data.requestText,
      notes: data.notes,
      explicitInterests: data.explicitInterests,
      textInterests: data.textInterests,
      categoryFocus: data.categoryFocus,
      categoryFocusQuery: data.categoryFocusQuery,
      requestedStopCategory: data.requestedStopCategory,
      requestedStopQuery: data.requestedStopQuery,
      requiredPlaces: data.requiredPlaces,
      requiredStopQueries: data.requiredStopConstraints.map((c) => c.query),
      cuisine: data.cuisine,
      mealMode: data.mealMode ?? null,
    });
    // Aliases obrigatórios: foodStopIntent é o sinal real; foodIntent é o
    // nome legado mantido por compatibilidade com o código existente.
    const foodStopIntent = data.foodStopIntent === true || data.foodIntent === true || foodIntentDecision.foodStopIntent;
    const foodIntent = foodStopIntent;
    // O servidor deriva este modo exclusivamente dos sinais vinculativos do
    // pedido. A flag opcional do cliente é apenas compatibilidade antiga: a sua
    // ausência nunca pode transformar uma visita genérica num fluxo especial.
    const genericVisitMode = foodIntentDecision.genericVisitMode;

    if (foodIntent) {
      console.log("[food-intent] active", JSON.stringify({
        city: data.city,
        reasons: foodIntentDecision.reasons,
        mealMode: data.mealMode ?? null,
        genericVisitMode,
      }));
    } else {
      console.log("[food-intent] inactive", JSON.stringify({
        city: data.city,
        mealMode: data.mealMode ?? null,
        genericVisitMode,
      }));
    }

    const flexibleFoodQuery = `${data.categoryFocusQuery ?? ""} ${data.requestedStopQuery ?? ""}`;
    const foodExcluded =
      isCategoryExcluded(negativePrefs, "restaurant") || isCategoryExcluded(negativePrefs, "cafe");
    const wantsFood =
      foodIntent &&
      !foodExcluded &&
      (notesWantsFood ||
        data.categoryFocus === "cafe" ||
        data.categoryFocus === "restaurant" ||
        data.categoryFocus === "sweets" ||
        data.categoryFocus === "breakfast" ||
        data.requestedStopCategory === "cafe" ||
        data.requestedStopCategory === "restaurant" ||
        data.requestedStopCategory === "sweets" ||
        effectiveInterests.includes("food") ||
        /cafe|coffee|bakery|pastry|restaurant|food|ice[ -]?cream|gelataria|sorveteria/i.test(flexibleFoodQuery));
    const sightseeingInterests = wantsFood ? effectiveInterests : effectiveInterests.filter((i) => i !== "food");
    const interestsLine = sightseeingInterests.length
      ? sightseeingInterests.join(", ")
      : hasUsefulRequest
        ? "(none specified — follow the request text and notes exactly)"
        : "iconic sightseeing, culture, viewpoints";

    const notesLine = data.notes.trim() ? data.notes.trim() : "(none)";
    const modeLabel = data.mode === "walk" ? "walking" : data.mode === "bike" ? "bicycle" : data.mode === "car" ? "car" : data.mode === "transit" ? "public transport" : "e-scooter";
    const localModeLabel = data.localMode === "walk" ? "walking" : data.localMode;
    const isMultiDay = effectiveDays > 1;
    const days = effectiveDays;
    const endpointQuery = extractEndConstraintQuery(data.notes);
    const requestedStartPlace = data.startPlace?.trim();

    // Category focus (e.g. only cafés / only museums / only hotels). When set,
    // every stop must belong to that category; the "no restaurants as stops"
    // rule is relaxed for food/hotel categories.
    const focus = data.categoryFocus;
    const FOCUS_META: Record<NonNullable<typeof focus>, { label: string; kinds: string; ptLabel: string }> = {
      cafe: { label: "cafés / coffee shops / pastry shops", kinds: "cafés, coffee shops, pastelarias, padarias, tea rooms", ptLabel: "cafés e pastelarias" },
      restaurant: { label: "restaurants", kinds: "restaurants, tascas, bistros, food halls", ptLabel: "restaurantes" },
      hotel: { label: "hotels and places to stay", kinds: "hotels, boutique hotels, hostels, pousadas, guesthouses", ptLabel: "hotéis e alojamentos" },
      viewpoint: { label: "viewpoints", kinds: "miradouros, panoramic viewpoints, overlooks, rooftop viewpoints", ptLabel: "miradouros" },
      museum: { label: "museums", kinds: "museums, art galleries, interpretation centres", ptLabel: "museus" },
      monument: { label: "monuments", kinds: "monuments, historical landmarks, statues, memorials", ptLabel: "monumentos" },
      church: { label: "churches and religious sites", kinds: "churches, cathedrals, chapels, sanctuaries, convents", ptLabel: "igrejas" },
      park: { label: "parks and gardens", kinds: "urban parks, botanical gardens, public gardens", ptLabel: "parques e jardins" },
      nature: { label: "nature sites", kinds: "trails, forests, natural parks, protected areas", ptLabel: "natureza" },
      water: { label: "rivers, lakes and beaches", kinds: "rivers, riverbanks, lakes, lagoons, beaches, seaside promenades, waterfronts", ptLabel: "rios, lagos e praias" },
      shopping: { label: "shopping spots", kinds: "shopping streets, malls, notable shops, artisan stores", ptLabel: "compras" },
      nightlife: { label: "nightlife venues", kinds: "bars, pubs, clubs, live music venues", ptLabel: "vida noturna" },
      market: { label: "markets", kinds: "public markets, farmers' markets, food halls, feiras", ptLabel: "mercados" },
      sweets: { label: "traditional sweets shops", kinds: "pastelarias, padarias tradicionais, confeitarias, tea houses with convent sweets, artisan dessert shops", ptLabel: "doçaria tradicional" },
      breakfast: { label: "breakfast and brunch spots", kinds: "cafés, pastelarias, padarias, coffee shops, brunch venues serving breakfast", ptLabel: "pequenos-almoços" },
    };
    const focusMeta = focus
      ? FOCUS_META[focus]
      : data.categoryFocusQuery
        ? { label: data.categoryFocusQuery, kinds: data.categoryFocusQuery, ptLabel: data.categoryFocusQuery }
        : null;
    const requestedCategoryMeta = data.requestedStopCategory
      ? FOCUS_META[data.requestedStopCategory]
      : data.requestedStopQuery
        ? { label: data.requestedStopQuery, kinds: data.requestedStopQuery, ptLabel: data.requestedStopQuery }
        : null;

    const durationLabel =
      data.durationHours < 1
        ? `${Math.round(data.durationHours * 60)}-minute`
        : isMultiDay
        ? `${days}-day`
        : `${data.durationHours}-hour`;
    const replyLanguage = languageInstruction(data.language);
    const stopCountInstruction = wantsExhaustive
      ? `Return AS MANY real ${focusMeta ? focusMeta.label : "notable tourist places"} as genuinely exist in ${data.city} (aim for up to ${targetCount}, but only include places you are certain are real — never invent filler). The traveler asked for a comprehensive listing, so cover EVERYTHING worth visiting even if it spans multiple days. If the city is small and there are fewer than ${targetCount} genuine places, return only the real ones.`
      : `with exactly ${targetCount} stops`;
    const routeArea = namedRoute
      ? `the real, official ${namedRoute} route in ${data.country}`
      : scopePolicy.requireAdministrativeLocalityMatch
        ? `${data.city}, ${data.country}`
        : `the requested wider area anchored at ${data.city}, ${data.country}`;
    const primaryLocationRule = scopePolicy.requireAdministrativeLocalityMatch
      ? `EVERY stop MUST be a real place physically located INSIDE the administrative boundary of ${data.city}, ${data.country}. Do NOT use neighbouring municipalities or generic nearby attractions.`
      : namedRoute
        ? `EVERY stop MUST be a real, verifiable point that belongs to or directly serves ${namedRoute}. It may cross municipalities, but never replace the named route with generic attractions.`
        : `EVERY stop MUST be a real, verifiable place within ${scopePolicy.radiusMeters / 1000} km of the anchor and relevant to the requested regional or multi-city route. Do not force all stops into one municipality.`;
    const constraintList = data.requiredStopConstraints?.length
      ? data.requiredStopConstraints.map((c) => {
          const type = c.cuisine ? `${c.cuisine} ${c.kind}` : c.activityType ? c.activityType.replace(/_/g, " ") : c.kind;
          const where = expandedArea ? expandedArea.label : data.city;
          return `one real ${type} in ${where} (${c.query})`;
        }).join("; ")
      : "";
    // Restrições turísticas explícitas ("só museus e miradouros") são
    // vinculativas para o modelo também na escrita do roteiro: cada família
    // pedida tem de ter paragens reais no roteiro final.
    const tourismFamilyRule = explicitTourismFamilies.length
      ? `MANDATORY TOURISM FAMILIES — the traveler explicitly asked to see ONLY these families: ${explicitTourismFamilies
          .map((family) => tourismFamilyLabel(family, data.language))
          .join(", ")}. Every stop in the itinerary must belong to one of these families (verified by place types). Include enough stops of EACH family; never mix in unrelated categories.`
      : "";
    const mandatoryRule = requiredPlaces.length || constraintList
      ? `MANDATORY STOPS — include and preserve all of these if Google Places can validate them: ${[...requiredPlaces, constraintList].filter(Boolean).join("; ")}. A requested shop, pastry shop, café, restaurant, activity venue or shopping centre is a valid mandatory stop. Do not substitute it. Arrange the other stops along a compact, logical path from the origin through these mandatory places; never add unrelated detours on the opposite side of the city.`
      : "";
    const requestedCategoryRule = requestedCategoryMeta
      ? `REQUESTED CATEGORY STOP — include at least one real, well-rated ${requestedCategoryMeta.label} close to the route. This is one requested stop inside a normal itinerary, not a route made entirely of that category.`
      : "";
    const firstStopRule = requestedStartPlace
      ? `The route origin is the named place "${requestedStartPlace}". The FIRST sightseeing stop must be the closest sensible stop to that origin; do not replace it with GPS or the city centre.`
      : `The FIRST stop must be a sensible central stop in ${data.city}; subsequent stops follow a logical path minimising backtracking.`;
    const walkingCompactnessRule = data.localMode === "walk" && data.mode !== "walk"
      ? `HYBRID TRANSPORT — use ${modeLabel} only for longer moves between distinct districts/zones. Inside each compact zone, park or leave the vehicle once and connect consecutive nearby stops by walking. Never describe the whole route as walking and never use the car for short hops inside a walkable cluster.`
      : data.mode === "walk"
      ? `WALKING COMPACTNESS — choose a dense group of attractions that genuinely fits on foot. Prefer consecutive walking legs around 20 minutes or less and return enough nearby places for at least ${minimumUsefulStops} useful stops after validation. Do not mix distant districts merely because each place is famous. A distant place is acceptable only when the traveler explicitly requested it; in that case build a logical corridor towards it.`
      : `MODE COMPACTNESS — keep the route realistic for ${localModeLabel ?? modeLabel} and avoid unnecessary cross-city detours.`;

    const sys = `You are a local tourism concierge. Build a ${durationLabel} itinerary by ${modeLabel}, ${stopCountInstruction}, ordered as a GEOGRAPHICALLY OPTIMAL route within ${routeArea}. The lat/lng below are the route anchor used for distance validation.

CRITICAL LOCATION RULES — do not violate any:
1. ${primaryLocationRule}
${tourismFamilyRule ? `0. ${tourismFamilyRule}\n` : ""}${mandatoryRule}
${requestedCategoryRule}
2. NEVER suggest a place in another city, region, country, or continent — even if its name sounds similar or famous globally.
3. Only use place names you are HIGHLY CONFIDENT are real and belong within ${routeArea}. If unsure, omit the place instead of guessing.
4. Use the LOCAL/OFFICIAL name of the place (in the local language or its widely recognised form) so it is findable on Google Maps — avoid invented translations or generic descriptions like "the old church".
5. ${firstStopRule}
6. All category and blurb text MUST be written in ${replyLanguage}. Never mix French/English category labels when another language is requested.
7. QUALITY BAR — ${wantsExhaustive
        ? `since the traveler asked for a comprehensive listing, include EVERY genuinely notable ${focusMeta ? focusMeta.label : "attraction"} in ${data.city} — start with the most iconic and continue with real lesser-known ones. Do NOT invent obscure places just to reach a number: if a place is not real, leave it out.`
        : `prioritize the city's most ICONIC, MUST-SEE attractions first (the kind that appear on the front of every guidebook, top of TripAdvisor, with thousands of reviews and 4.4+ ratings). Only after covering the obvious headline landmarks should you add lesser-known local gems. Do NOT fill the list with obscure or low-rated places just to reach the stop count — fewer iconic stops beat many forgettable ones.`}
8. ${focusMeta
        ? `CATEGORY FILTER — the traveler restricted the request to ${focusMeta.label} ONLY. EVERY stop MUST be a ${focusMeta.kinds}. Do NOT include any place from another category, even if famous (no bookshops, churches, museums, viewpoints or monuments).${
            focus === "restaurant" || focus === "cafe"
              ? " Spread the venues across eating moments (morning coffee/pastry, lunch, afternoon snack, dinner) instead of scheduling several full meals in a row; additional venues are nearby alternatives for the same moment."
              : ""
          }`
        : `STOPS ARE FOR SIGHTSEEING — prefer monuments, viewpoints/miradouros, museums, churches, plazas, parks, markets and iconic landmarks. ${wantsFood ? `The traveler EXPLICITLY asked to visit food/restaurant places${notesWantsFood ? ` (in their notes: "${data.notes.trim().slice(0, 120)}")` : ""}, so you MAY include at most 1–2 genuinely iconic gastronomic landmarks as stops if they are worth visiting in themselves. Normal lunch/dinner restaurant recommendations are handled separately as meal breaks.` : `ABSOLUTELY DO NOT include restaurants, cafés, bars, bakeries, pastry shops, tascas or places whose main purpose is eating/drinking as route stops. Meal pauses are handled separately — never use a stop slot for a restaurant just to "fit a meal".`}`}
9. ${walkingCompactnessRule}
10. ROUTE ENDPOINTS — read the traveler's named start and notes CAREFULLY and OBEY them${requestedStartPlace ? ` (detected requested starting point: "${requestedStartPlace}")` : ""}${endpointQuery ? ` (detected requested ending area: "${endpointQuery}")` : ""}:
   - If they say they want the route to END, FINISH or ARRIVE near a specific place (e.g. "acaba perto do aeroporto", "termina na estação de São Bento", "end near the airport", "finish at Campanhã station", "que acabe perto de X"), the LAST stop${isMultiDay ? " of the LAST day" : ""} MUST be the sightseeing place that is geographically CLOSEST to that endpoint, and the rest of the route must be ordered so the traveler naturally progresses TOWARDS it — no backtracking at the end. The endpoint itself (airport, train station, hotel) is NOT a sightseeing stop; do not include it in the list.
   - If they say they will START, BEGIN or ARE currently at a specific place, the FIRST stop must be the sightseeing place closest to that origin.
   - If they mention both, order the ENTIRE route as a one-way path from origin → destination with no backtracking.

${negativePrefs.excludedCategories.length ? `11. HARD EXCLUSION — the traveler explicitly said they do NOT want: ${negativePrefs.excludedCategories.join(", ")}. NEVER include a stop belonging to any of these categories, under any name or disguise. This overrides every other rule above, including any category focus or requested category.` : ""}

${
      isMultiDay
        ? `Spread the stops sensibly across ${days} days, grouping each day by neighborhood. Do NOT send the traveler back and forth across the city: day 1 should be one compact area near the anchor/centre, day 2 an adjacent compact area, and so on. For EVERY stop, set "day" to an integer from 1 to ${days} indicating which day of the trip it belongs to. Days must appear in order (all day 1 stops first, then day 2, etc.). Aim for a balanced number of stops per day (roughly ${Math.round(targetCount / days)} per day). If notes specify an ending location, the LAST stop of day ${days} is the one closest to it.`
        : "Fit the stops realistically into the available time."
    } Respect the traveler's interests and notes. Reply ONLY through the tool call.`;


    const user = `City: ${data.city}, ${data.country}.
City anchor for validation: lat ${data.lat.toFixed(5)}, lng ${data.lng.toFixed(5)}.
Duration: ${durationLabel} by ${modeLabel}${isMultiDay ? ` (${days} days)` : ""}.
Interests: ${interestsLine}.
Notes: ${notesLine}.
Reply in ${replyLanguage}. Use local currency ${data.currency} for priceLocal (0 if free). Write a vivid 1-sentence blurb with a historical or cultural fact. Remember: every "name" you return must be a REAL place within ${routeArea} — no guesses.`;

    const stopProps: Record<string, unknown> = {
      name: { type: "string" },
      category: { type: "string" },
      blurb: { type: "string", description: "1 vivid sentence with a historical/cultural fact, in the requested language" },
      priceLocal: { type: "number" },
      rating: { type: "number", minimum: 4, maximum: 5 },
    };
    const stopRequired = ["name", "category", "blurb", "priceLocal", "rating"];
    if (isMultiDay) {
      stopProps.day = { type: "integer", minimum: 1, maximum: days, description: `Day number (1 to ${days})` };
      stopRequired.push("day");
    }

    const {
      attachGenerationQuotaInternal,
      beginGenerationAttemptInternal,
      consumeQuotaInternal,
      finishGenerationAttemptInternal,
      releaseQuotaInternal,
    } = await import("./quota.server");
    const generationAttempt = await beginGenerationAttemptInternal(data.anonId, data.requestId, [
      data.city,
      data.country,
      data.durationHours,
      data.mode,
      effectiveInterests.join(","),
      data.notes,
      data.startTime,
    ]);
    if (!generationAttempt.ok) {
      const error = generationAttempt.reason === "in_progress" || generationAttempt.reason === "duplicate"
        ? "generation_in_progress"
        : "generation_temporarily_limited";
      return { stops: [] as AIStop[], error, costGuard: generationAttempt };
    }

    let quota;
    try {
      quota = await consumeQuotaInternal(data.anonId);
    } catch (error) {
      console.error("Quota check failed before generation", error);
      await finishGenerationAttemptInternal(generationAttempt, "technical_failure", {
        failureCode: "quota_check_failed",
        model: "gpt-4o-mini",
      });
      return { stops: [] as AIStop[], error: "quota_error", quotaReleased: true };
    }
    if (!quota.ok) {
      await finishGenerationAttemptInternal(generationAttempt, "quota_denied", {
        failureCode: quota.reason,
        model: "gpt-4o-mini",
      });
      return { stops: [] as AIStop[], error: "quota_exceeded", quota };
    }

    const quotaAttached = await attachGenerationQuotaInternal(generationAttempt, quota.reservation);
    if (!quotaAttached) {
      const quotaReleased = await releaseQuotaInternal(quota);
      if (quotaReleased) {
        await finishGenerationAttemptInternal(generationAttempt, "technical_failure", {
          failureCode: "quota_attachment_failed",
          model: "gpt-4o-mini",
        });
      }
      return { stops: [] as AIStop[], error: "generation_temporarily_limited", quotaReleased };
    }

    let openaiInputTokens = 0;
    let openaiOutputTokens = 0;
    let attemptFinished = false;
    const finishAttempt = async (
      status: "success" | "technical_failure",
      failureCode?: string,
    ) => {
      if (attemptFinished) return true;
      const finished = await finishGenerationAttemptInternal(generationAttempt, status, {
        failureCode,
        model: "gpt-4o-mini",
        inputTokens: openaiInputTokens,
        outputTokens: openaiOutputTokens,
      });
      if (finished) attemptFinished = true;
      return finished;
    };

    /**
     * Liberta APENAS a reserva desta geração, e só enquanto está pendente.
     * Idempotente: chamadas repetidas no mesmo pedido libertam uma só vez.
     * Nunca liberta a reserva de outro pedido nem uma geração já cobrada.
     */
    let quotaReleasedOnce: boolean | null = null;
    const releaseFailedGeneration = async (error: string) => {
      if (attemptFinished || quotaReleasedOnce !== null) {
        // Já concluída (sucesso ou libertação anterior): não tocar em créditos.
        return { stops: [] as AIStop[], error, quotaReleased: quotaReleasedOnce ?? false };
      }
      const quotaReleased = await releaseQuotaInternal(quota);
      quotaReleasedOnce = quotaReleased;
      if (quotaReleased) {
        // Escassez/decisões de pesquisa não são avarias: não podem somar para
        // o travão de "falhas técnicas seguidas" (que bloqueia 15–60 min).
        const isSearchOutcome =
          error === "visit_scarcity" ||
          error === "cuisine_scarce" ||
          error === "required_constraints_unmet" ||
          error === "needs_locality_choice";
        if (isSearchOutcome) {
          const ok = await finishGenerationAttemptInternal(generationAttempt, "quota_denied", {
            failureCode: error,
            model: "gpt-4o-mini",
            inputTokens: openaiInputTokens,
            outputTokens: openaiOutputTokens,
          });
          if (ok) attemptFinished = true;
        } else {
          await finishAttempt("technical_failure", error);
        }
      } else {
        // Keep the attempt active. The database lease cleanup will retry the
        // atomic quota rollback before allowing another expensive generation.
        console.error("CRITICAL: failed to release itinerary quota after generation error", {
          error,
          reservationId: quota.reservation.usageCounterId,
        });
      }
      return { stops: [] as AIStop[], error, quotaReleased };
    };

    try {
      const res = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        signal: AbortSignal.timeout(30000),
        headers: {
          Authorization: `Bearer ${apiKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: "gpt-4o-mini",
          messages: [
            { role: "system", content: sys },
            { role: "user", content: user },
          ],
          tools: [
            {
              type: "function",
              function: {
                name: "return_stops",
                description: `Return ${targetCount} tourist stops`,
                parameters: {
                  type: "object",
                  properties: {
                    stops: {
                      type: "array",
                      items: {
                        type: "object",
                        properties: stopProps,
                        required: stopRequired,
                        additionalProperties: false,
                      },
                    },
                  },
                  required: ["stops"],
                  additionalProperties: false,
                },
              },
            },
          ],
          tool_choice: { type: "function", function: { name: "return_stops" } },
        }),
      });

      if (!res.ok) {
        const t = await res.text();
        console.error("OpenAI error", res.status, t);
        const error =
          res.status === 429
            ? "rate_limited"
            : res.status === 401
            ? "auth_error"
            : res.status === 402
            ? "payment_required"
            : "ai_error";
        return releaseFailedGeneration(error);
      }
      // Fase 6 do funil: exceção técnica concreta na montagem (resposta IA
      // inválida) — trace assembly_error antes de falhar a geração.
      let parsed: { stops?: unknown[] } | null = null;
      try {
        const json = await res.json();
        openaiInputTokens = Math.max(0, Number(json.usage?.prompt_tokens) || 0);
        openaiOutputTokens = Math.max(0, Number(json.usage?.completion_tokens) || 0);
        const args = json.choices?.[0]?.message?.tool_calls?.[0]?.function?.arguments;
        parsed = args ? JSON.parse(args) : null;
      } catch (parseError) {
        trace.phase("assembly_error", {
          stage: "ai_response_parse",
          message: parseError instanceof Error ? parseError.message : String(parseError),
        });
        return releaseFailedGeneration("ai_error");
      }
      const stops = ((parsed?.stops ?? []) as AIStop[])
        .filter((s) => wantsFood || !isFoodLikeCategory(`${s.category} ${s.name}`))
        .filter((s) => !isStopExcluded(s))
        .slice(0, targetCount);
      trace.phase("ai_stops", { returned: stops.length, targetStops: targetCount });
      /* Índice de resolução ÚNICO desta geração: tudo o que for pago (recolha
       * diversificada, famílias, validação de obrigatórios) fica disponível
       * para as fases seguintes sem nova pesquisa de texto. */
      const stopResolution = new StopResolutionIndex();
      const stopValidationContext: StopValidationContext = {
        city: data.city,
        country: data.country,
        lat: data.lat,
        lng: data.lng,
        language: data.language,
        allowFoodStops: wantsFood,
        radiusMeters: scopePolicy.radiusMeters,
        requireAdministrativeLocalityMatch: scopePolicy.requireAdministrativeLocalityMatch,
        namedRoute,
        resolution: stopResolution,
        ...(activeArea ? { catalogBounds: activeArea.bounds as CatalogBounds } : {}),
        onPhase: (name, details) => trace.phase(name, details ?? {}),
      };


      /* ---- Âmbito protegido da recolha diversificada ----
       * Só o modo genérico de visita usa a política de temas. Qualquer sinal
       * especializado (locais obrigatórios, cozinha, categoria explícita,
       * interesses escolhidos, constraints, paragens fixas) mantém o caminho
       * atual — reutilizando apenas a geometria segura da área ativa.
       */
      const diversifiedAllowed = isDiversifiedCollectionAllowed({
        genericVisitMode,
        hasRequiredPlaces: requiredPlaces.length > 0,
        hasCuisine: !!data.cuisine,
        hasExplicitCategory: !!data.requestedStopCategory || !!data.categoryFocus || !!data.categoryFocusQuery,
        // `interests` pode conter defaults visuais. Só escolhas explícitas do
        // pedido bloqueiam a política genérica de temas.
        hasSelectedInterests: data.explicitInterests.length > 0 || data.textInterests.length > 0,
        hasConstraints: !!data.notes && !!extractEndConstraintQuery(data.notes),
        hasPinnedStops: (data.pinnedStops ?? []).length > 0,
        // Autoridade multi-família: presente, a recolha genérica de temas
        // cede o lugar à recolha por famílias explícitas.
        hasExplicitTourismFamilies: explicitTourismFamilies.length > 0,
      });
      trace.phase("collect_scope", { diversified: diversifiedAllowed });
      /* ---- Orçamento ÚNICO de chamadas pagas desta geração (Parte G/H) ----
       * Cobre a recolha genérica diversificada, a recolha por famílias
       * explícitas e o fallback determinístico: todos gastam da mesma
       * contabilidade, com limites fail-closed por ambiente e kill switch. */
      const placesBudget = createPlacesBudget({ scope: `generation:${trace.id}` });
      // A validação de paragens deixa de ser gratuita e ilimitada: gasta do
      // mesmo orçamento único da geração.
      stopValidationContext.budget = placesBudget;

      trace.phase("places_budget_limits", {
        source: placesBudget.snapshot().source,
        killSwitch: placesBudget.snapshot().killSwitch,
        total: placesBudget.snapshot().limits.total,
      });
      const collectionRef: { current: DiversifiedCollectionResult | null } = { current: null };
      const familyCollectionRef: { current: FamilyCollectionResult | null } = { current: null };
      const diversifiedCollection = diversifiedAllowed
        ? {
            area: activeArea,
            days: totalDays,
            stopsPerDay,
            generationId: undefined as string | undefined,
            budget: placesBudget,
            onPhase: trace.phase,
            onResult: (result: DiversifiedCollectionResult) => {
              collectionRef.current = result;
              // Fases 1-2 do funil: candidatos validados pelo Places e
              // candidatos após os filtros intencionais.
              trace.phase("places_candidates", {
                raw: result.rawCount,
                unique: result.candidates.length,
                fromCache: result.fromCache,
                themesAttempted: result.themesAttempted,
                themesCompleted: result.themesCompleted,
                errors: result.errors,
              });
              trace.phase("complementary_collection_eligible", {
                accepted: result.filterRejections?.accepted ?? 0,
                rejections: result.filterRejections ?? null,
              });
            },
          }
        : undefined;
      /* ---- Recolha por famílias explícitas (autoridade taxonómica) ----
       * Corre quando o pedido nomeia famílias turísticas ("só museus e
       * miradouros"). Progressiva, orçamentada e presa à área ativa. Os
       * candidatos são injetados no contexto de preenchimento do chamador
       * (searchLocalFallbackStops) via diversifiedCollection — o mesmo canal
       * geométrico da recolha genérica, sem paralelo não-orçamentado. */
      if (isPlacesKillSwitchActive()) {
        // Travão global de custo (H.2/H.3): sem chamadas pagas não há rota
        // possível — falha técnica estruturada, nunca escassez da área.
        trace.phase("places_kill_switch", { active: true });
        return releaseFailedGeneration("places_budget_exhausted");
      }
      const familyCollection =
        explicitTourismFamilies.length > 0 && !diversifiedAllowed
          ? await collectExplicitFamilyCandidates({
              families: explicitTourismFamilies,
              area: activeArea,
              apiKey: placesServerKey(),
              language: data.language,
              days: totalDays,
              generationId: undefined as string | undefined,
              budget: placesBudget,
              onPhase: trace.phase,
            })
          : null;
      familyCollectionRef.current = familyCollection;
      if (familyCollection) {
        trace.phase("family_collection_result", {
          families: explicitTourismFamilies,
          unique: familyCollection.uniqueCount,
          complete: familyCollection.complete,
          budgetExhausted: familyCollection.budgetExhausted,
          budgetUsed: familyCollection.budget.used.total,
          errors: familyCollection.errors,
        });
        if (!familyCollection.complete) {
          // Falha técnica da recolha por famílias: NUNCA é escassez da área.
          trace.phase("family_collection_incomplete", {
            reports: familyCollection.reports.map((r) => ({
              family: r.family,
              accepted: r.accepted,
              technicalFailure: r.technicalFailure,
            })),
          });
          return releaseFailedGeneration("family_collection_incomplete");
        }
      }
      const mandatoryCandidates: AIStop[] = requiredPlaces.map((name) => ({
        name,
        category: "Ponto obrigatório",
        blurb: fallbackBlurb(name, data.city, data.language),
        priceLocal: 0,
        rating: 4.5,
      }));
      /* Ordem deliberada (não paralela): os locais obrigatórios resolvem
       * primeiro, com prioridade sobre o teto, e alimentam o índice; depois
       * as sugestões reutilizam tudo o que já foi pago. Em paralelo, um nome
       * repetido nas duas listas custava duas pesquisas. */
      const mandatoryResolutionResults = await Promise.all(
        // Resolve requested places individually so failures can be reported by
        // their original user-provided names instead of disappearing silently.
        dedupeByResolutionKey(mandatoryCandidates, (c) => c.name).unique.map(async (candidate) => ({
          requestedName: candidate.name,
          stop: await resolveStopInsideCity(candidate, {
            ...stopValidationContext,
            allowFoodStops: true,
            requireNameMatch: true,
            highPriority: true,
          }),
        })),
      );
      const validatedSuggested = await validateStopsInsideCity(stops, stopValidationContext);
      trace.phase("stop_validation_reuse", stopResolution.snapshot());

      const validatedMandatory = dedupeStops(
        mandatoryResolutionResults
          .map((result) => result.stop)
          .filter((stop): stop is AIStop => !!stop),
      );
      const unresolvedRequiredPlaces = mandatoryResolutionResults
        .filter((result) => !result.stop)
        .map((result) => result.requestedName);
      const validated = dedupeStops([...validatedMandatory, ...validatedSuggested]);
      const endpointContext = {
        city: data.city,
        country: data.country,
        lat: data.lat,
        lng: data.lng,
      };
      const suppliedStartConstraint: EndConstraint | null =
        requestedStartPlace &&
        typeof data.startLat === "number" &&
        typeof data.startLng === "number"
          ? {
              query: requestedStartPlace,
              name: requestedStartPlace,
              lat: data.startLat,
              lng: data.startLng,
              types: [],
            }
          : null;
      const [resolvedStartConstraint, endConstraint] = await Promise.all([
        requestedStartPlace
          ? resolveStartConstraint(requestedStartPlace, endpointContext)
          : Promise.resolve(null),
        resolveEndConstraintFromNotes(data.notes, endpointContext),
      ]);
      // A browser geocoder is useful as a fallback, but the server-side Google
      // result is canonical and must win when it can validate the named origin.
      const startConstraint = resolvedStartConstraint ?? suppliedStartConstraint;

      const expandWalkingPool =
        !isMulti &&
        data.mode === "walk" &&
        (requiredPlaces.length > 0 || !!requestedStartPlace);
      const candidatePoolTarget = Math.min(25, targetCount + (expandWalkingPool ? 4 : 0));
      const familyInjectionContext = familyCollection
        ? {
            families: explicitTourismFamilies,
            candidates: familyCollection.candidates.map((candidate) => ({
              placeId: candidate.placeId,
              name: candidate.name,
              lat: candidate.lat,
              lng: candidate.lng,
              rating: candidate.rating,
              types: candidate.types,
              photoName: candidate.photoName,
              addressComponents: candidate.addressComponents,
            })),
            reports: familyCollection.reports.map((report) => ({
              family: report.family,
              accepted: report.accepted,
              technicalFailure: report.technicalFailure,
            })),
            onPhase: trace.phase,
          }
        : undefined;
      const fallbackSearchContext = startConstraint
        ? {
            ...stopValidationContext,
            lat: startConstraint.lat,
            lng: startConstraint.lng,
            language: data.language,
            interests: sightseeingInterests,
            areaContains: areaContainsStop,
            diversified: diversifiedCollection,
            families: familyInjectionContext,
          }
        : {
            ...stopValidationContext,
            language: data.language,
            interests: sightseeingInterests,
            areaContains: areaContainsStop,
            diversified: diversifiedCollection,
            families: familyInjectionContext,
          };
      const filled = validated.length < candidatePoolTarget && !focusMeta
        ? [
            ...validated,
            ...(await searchLocalFallbackStops(
              fallbackSearchContext,
              candidatePoolTarget - validated.length,
              validated,
            )),
          ]
        : validated;

      const categorySearchContext = {
        ...stopValidationContext,
        lat: startConstraint?.lat ?? data.lat,
        lng: startConstraint?.lng ?? data.lng,
        allowFoodStops: true,
      };
      // Cozinha explícita: vinculativa. Nunca é substituída por mercados,
      // food halls ou restaurantes genéricos.
      const cuisineIntent: CuisineIntent | null = data.cuisine
        ? detectCuisineIntent(data.cuisine)
        : null;
      const cuisineAllowsMarket = requestsMarket(data.cuisine ?? "");
      const cuisineFor = (category?: RequestedStopCategory) =>
        cuisineIntent && (!category || category === "restaurant" || category === "cafe")
          ? cuisineIntent
          : null;
      const focusCategoryStops = focusMeta
        ? await searchRequestedCategoryStops(
            categorySearchContext,
            {
              category: data.categoryFocus,
              query: cuisineFor(data.categoryFocus)
                ? cuisineSearchQuery(cuisineIntent!)
                : data.categoryFocus
                  ? REQUESTED_CATEGORY_QUERY[data.categoryFocus]
                  : data.categoryFocusQuery!,
              label: data.categoryFocus
                ? categoryFocusDisplayLabel(data.categoryFocus, data.language)
                : undefined,
              cuisine: cuisineFor(data.categoryFocus),
              allowMarket: cuisineAllowsMarket,
            },
            filled,
            candidatePoolTarget,
          )
        : [];
      const requestedCategoryStops = !focusMeta && (data.requestedStopCategory || data.requestedStopQuery)
        ? await searchRequestedCategoryStops(
            categorySearchContext,
            {
              category: data.requestedStopCategory,
              query: cuisineFor(data.requestedStopCategory)
                ? cuisineSearchQuery(cuisineIntent!)
                : data.requestedStopCategory
                  ? REQUESTED_CATEGORY_QUERY[data.requestedStopCategory]
                  : data.requestedStopQuery!,
              label: data.requestedStopCategory
                ? categoryFocusDisplayLabel(data.requestedStopCategory, data.language)
                : undefined,
              cuisine: cuisineFor(data.requestedStopCategory),
              allowMarket: cuisineAllowsMarket,
            },
            filled,
            1,

          )
        : [];
      const requestedCategoryStop = requestedCategoryStops[0] ?? null;

      let candidateStops = dedupeStops([
        ...validatedMandatory,
        ...focusCategoryStops,
        ...requestedCategoryStops,
        ...filled,
      ]);
      if (startConstraint) {
        // The named origin is already represented as route point 1. Do not
        // spend a sightseeing slot on the very same station, airport, hotel
        // or venue again. Match by identity first and use a tiny coordinate
        // tolerance only for providers that localise the display name.
        candidateStops = candidateStops.filter((stop) => {
          const distance =
            typeof stop.lat === "number" && typeof stop.lng === "number"
              ? haversineMeters({ lat: stop.lat, lng: stop.lng }, startConstraint)
              : undefined;
          const samePlace =
            isSameRoutePlace(startConstraint.query, stop.name, distance) ||
            isSameRoutePlace(startConstraint.name, stop.name, distance);
          return !samePlace;
        });
      }
      if (endConstraint) {
        const endpointName = normalizeName(endConstraint.name);
        candidateStops = candidateStops.filter((s) => {
          const stopName = normalizeName(s.name);
          if (stopName.includes(endpointName) || endpointName.includes(stopName)) return false;
          if (typeof s.lat === "number" && typeof s.lng === "number") {
            return haversineMeters({ lat: s.lat, lng: s.lng }, endConstraint) > 120;
          }
          return true;
        });
        const endpointStops = await searchStopsNearEndpoint(
          { city: data.city, country: data.country, lat: data.lat, lng: data.lng, language: data.language, allowFoodStops: wantsFood },
          endConstraint,
          3,
          candidateStops,
        );
        const merged = dedupeStops([...candidateStops, ...endpointStops]);
        const closestToEndpoint = (endpointStops.length ? endpointStops : merged)
          .filter((s) => typeof s.lat === "number" && typeof s.lng === "number")
          .sort((a, b) => haversineMeters(a as Coord, endConstraint) - haversineMeters(b as Coord, endConstraint))[0];
        if (closestToEndpoint) {
          const closestKey = closestToEndpoint.placeId || normalizeName(closestToEndpoint.name);
          const withoutClosest = candidateStops.filter((s) => (s.placeId || normalizeName(s.name)) !== closestKey);
          const keepCount = Math.max(0, targetCount - 1);
          candidateStops = [...withoutClosest.slice(0, keepCount), closestToEndpoint];
        }
      }

      // Choose an anchor for both candidate selection and reordering: user GPS
      // when on-site, an explicit named start when supplied, otherwise city centre.
      let anchor = startConstraint
        ? { lat: startConstraint.lat, lng: startConstraint.lng }
        : { lat: data.lat, lng: data.lng };
      if (!startConstraint && typeof data.userLat === "number" && typeof data.userLng === "number") {
        const dy = data.userLat - data.lat;
        const dx = (data.userLng - data.lng) * Math.cos((data.lat * Math.PI) / 180);
        const approxKm = Math.sqrt(dy * dy + dx * dx) * 111;
        if (approxKm <= 25) anchor = { lat: data.userLat, lng: data.userLng };
      }

      const mandatoryStopKeys = new Set(
        [...validatedMandatory, ...(requestedCategoryStop ? [requestedCategoryStop] : [])]
          .map((stop) => stop.placeId || normalizeName(stop.name)),
      );
      const selectedCandidates =
        !isMulti && data.mode === "walk" && !endConstraint
          ? selectCompactRouteCandidates(
              candidateStops,
              targetCount,
              anchor,
              (stop) => mandatoryStopKeys.has(stop.placeId || normalizeName(stop.name)),
              data.mode,
            )
          : candidateStops.slice(0, targetCount);
      trace.phase("candidate_pool", {
        aiReturned: stops.length,
        afterValidation: candidateStops.length,
        selected: selectedCandidates.length,
      });
      // Fase 3 do funil: paragens selecionadas antes de ordenar.
      trace.phase("selected_before_order", {
        selected: selectedCandidates.length,
        pool: candidateStops.length,
      });
      let finalStops = selectedCandidates.map((s, i) =>
        isMultiDay ? { ...s, day: s.day ?? Math.min(days, Math.floor(i / Math.max(1, stopsPerDay)) + 1) } : s,
      );

      /* ---- Etapa genérica de distribuição geográfica por dias ----
       * Apenas o modo genérico multi-dia: zonas inteiras por dia antes da
       * ordenação interna; o alvo de ritmo é objetivo suave e nunca reparte
       * uma zona. Qualquer falha cai no clustering por eixo principal. */
      const buildGenericDayAnchors = (dayCount: number): DayAnchor[] =>
        Array.from({ length: dayCount }, (_, i) => ({
          start: i === 0 ? anchor : null,
          end:
            i === dayCount - 1 && endConstraint
              ? { lat: endConstraint.lat, lng: endConstraint.lng }
              : null,
        }));
      const assignGenericDays = (list: AIStop[], dayCount: number) =>
        assignStopsAcrossDays<AIStop>({
          stops: list,
          days: dayCount,
          dayAnchors: buildGenericDayAnchors(dayCount),
          endConstraint: endConstraint ? { lat: endConstraint.lat, lng: endConstraint.lng } : null,
          minPerDay: Math.max(1, minimumViableStopsPerDay),
          targetPerDay: targetItineraryStopsPerDay,
          budgetMinutesPerDay: Math.max(240, Math.round(activeHours * 60) - mealsPerDay),
          // Dia 1 com logística de chegada confirmada: janela útil menor.
          budgetMinutesPerDayByDay: usablePerDayByDay,
          minutesPerStop: visitMin,
          estimateDayMinutes: (dayList) => estimateRouteMinutes(dayList, visitMin, data.mode),
        });
      const traceDayGeoAssignment = (
        result: NonNullable<ReturnType<typeof assignGenericDays>>,
        dayCount: number,
      ) => {
        trace.phase("day_geo_assignment", {
          days: dayCount,
          zones: result.zones.length,
          zoneDays: result.zones.map((z) => z.day),
          movesApplied: result.moves.filter((m) => m.applied).length,
          rejected: result.rejectedCounts,
          complete: result.complete,
        });
      };

      if (isMultiDay && days > 1) {
        if (genericVisitMode) {
          const geoAssignment = assignGenericDays(finalStops, days);
          if (geoAssignment) {
            finalStops = geoAssignment.stops;
            traceDayGeoAssignment(geoAssignment, days);
          } else {
            finalStops = clusterStopsByDay(finalStops, days, anchor, endConstraint, data.mode);
          }
        } else {
          finalStops = clusterStopsByDay(finalStops, days, anchor, endConstraint, data.mode);
        }
      } else {
        // Single-day: reorder via nearest-neighbour from the anchor, but keep
        // the stop closest to an explicitly requested endpoint as the final stop.
        finalStops = orderNearest(finalStops, anchor, endConstraint ?? undefined, data.mode);
      }

      if (endConstraint && finalStops.length > 1) {
        const closest = finalStops
          .map((s, idx) => ({ s, idx }))
          .filter(({ s }) => typeof s.lat === "number" && typeof s.lng === "number")
          .sort((a, b) => haversineMeters(a.s as Coord, endConstraint) - haversineMeters(b.s as Coord, endConstraint))[0];
        if (closest && closest.idx !== finalStops.length - 1) {
          const without = finalStops.filter((_, idx) => idx !== closest.idx);
          finalStops = [...without, isMultiDay ? { ...closest.s, day: days } : closest.s];
        } else if (closest && isMultiDay && closest.s.day !== days) {
          finalStops = finalStops.map((s, idx) => idx === closest.idx ? { ...s, day: days } : s);
        }
      }

      // Hard-trim to fit the real time budget. The AI has no idea about
      // actual distances between stops, so if the ordered route would
      // require more time than the user asked for (visit + realistic
      // walking/driving transit + meal breaks), drop stops from the end
      // until it fits. This prevents the "asked for 3h, got 6h" case.
      let requiredPlacesExceedTime = false;
      if (!isMulti) {
        const budgetMin = Math.round(activeHours * 60) - mealsPerDay;
        const trimmed = trimOptionalStopsToBudget(
          finalStops,
          budgetMin,
          (candidate) => estimateRouteMinutes(candidate, visitMin, data.mode),
          (stop) => mandatoryStopKeys.has(stop.placeId || normalizeName(stop.name)),
          !!endConstraint,
        );
        finalStops = trimmed.stops;
        requiredPlacesExceedTime = trimmed.exceedsBudget && validatedMandatory.length > 0;
        trace.phase("schedule_filter", {
          before: selectedCandidates.length,
          after: finalStops.length,
          availableMinutes: budgetMin,
        });
      }

      // If validation and time trimming left a normal walking itinerary too
      // sparse, replenish it with real nearby places around the actual origin.
      // This avoids a six-hour request collapsing to only two or three stops
      // just because the first AI proposal mixed distant districts.
      if (!isMulti && data.mode === "walk" && finalStops.length < minimumUsefulStops && !focusMeta) {
        const supplemental = await searchLocalFallbackStops(
          {
            ...stopValidationContext,
            lat: anchor.lat,
            lng: anchor.lng,
            language: data.language,
            interests: sightseeingInterests,
            areaContains: areaContainsStop,
            diversified: diversifiedCollection,
          },
          minimumUsefulStops - finalStops.length,
          finalStops,
        );
        if (supplemental.length) {
          const compactCandidate = orderNearest(
            dedupeStops([...finalStops, ...supplemental]),
            anchor,
            endConstraint ?? undefined,
            data.mode,
          );
          const budgetMin = Math.round(activeHours * 60) - mealsPerDay;
          if (
            compactCandidate.length >= minimumUsefulStops &&
            estimateRouteMinutes(compactCandidate, visitMin, data.mode) <= budgetMin
          ) {
            finalStops = compactCandidate;
          }
        }
      }

      // Cumprimento da duração pedida (genérico, todos os modos): um pedido de
      // 7 horas não pode terminar em 2h50. Primeiro procuramos locais reais
      // próximos; só depois alongamos as visitas dentro de limites realistas.
      let planVisitMinutes = visitMin;
      let planFreeBreakMinutes = 0;
      if (!isMulti) {
        const budgetMin = Math.round(activeHours * 60) - mealsPerDay;
        const measure = (list: AIStop[], visit: number) =>
          estimateRouteMinutes(list, visit, data.mode);
        const transitEstimate = (list: AIStop[]) =>
          list.length > 1
            ? Math.max(0, Math.round((measure(list, visitMin) - list.length * visitMin) / (list.length - 1)))
            : 0;

        let fill = planDurationFill({
          requestedMinutes: budgetMin,
          plannedMinutes: measure(finalStops, visitMin),
          stopCount: finalStops.length,
          baseVisitMinutes: visitMin,
          avgTransitMinutes: transitEstimate(finalStops),
        });

        let moreStopsAvailable = true;
        if (!fill.satisfied && fill.extraStopsNeeded > 0 && !focusMeta) {
          const supplemental = await searchLocalFallbackStops(
            {
              ...stopValidationContext,
              lat: anchor.lat,
              lng: anchor.lng,
              language: data.language,
              interests: sightseeingInterests,
              areaContains: areaContainsStop,
              diversified: diversifiedCollection,
            },
            fill.extraStopsNeeded,
            finalStops,
          );
          moreStopsAvailable = supplemental.length > 0;
          if (supplemental.length) {
            const candidate = orderNearest(
              dedupeStops([...finalStops, ...supplemental]),
              anchor,
              endConstraint ?? undefined,
              data.mode,
            );
            const fitted = trimOptionalStopsToBudget(
              candidate,
              budgetMin,
              (list) => measure(list, visitMin),
              (stop) => mandatoryStopKeys.has(stop.placeId || normalizeName(stop.name)),
              !!endConstraint,
            );
            if (fitted.stops.length > finalStops.length) finalStops = fitted.stops;
          }
        }

        fill = planDurationFill({
          requestedMinutes: budgetMin,
          plannedMinutes: measure(finalStops, visitMin),
          stopCount: finalStops.length,
          baseVisitMinutes: visitMin,
          avgTransitMinutes: transitEstimate(finalStops),
          moreStopsAvailable,
        });
        planVisitMinutes = fill.visitMinutes;
        planFreeBreakMinutes = fill.freeBreakMinutes;
      }



      const boundaryChecked = await Promise.all(finalStops.map(async (stop) => {
        if (typeof stop.lat !== "number" || typeof stop.lng !== "number") return null;
        // Geometria da área ativa decide (bounds/ráio da identidade do
        // geocoder). O nome administrativo é apenas diagnóstico.
        if (!areaContainsStop(stop)) {
          trace.phase("boundary_reject", { name: stop.name });
          return null;
        }
        return stop;
      }));
      finalStops = boundaryChecked.filter((stop): stop is AIStop => !!stop);
      // Passe final e obrigatório: nenhuma exclusão explícita pode sobreviver
      // a fallback local, categoria pedida ou preenchimento por endpoint.
      finalStops = finalStops.filter((stop) => !isStopExcluded(stop));

      // Filtro RÍGIDO de categoria exclusiva: quando o pedido é "só X" (ou o
      // formulário tem uma única categoria), nenhum POI de outro tipo — nem
      // um rótulo genérico "Ponto de interesse" — pode chegar ao ecrã.
      // Autoridade multi-família: com famílias explícitas, o filtro LEGADO de
      // foco único não corre — o filtro MULTI-FAMÍLIA é vinculativo em vez
      // disso, exceto para paragens obrigatórias/fixas (prova de localidade
      // já revalidada por outras barreiras).
      if (explicitTourismFamilies.length > 0) {
        const familyBinder = filterStopsByTourismFamilies(explicitTourismFamilies, finalStops);
        if (familyBinder.length > 0) {
          const kept = new Set(familyBinder.map((b) => b.stop));
          const mandatoryByName = new Set(requiredPlaces.map((p) => normalizeName(p)));
          const mandatoryKept = finalStops.filter(
            (s) =>
              !kept.has(s) &&
              (s.category === "Ponto obrigatório" || mandatoryByName.has(normalizeName(s.name))),
          );
          finalStops = [...familyBinder.map((b) => b.stop), ...mandatoryKept];
          trace.phase("family_filter", {
            families: explicitTourismFamilies,
            before: familyBinder.length + mandatoryKept.length,
            familyMatched: familyBinder.length,
            mandatoryKept: mandatoryKept.length,
          });
        }
      } else if (data.categoryFocus) {
        finalStops = filterStopsByFocus(data.categoryFocus as FocusCategory, finalStops);
      }

      /* ---- Validação final vinculativa: localidade + categoria + cozinha ----
       * Última barreira antes de apresentar/guardar a rota. Nenhum local que
       * falhe a localidade explícita, o pedido "só restaurantes" ou a cozinha
       * explícita pode chegar ao ecrã. Nunca se alarga a zona em silêncio.
       */
      const requestText = [
        data.notes ?? "",
        data.requestedStopQuery ?? "",
        data.categoryFocusQuery ?? "",
        data.cuisine ?? "",
      ].join(" ");
      const restaurantsOnlyRequest =
        detectRestaurantsOnlyRequest(requestText) ||
        (!mentionsAttractions(requestText) &&
          (data.categoryFocus === "restaurant" || data.requestedStopCategory === "restaurant"));
      const explicitLocality = data.explicitLocality?.trim() || null;
      let finalValidationScarce: { cuisine: string; area: string; found: number } | null = null;
      // Paragens válidas removidas pela validação final por prova de
      // localidade: se isto acontece, a falha é interna (collector_fault),
      // nunca "escassez da área".
      let finalValidationLocalityRejections = 0;

      // Modo genérico: a última barreira territorial é a ActiveSearchArea
      // (geometria); a validação por localidade explícita só corre nos ramos
      // específicos (restaurantes/cozinha/localidade vinculativa).
      if (restaurantsOnlyRequest || cuisineIntent || (explicitLocality && !genericVisitMode)) {
        const localityArea = explicitLocality
          ? await resolveLocalityArea(explicitLocality, data.country, data.language)
          : null;

        const evidenceFor = async (stop: AIStop) => {
          let types = stop.placeTypes;
          let primaryType = stop.placePrimaryType;
          let localityNames = stop.placeLocalityNames;
          if ((!types?.length || !localityNames?.length) && stop.placeId) {
            const fetched = await fetchPlaceEvidence(stop.placeId, data.language);
            if (fetched) {
              types = types?.length ? types : fetched.types;
              primaryType = primaryType ?? fetched.primaryType;
              localityNames = localityNames?.length ? localityNames : fetched.localityNames;
            }
          }
          const coord =
            typeof stop.lat === "number" && typeof stop.lng === "number"
              ? { lat: stop.lat, lng: stop.lng }
              : null;
          const insideExactLocalityBounds =
            !!coord && !!localityArea?.bounds && insideLocalityBounds(coord, localityArea.bounds);
          const insideRequestedArea = !explicitLocality
            ? true
            : coord
              ? localityArea?.bounds
                ? insideExactLocalityBounds
                : await isCoordinateInsideRequestedLocality(coord, {
                    city: explicitLocality,
                    country: data.country,
                    language: data.language,
                  })
              : false;
          return {
            stop: { ...stop, placeTypes: types, placePrimaryType: primaryType, placeLocalityNames: localityNames },
            evidence: {
              name: stop.name,
              primaryType,
              types,
              localityNames,
              insideRequestedArea,
              insideExactLocalityBounds,
            },
          };
        };

        const validationOptions = {
          requestedLocality: explicitLocality,
          localityAliases: [
            ...(localityArea?.aliases ?? []),
            ...(expandedArea?.localities ?? []),
          ],
          restaurantsOnly: restaurantsOnlyRequest,
          cuisine: cuisineIntent,
          allowMarket: cuisineAllowsMarket || requestsMarket(requestText),
        };

        const validateAll = async (stops: AIStop[]) => {
          const enriched = await Promise.all(stops.map(evidenceFor));
          const { kept, rejected } = validateFinalPlaces(
            enriched,
            (item) => item.evidence,
            validationOptions,
          );
          if (rejected.length) {
            finalValidationLocalityRejections += rejected.filter((r) => r.reason === "locality").length;
            console.info("final validation removed stops", {
              locality: explicitLocality,
              rejected: rejected.map((r) => [r.item.stop.name, r.reason]),
            });
          }
          return kept.map((item) => item.stop);
        };

        const targetStops = finalStops.length;
        finalStops = await validateAll(finalStops);

        // Substituir os removidos DENTRO da mesma localidade — nunca fora.
        const missing = targetStops - finalStops.length;
        if (missing > 0 && (restaurantsOnlyRequest || cuisineIntent)) {
          const replacements = await searchRequestedCategoryStops(
            {
              ...categorySearchContext,
              city: explicitLocality ?? categorySearchContext.city,
              requireAdministrativeLocalityMatch: true,
            },
            {
              category: "restaurant",
              query: cuisineIntent ? cuisineSearchQuery(cuisineIntent) : "restaurants",
              label: categoryFocusDisplayLabel("restaurant", data.language),
              cuisine: cuisineIntent,
              allowMarket: validationOptions.allowMarket,
            },
            finalStops,
            Math.min(8, missing),
          );
          if (replacements.length) {
            finalStops = [...finalStops, ...(await validateAll(replacements))];
          }
        }

        if (finalStops.length === 0 && (restaurantsOnlyRequest || cuisineIntent)) {
          finalValidationScarce = {
            cuisine: cuisineIntent?.requested ?? "",
            area: explicitLocality ?? data.city,
            found: 0,
          };
        }
      }


      // Especialidade gastronómica local: prova de categoria ≠ prova de
      // produto. A etiqueta de produto só aparece com evidência específica.
      if (data.categoryFocus === "sweets" || data.categoryFocus === "restaurant") {
        const specialtyIntent = detectLocalSpecialtyIntent(data.notes ?? "");
        finalStops = annotateStopsWithSpecialty(finalStops, {
          focus: data.categoryFocus as FocusCategory,
          targetProduct: specialtyIntent.targetProduct ?? null,
          language: data.language,
        });
      }

      /* ---- Restrições obrigatórias do texto (contrato do pedido) ----
       * "um restaurante japonês e um kartódromo": cada restrição tem de ser
       * satisfeita por uma paragem REAL e DISTINTA, provada pelos tipos
       * oficiais do Places. Nunca é substituída por igreja/praia/farol/
       * miradouro. Se faltar, o ecrã recebe o motivo estruturado.
       */
      const requiredConstraints = data.requiredStopConstraints ?? [];
      let missingRequiredConstraints: string[] = [];

      /* ---- Paragens fixas escolhidas pela pessoa ("Escolher este local") ----
       * O placeId chega sem geometria nem tipos: o servidor re-confirma via
       * Place Details. A paragem deixa de ser rejeitada pela localidade exata,
       * mas continua sujeita à geometria da área alargada confirmada e entra
       * na correspondência de restrições pelos tipos oficiais reais.
       */
      const pinnedRefs = data.pinnedStops ?? [];
      if (pinnedRefs.length) {
        const { resolvePinnedStops } = await import("./pinned-stops.server");
        const pinnedResolved = await resolvePinnedStops(
          pinnedRefs.map((pinned) => pinned.placeId),
          { language: data.language, expandedArea },
        );
        if (pinnedResolved.length) {
          finalStops = dedupeStops([
            ...pinnedResolved.map((stop) =>
              isMultiDay ? { ...stop, day: 1 } : stop,
            ),
            ...finalStops,
          ]);
        }
        if (pinnedResolved.length < pinnedRefs.length) {
          console.info("pinned stops dropped", {
            requested: pinnedRefs.length,
            resolved: pinnedResolved.length,
          });
        }
      }

      if (requiredConstraints.length) {
        /* ---- Satisfação de restrições (fonte da verdade única) ----
         * - source "tourism_family": satisfação decidida EXCLUSIVAMENTE pelo
         *   predicado da taxonomia (classifyPlaceForFamily) sobre os tipos
         *   reais do fornecedor + sinal de nome — o pool já contém os
         *   candidatos injetados da recolha por famílias.
         * - "named_place" | "food" | "text": caminho legado (tipos oficiais
         *   do Places + sinal secundário de nome), inalterado.
         */
        let match = matchConstraintsToStops(requiredConstraints, finalStops);
        const legacyMissing = match.missing.filter((c) => c.source !== "tourism_family");
        const tourismMissing = match.missing.filter((c) => c.source === "tourism_family");

        for (const constraint of legacyMissing) {
          const found = await searchRequestedCategoryStops(
            {
              ...categorySearchContext,
              city: explicitLocality ?? categorySearchContext.city,
              // Depois de o utilizador alargar a zona, um kartódromo em
              // Perafita é aceitável para "Leça da Palmeira"; sem alargamento
              // a localidade continua vinculativa.
              requireAdministrativeLocalityMatch: !!explicitLocality && !expandedArea,
              ...(expandedArea ? { radiusMeters: expandedArea.radiusMeters } : {}),
            },
            { query: placesSearchQuery(constraint) },
            finalStops,
            6,
          );
          const candidate = found.find((s) => stopSatisfiesConstraint(constraint, s));
          if (candidate) {
            finalStops = [...finalStops, isMultiDay ? { ...candidate, day: candidate.day ?? 1 } : candidate];
          }
        }

        // Satisfação turística: prova por família com o predicado único.
        const perFamilyCounts = new Map<TourismFamilyId, number>();
        for (const stop of finalStops) {
          const fam = explicitTourismFamilies.find((family) =>
            classifyPlaceForFamily(family, {
              name: stop.name,
              types: stop.placeTypes ?? null,
              primaryType: stop.placePrimaryType ?? null,
            }).accepted,
          );
          if (fam) perFamilyCounts.set(fam, (perFamilyCounts.get(fam) ?? 0) + 1);
        }
        trace.phase("tourism_satisfaction_evaluated", {
          families: explicitTourismFamilies,
          perFamilyCounts: Object.fromEntries(perFamilyCounts),
          tourismConstraints: tourismMissing.map((c) => ({
            kind: c.kind,
            source: c.source,
            tourismFamily: c.tourismFamily,
            query: c.query,
          })),
          poolSize: finalStops.length,
        });

        // Uma restrição turística fica satisfeita quando a sua família tem
        // ≥1 local no pool final. Museus nunca mascaram miradouros.
        const unmetTourism = tourismMissing.filter(
          (c) => c.tourismFamily && !perFamilyCounts.get(c.tourismFamily),
        );
        missingRequiredConstraints = unmetTourism.map((c) => c.query);
        if (missingRequiredConstraints.length) {
          const byFamily = unmetTourism
            .map((c) => c.tourismFamily!)
            .filter((family, index, arr) => arr.indexOf(family) === index);
          // Escassez provada: a recolha da família em falta concluiu as suas
          // estratégias sem erro técnico e devolveu zero elegíveis, e o pool
          // final não tem nenhum local dessa família. Falhas técnicas da
          // recolha já saíram antes como technicalFailure estruturada —
          // escassez de família ≠ falha do coletor.
          const technicalReports = (familyCollectionRef.current?.reports ?? []).filter(
            (report) => byFamily.includes(report.family) && report.technicalFailure,
          );
          if (technicalReports.length) {
            trace.phase("tourism_family_collection_technical", {
              reports: technicalReports.map((r) => ({
                family: r.family,
                accepted: r.accepted,
                technicalFailure: r.technicalFailure,
              })),
            });
            return releaseFailedGeneration("family_collection_incomplete");
          }
          console.info("required constraints unmet", { missing: missingRequiredConstraints, byFamily });
          return {
            ...(await releaseFailedGeneration("required_constraints_unmet")),
            missingRequiredConstraints,
          };
        }
      }


      const finalStopKeys = new Set(
        finalStops.flatMap((stop) => [stop.placeId, normalizeName(stop.name)].filter(Boolean) as string[]),
      );
      const omittedResolvedRequired = mandatoryResolutionResults
        .filter((result) => {
          const stop = result.stop;
          return !!stop && !finalStopKeys.has(stop.placeId || normalizeName(stop.name));
        })
        .map((result) => result.requestedName);
      const missingRequiredPlaces = Array.from(
        new Set([...unresolvedRequiredPlaces, ...omittedResolvedRequired]),
      );

      // When the traveler asked for an EXHAUSTIVE listing ("todos os pontos"),
      // the AI decides how many days are actually needed based on real content.
      // Never force the trip to span the user's original duration if there
      // aren't enough real places to fill it — collapse to the smallest number
      // of days that fits the returned stops.
      if (wantsExhaustive && finalStops.length > 0) {
        const neededDays = Math.max(1, Math.ceil(finalStops.length / Math.max(3, stopsPerDay)));
        const currentMaxDay = finalStops.reduce((m, s) => Math.max(m, s.day ?? 1), 1);
        if (neededDays < currentMaxDay) {
          if (neededDays > 1) {
            if (genericVisitMode) {
              const geoAssignment = assignGenericDays(finalStops, neededDays);
              if (geoAssignment) {
                finalStops = geoAssignment.stops;
                traceDayGeoAssignment(geoAssignment, neededDays);
              } else {
                finalStops = clusterStopsByDay(finalStops, neededDays, anchor, endConstraint, data.mode);
              }
            } else {
              finalStops = clusterStopsByDay(finalStops, neededDays, anchor, endConstraint, data.mode);
            }
          } else {
            finalStops = orderNearest(
              finalStops.map((s) => ({ ...s, day: undefined })),
              anchor,
              endConstraint ?? undefined,
              data.mode,
            );
          }
        }
      }

      // Os dias pedidos são vinculativos: sem pedido exaustivo, nenhuma
      // paragem pode cair num dia além do último dia pedido.
      if ((!wantsExhaustive || data.requestedDays) && isMultiDay) {
        finalStops = finalStops.map((s) =>
          typeof s.day === "number" && s.day > days ? { ...s, day: days } : s,
        );
      }


      // ===== Garantia B — barreira final de comida =====
      // Se foodStopIntent=false (foodIntent é o alias), NENHUMA paragem
      // alimentar sobrevive ao resultado final, independentemente de onde
      // tenha vindo (IA, fallback, enriquecimento). Decisão baseada apenas em
      // primaryType/types reais. NOTA: esta barreira só afeta PARAGENS DE
      // VISITA — os cartões de almoço/jantar (mealMode) nunca passam por
      // aqui, pelo que uma sugestão de refeição nunca é removida.
      if (!foodIntent) {
        const beforeBarrier = finalStops.length;
        finalStops = finalStops.filter((s) =>
          !isFoodPlace({ primaryType: s.placePrimaryType, types: s.placeTypes }),
        );
        if (finalStops.length < beforeBarrier) {
          console.info("[food-intent] final barrier removed food stops", JSON.stringify({
            city: data.city, removed: beforeBarrier - finalStops.length,
          }));
        }
        trace.phase("food_filter", { before: beforeBarrier, after: finalStops.length });
      }

      // ===== Qualidade temporal dos dias (só modo genérico multi-dia) =====
      // Reequilíbrio por paragens E minutos úteis, com orçamentos e alvos por
      // dia (o dia 1 com logística de chegada confirmada perde minutos).
      // Qualidade, nunca barreira: sem movimentos válidos, a rota segue.
      if (genericVisitMode && isMultiDay && days > 1 && finalStops.length > 1) {
        const byDay: AIStop[][] = Array.from({ length: days }, () => []);
        for (const stop of finalStops) {
          const dayIdx = Math.min(
            days - 1,
            Math.max(0, (typeof stop.day === "number" && stop.day >= 1 ? stop.day : 1) - 1),
          );
          byDay[dayIdx].push(stop);
        }
        let rebalanced: ReturnType<typeof rebalanceDayQuality<AIStop>> | null = null;
        try {
          rebalanced = rebalanceDayQuality<AIStop>({
            days: byDay,
            minutesPerStop: planVisitMinutes,
            budgetMinutesPerDay: Math.max(240, Math.round(activeHours * 60) - mealsPerDay),
            budgetMinutesPerDayByDay: usablePerDayByDay,
            targetPerDay: targetItineraryStopsPerDayByDay,
            minPerDay: Math.max(1, minimumViableStopsPerDay),
            spareCandidates: candidateStops,
            estimateDayMinutes: (list) => estimateRouteMinutes(list, planVisitMinutes, data.mode),
            // A pé, um dia curto só pode ser preenchido perto; com transporte
            // aceita-se a zona alargada em vez de deixar a tarde vazia.
            maxFillRadiusMeters: data.mode === "walk" ? 4000 : 12000,
          });
        } catch (qualityError) {
          trace.phase("day_quality_rebalance", {
            error: qualityError instanceof Error ? qualityError.message : String(qualityError),
          });
        }
        if (rebalanced) {
          finalStops = rebalanced.days.flat();
          trace.phase("day_quality_rebalance", {
            moves: rebalanced.moves.length,
            perDay: rebalanced.perDay.map((d) => ({
              day: d.day,
              stops: d.stops,
              usefulMinutes: d.usefulMinutes,
            })),
          });
        }
        // Experiência noturna por dia: etiqueta a paragem escolhida (aberta à
        // noite com horários confirmados, ou vista exterior) e move-a para o
        // fim do dia — a agenda ancora o jantar antes dela.
        const toEveningCandidate = (stop: AIStop): EveningCandidate | null => {
          if (typeof stop.lat !== "number" || typeof stop.lng !== "number") return null;
          return {
            name: stop.name,
            placeTypes: stop.placeTypes,
            placePrimaryType: stop.placePrimaryType,
            category: stop.category,
            openingPeriods: stop.openingPeriods ?? null,
          };
        };
        const eveningTags: Array<{ day: number; name: string; level: EveningSuitability }> = [];
        if (rebalanced) {
          finalStops = rebalanced.days
            .map((dayStops, dayIdx) => {
              const evening = pickEveningStop(dayStops, toEveningCandidate);
              if (!evening) return dayStops;
              eveningTags.push({ day: dayIdx + 1, name: evening.stop.name, level: evening.level });
              const rest = dayStops.filter((s) => s !== evening.stop);
              return [...rest, { ...evening.stop, evening: true }];
            })
            .flat();
        } else {
          // Sem reequilíbrio: mantém a ordem, apenas etiqueta/move a noturna.
          const grouped: AIStop[][] = Array.from({ length: days }, () => []);
          for (const stop of finalStops) {
            const dayIdx = Math.min(
              days - 1,
              Math.max(0, (typeof stop.day === "number" && stop.day >= 1 ? stop.day : 1) - 1),
            );
            grouped[dayIdx].push(stop);
          }
          finalStops = grouped
            .map((dayStops, dayIdx) => {
              const evening = pickEveningStop(dayStops, toEveningCandidate);
              if (!evening) return dayStops;
              eveningTags.push({ day: dayIdx + 1, name: evening.stop.name, level: evening.level });
              const rest = dayStops.filter((s) => s !== evening.stop);
              return [...rest, { ...evening.stop, evening: true }];
            })
            .flat();
        }
        if (eveningTags.length) {
          trace.phase("evening_stops", { tags: eveningTags });
        }
      }

      // ===== Diversidade de visita (apenas modo realmente genérico) =====
      // Fases 4-5 do funil: paragens após horários/ordenação e por dia.
      trace.phase("after_schedule_order", { stops: finalStops.length });
      const collectionForScarcity = collectionRef.current;
      /* A recolha só é "completa" quando todos os temas previstos foram
       * tentados, sem erro técnico e sem teto atingido antes do fim. Se ficou
       * incompleta, a causa é técnica/de recolha — nunca "a cidade tem poucos
       * locais". */
      const collectionIncomplete = !!collectionForScarcity && (
        collectionForScarcity.errors > 0 ||
        collectionForScarcity.budgetExhausted ||
        collectionForScarcity.themesCompleted < collectionForScarcity.themesAttempted
      );
      const scarcityRequestedMinutes = Math.max(
        0,
        Math.round(activeHours * 60) - mealsPerDay * totalDays,
      );
      // Contagens de elegibilidade da RECOLHA DIVERSIFICADA (fase intermédia
      // do funil — NUNCA as paragens finais): `eligible` = passaram os filtros
      // intencionais; `scheduleEligible` = desses, os que encaixam numa janela
      // diurna de visita (horas desconhecidas nunca desqualificam).
      const complementary_collection_eligible =
        collectionForScarcity?.filterRejections?.accepted ?? 0;
      const acceptedPool = collectionForScarcity?.acceptedCandidates;
      const complementary_collection_schedule_eligible =
        acceptedPool && acceptedPool.length > 0
          ? acceptedPool.filter((c) => candidateFitsVisitingWindow(c.openingPeriods ?? null, visitMin)).length
          : complementary_collection_eligible;
      trace.phase("schedule_eligible_candidates", {
        complementary_collection_eligible,
        complementary_collection_schedule_eligible,
      });
      const scarcityBase = {
        city: data.city,
        target: targetCandidatePool,
        minimum: minimumTotalStops,
        minimumViableStopsPerDay: Array.from({ length: totalDays }, () => minimumViableStopsPerDay),
        targetStops: targetTotalStops,
        durationDays: totalDays,
        areaLabel: activeArea?.requestedLabel ?? data.city,
        areaRadiusMeters: activeArea ? Math.round(generationAreaRadiusMeters(activeArea)) : 0,
        collectionComplete: !collectionIncomplete,
        // Causa da escassez: falha técnica da recolha, falha interna dos
        // filtros (collector_fault — candidatos elegíveis barrados por uma
        // barreira administrativa contraditória) ou escassez real da área.
        // Remoções intencionais (restaurantes fora de pedido de comida)
        // nunca entram nesta classificação.
        eligibleCount: complementary_collection_eligible,
        cause: classifyScarcityCause({
          collectionComplete: !collectionIncomplete,
          finalStops: finalStops.length,
          minimumStops: minimumTotalStops,
          administrativeRejections:
            (collectionForScarcity?.filterRejections?.rejectedLocalityAdmin ?? 0) +
            finalValidationLocalityRejections,
          finalValidationRemoved: false,
          eligibleCandidates: complementary_collection_eligible,
          scheduleEligibleCandidates: complementary_collection_schedule_eligible,
        }),
      };
      let visitScarcity: {
        city: string;
        found: number;
        target: number;
        minimum: number;
        /** Mínimo de segurança por dia, na ordem dos dias. */
        minimumViableStopsPerDay?: number[];
        /** Paragens finais por dia, na ordem dos dias. */
        stopsPerDay?: number[];
        targetStops: number;
        coveredMinutes: number;
        requestedMinutes: number;
        durationDays: number;
        areaLabel: string;
        areaRadiusMeters: number;
        collectionComplete: boolean;
        eligibleCount?: number;
        cause:
          | "collection_incomplete"
          | "area_scarcity"
          | "collector_fault"
          | "route_assembly_failure"
          | "schedule_unavailable";
      } | null = null;
      // Diversidade de visita: sinal PROVISÓRIO apenas. Não decide o
      // resultado — a decisão única acontece na barreira final de
      // completude sobre as PARAGENS FINAIS.
      let provisionalDiversityFailed = false;
      if (genericVisitMode && finalStops.length > 0) {
        const diversity = evaluateVisitDiversity({
          stops: finalStops,
          totalDays: days,
          genericVisitMode: true,
        });
        console.info("[visit-diversity]", JSON.stringify({
          city: data.city,
          ok: diversity.ok,
          days: diversity.days.map((d) => ({
            day: d.day, stops: d.stops, families: d.families,
            dominantShare: d.dominantShare, failures: d.failures,
          })),
        }));
        provisionalDiversityFailed = !diversity.ok;
      }

      // Nota de roteiro mais leve: paragens finais no mínimo de segurança mas
      // abaixo do alvo de ritmo — a rota é válida, com aviso de tempo livre.
      let itineraryPaceNotice: { target: number; stops: number } | null = null;
      let interestFillNotice: { interests: string[] } | null = null;
      let calmSmallPlaceNotice: { area: string; stops: number; areaRadiusMeters: number } | null = null;
      // Barreira FINAL e ÚNICA de decisão — corre depois de TODAS as reduções
      // do funil, sobre as PARAGENS FINAIS. Estados provisórios (diversidade,
      // recolha incompleta) são apenas diagnósticos: nenhuma rota que cumpra o
      // contrato é bloqueada por um sinal intermédio. A decisão usa apenas os
      // mínimos de segurança (total e por dia); cobertura e alvo de ritmo são
      // avisos.
      if (genericVisitMode) {
        const requestedMinutes = scarcityRequestedMinutes;
        const coveredMinutes = isMulti
          ? finalStops.length * planVisitMinutes
          : estimateRouteMinutes(finalStops, planVisitMinutes, data.mode) + planFreeBreakMinutes;
        // Paragens finais por dia (dia ausente = 0 paragens).
        const stopsPerDay: number[] = Array.from({ length: days }, () => 0);
        for (const stop of finalStops) {
          const dayIdx = (typeof stop.day === "number" && stop.day >= 1 ? stop.day : 1) - 1;
          if (dayIdx >= 0 && dayIdx < stopsPerDay.length) {
            stopsPerDay[dayIdx] += 1;
          }
        }
        // Minutos realmente ocupados em cada dia, para detetar dias que
        // acabam a meio da manhã (aviso day_ends_early, nunca bloqueio).
        const usefulMinutesByDay = Array.from({ length: days }, (_, i) =>
          estimateRouteMinutes(
            finalStops.filter((s) => ((typeof s.day === "number" && s.day >= 1 ? s.day : 1) - 1) === i),
            planVisitMinutes,
            data.mode,
          ),
        );
        const perDay = stopsPerDay.map((stops, i) => ({
          day: i + 1,
          stops,
          minimumViableStops: minimumViableStopsPerDay,
          usefulMinutes: usefulMinutesByDay[i],
          budgetMinutes: usablePerDayByDay?.[i],
        }));
        const completeness = assessGenericRouteCompleteness({
          stopCount: finalStops.length,
          minimumStops: minimumTotalStops,
          perDay,
          targetStops: targetTotalStops,
          requestedMinutes,
          coveredMinutes,
        });
        // Fase 5 explícita: paragens finais por dia + barreira decisória.
        trace.phase("final_per_day", { stopsPerDay });
        // Auditoria de custo (H.5/H.7): contagens por tipo de chamada paga,
        // comparáveis com os Billing Reports por SKU. Sem dados pessoais.
        trace.phase("places_budget_used", {
          used: placesBudget.snapshot().used,
          source: placesBudget.snapshot().source,
        });

        trace.phase("final_completeness", {
          finalStops: completeness.stopCount,
          minimumTotalStops: completeness.minimumStops,
          minimumViableStopsPerDay,
          targetItineraryStopsPerDay,
          targetTotalStops: completeness.targetStops,
          stopsPerDay,
          usefulMinutesByDay,
          earlyDays: completeness.earlyDays,
          requestedMinutes: completeness.requestedMinutes,
          coveredMinutes: completeness.coveredMinutes,
          coverageRatio: completeness.coverageRatio,
          failures: completeness.failures,
          warnings: completeness.warnings,
          complete: completeness.complete,
        });
        if (completeness.complete) {
          // Contrato cumprido: qualquer estado provisório de escassez é
          // descartado — a rota é apresentada.
          if (provisionalDiversityFailed) {
            trace.phase("provisional_state_discarded", {
              reason: "diversity_failed_but_final_stops_sufficient",
            });
          }
          if (
            completeness.targetStops !== null &&
            completeness.stopCount < completeness.targetStops
          ) {
            itineraryPaceNotice = {
              target: completeness.targetStops,
              stops: completeness.stopCount,
            };
          }
        } else if (
          acceptsCalmSmallPlaceRoute({
            cause: scarcityBase.cause,
            collectionComplete: scarcityBase.collectionComplete,
            stopsPerDay,
          })
        ) {
          // Modo Aldeia: a localidade é pequena (escassez real confirmada).
          // Entrega-se um roteiro calmo só dentro dela; alargar fica opcional.
          calmSmallPlaceNotice = {
            area: scarcityBase.areaLabel,
            stops: completeness.stopCount,
            areaRadiusMeters: scarcityBase.areaRadiusMeters,
          };
          trace.phase("calm_small_place_accepted", { stopsPerDay });
        } else {
          visitScarcity = {
            ...scarcityBase,
            found: completeness.stopCount,
            stopsPerDay,
            coveredMinutes: completeness.coveredMinutes,
            requestedMinutes: completeness.requestedMinutes,
          };
        }
      }

      /* Escassez da recolha diversificada: o teto de consultas esgotou-se sem
       * candidatos suficientes na área ativa. É apenas um sinal de contexto
       * para a barreira final — nunca um atalho que bloqueie uma rota válida.
       * A decisão é tomada uma única vez, sobre as paragens finais. */
      const collection = collectionRef.current;
      if (collection?.scarcity) {
        trace.phase("collect_scarcity_result", {
          found: collection.scarcity.found,
          target: collection.scarcity.target,
          queriesUsed: collection.scarcity.queriesUsed,
          pagesUsed: collection.scarcity.pagesUsed,
          budgetExhausted: collection.scarcity.budgetExhausted,
          themesAttempted: collection.themesAttempted,
          themesCompleted: collection.themesCompleted,
          errors: collection.errors,
          stops: finalStops.length,
        });
      }


      /* Regra de decisão: se há candidatos elegíveis e abertos em número
       * suficiente mas a montagem assistida falhou, a causa é
       * route_assembly_failure — tenta-se a montagem determinística a partir
       * desses candidatos antes de mostrar qualquer cartão. */
      if (visitScarcity && genericVisitMode && visitScarcity.cause === "route_assembly_failure") {
        let assembled: ReturnType<typeof assembleDeterministicRoute<AIStop>> | null = null;
        try {
          trace.phase("deterministic_fallback", {
            trigger: "route_assembly_failure",
            finalStops: finalStops.length,
          });
          assembled = assembleDeterministicRoute<AIStop>({
            candidates: candidateStops,
            days,
            minimumViableStopsPerDay,
            targetStopsPerDay: targetItineraryStopsPerDay,
            budgetMinutesPerDay: Math.max(240, Math.round(activeHours * 60) - mealsPerDay),
            budgetMinutesPerDayByDay: usablePerDayByDay,
            visitMinutes: planVisitMinutes,
            anchor,
            estimateMinutes: (list, v) => estimateRouteMinutes(list, v, data.mode),
            // Apenas este caminho genérico multi-dia usa a etapa geográfica;
            // o reequilíbrio de mínimos fica como último recurso.
            useGeoDayAssignment: genericVisitMode && days > 1,
            dayAnchors: buildGenericDayAnchors(days),
          });
        } catch (assemblyError) {
          // Fase 6 do funil: exceção concreta na montagem de fallback.
          trace.phase("assembly_error", {
            stage: "deterministic_fallback",
            message: assemblyError instanceof Error ? assemblyError.message : String(assemblyError),
          });
        }
        if (assembled?.complete) {
          finalStops = assembled.stops;
          visitScarcity = null;
          trace.phase("assembly_fallback_ok", {
            stops: assembled.stops.length,
            stopsPerDay: assembled.stopsPerDay,
            belowTarget: assembled.belowTarget,
          });
          if (assembled.belowTarget) {
            itineraryPaceNotice = { target: targetTotalStops, stops: assembled.stops.length };
          }
        } else {
          trace.phase("assembly_error", {
            stage: "deterministic_fallback",
            assembledStops: assembled?.stops.length ?? 0,
            stopsPerDay: assembled?.stopsPerDay ?? [],
          });
        }
      }

      /* Preenchimento de segurança (interesses raros/combinados): quando os
       * interesses escolhidos não deram paragens suficientes, completamos com
       * os destaques gerais da cidade — consultas genéricas servidas primeiro
       * pela cache de pesquisas (90 dias), por isso quase sempre custo 0.
       * Mantém as paragens que cumprem os interesses e avisa o viajante. */
      if (
        visitScarcity &&
        genericVisitMode &&
        visitScarcity.cause !== "collector_fault" &&
        sightseeingInterests.length > 0 &&
        !focusMeta
      ) {
        try {
          const needed = Math.max(1, (targetTotalStops ?? minimumTotalStops) - finalStops.length);
          const highlights = await searchLocalFallbackStops(
            {
              ...stopValidationContext,
              lat: anchor.lat,
              lng: anchor.lng,
              language: data.language,
              interests: [],
              areaContains: areaContainsStop,
              diversified: diversifiedCollection,
            },
            needed,
            finalStops,
          );
          const pool = dedupeStops([...finalStops, ...candidateStops, ...highlights]).filter(
            (stop) => !isStopExcluded(stop) && (wantsFood || !isFoodLikeCategory(`${stop.category} ${stop.name}`)),
          );
          const filled = assembleDeterministicRoute<AIStop>({
            candidates: pool,
            days,
            minimumViableStopsPerDay,
            targetStopsPerDay: targetItineraryStopsPerDay,
            budgetMinutesPerDay: Math.max(240, Math.round(activeHours * 60) - mealsPerDay),
            budgetMinutesPerDayByDay: usablePerDayByDay,
            visitMinutes: planVisitMinutes,
            anchor,
            estimateMinutes: (list, v) => estimateRouteMinutes(list, v, data.mode),
            useGeoDayAssignment: days > 1,
            dayAnchors: buildGenericDayAnchors(days),
          });
          trace.phase("interest_soft_fill", {
            before: finalStops.length,
            highlights: highlights.length,
            assembled: filled?.stops.length ?? 0,
            complete: !!filled?.complete,
          });
          if (filled && (filled.complete || filled.stops.length >= Math.max(finalStops.length + 1, 2))) {
            finalStops = filled.stops;
            visitScarcity = null;
            interestFillNotice = { interests: sightseeingInterests };
            if (filled.belowTarget) {
              itineraryPaceNotice = { target: targetTotalStops, stops: filled.stops.length };
            }
          }
        } catch (fillError) {
          trace.phase("assembly_error", {
            stage: "interest_soft_fill",
            message: fillError instanceof Error ? fillError.message : String(fillError),
          });
        }
      }

      if (visitScarcity) {
        // Ajuste do cartão para route_assembly_failure: a contagem elegível
        // mostrada é a da RECOLHA (fase intermédia) — nunca apresentada como
        // paragens finais.
        if (visitScarcity.cause === "route_assembly_failure") {
          visitScarcity.eligibleCount = complementary_collection_eligible;
        }
        trace.phase("visit_scarcity_result", visitScarcity);
        return { ...(await releaseFailedGeneration("visit_scarcity")), visitScarcity };
      }

      if (!isChargeableItineraryResult({ stops: finalStops, error: null })) {
        // Escassez de visita em modo genérico: devolve cartão de escassez
        // (opções de alargamento/interesses), nunca rota de restaurantes.
        // Sem locais válidos por causa da localidade/categoria/cozinha: o ecrã
        // tem de perguntar se pode alargar, nunca mostrar locais errados.
        if (finalValidationScarce) {
          return { ...(await releaseFailedGeneration("cuisine_scarce")), cuisineScarce: finalValidationScarce };
        }
        /* Rede de segurança final (best-effort): destino válido tem sempre
         * pontos de interesse. Antes de desistir, monta uma rota com os
         * destaques validados (cache primeiro) que caibam no tempo. */
        if (!focusMeta) {
          try {
            const target = Math.max(3, targetTotalStops ?? minimumTotalStops ?? 3);
            const highlights = await searchLocalFallbackStops(
              {
                ...stopValidationContext,
                lat: anchor.lat,
                lng: anchor.lng,
                language: data.language,
                interests: [],
                areaContains: areaContainsStop,
                diversified: diversifiedCollection,
              },
              target,
              [],
            );
            const pool = dedupeStops([...candidateStops, ...highlights]).filter(
              (stop) =>
                !isStopExcluded(stop) &&
                (wantsFood || !isFoodLikeCategory(`${stop.category} ${stop.name}`)),
            );
            let rescued: AIStop[] = [];
            try {
              const assembled = assembleDeterministicRoute<AIStop>({
                candidates: pool,
                days,
                minimumViableStopsPerDay: 1,
                targetStopsPerDay: targetItineraryStopsPerDay,
                budgetMinutesPerDay: Math.max(120, Math.round(activeHours * 60) - mealsPerDay),
                budgetMinutesPerDayByDay: usablePerDayByDay,
                visitMinutes: planVisitMinutes,
                anchor,
                estimateMinutes: (list, v) => estimateRouteMinutes(list, v, data.mode),
                useGeoDayAssignment: days > 1,
                dayAnchors: buildGenericDayAnchors(days),
              });
              rescued = assembled?.stops ?? [];
            } catch {
              rescued = [];
            }
            if (rescued.length === 0 && pool.length > 0) {
              // Último recurso: os mais próximos do ponto de partida.
              const dist = (s: AIStop) =>
                typeof s.lat === "number" && typeof s.lng === "number"
                  ? (s.lat - anchor.lat) ** 2 + (s.lng - anchor.lng) ** 2
                  : Number.POSITIVE_INFINITY;
              rescued = [...pool].sort((a, b) => dist(a) - dist(b)).slice(0, target);
            }
            trace.phase("best_effort_rescue", { pool: pool.length, rescued: rescued.length });
            if (rescued.length > 0) {
              finalStops = rescued;
              if (sightseeingInterests.length > 0) {
                interestFillNotice = { interests: sightseeingInterests };
              }
              if (rescued.length < target) {
                itineraryPaceNotice = { target, stops: rescued.length };
              }
            }
          } catch (rescueError) {
            trace.phase("assembly_error", {
              stage: "best_effort_rescue",
              message: rescueError instanceof Error ? rescueError.message : String(rescueError),
            });
          }
        }
        if (!isChargeableItineraryResult({ stops: finalStops, error: null })) {
          return releaseFailedGeneration("no_valid_stops");
        }
      }

      const costRecorded = await finishAttempt("success");
      if (!costRecorded) {
        return releaseFailedGeneration("cost_telemetry_failed");
      }

      // Paragens válidas do foco que sobreviveram até ao resultado final.
      const focusStopKeys = new Set(
        focusCategoryStops.map((stop) => stop.placeId || normalizeName(stop.name)),
      );
      const focusStopsInResult = finalStops.filter((stop) =>
        focusStopKeys.has(stop.placeId || normalizeName(stop.name)),
      ).length;
      const suppressUnresolvedCategory = shouldSuppressUnresolvedCategory({
        categoryFocus: data.categoryFocus,
        requestedStopCategory: data.requestedStopCategory,
        focusStopsInResult,
      });
      if (suppressUnresolvedCategory) {
        console.info("focus enrichment search returned nothing; route kept", {
          focus: data.categoryFocus,
          focusStopsInResult,
        });
      }

      return {
        stops: finalStops,
        error: null,
        start: startConstraint
          ? { name: startConstraint.name, lat: startConstraint.lat, lng: startConstraint.lng }
          : null,
        missingRequiredPlaces,
        unresolvedRequestedStopCategory:
          !suppressUnresolvedCategory &&
          (data.requestedStopCategory || data.requestedStopQuery) &&
          !requestedCategoryStop
            ? // Identificador de categoria, nunca a query interna: o ecrã
              // compõe a frase humana a partir daqui.
              (data.requestedStopCategory ?? "custom")
            : null,
        // Cozinha/categoria explícita sem locais verificados suficientes na
        // localidade: o ecrã pergunta se pode alargar, nunca inventa paragens.
        cuisineScarce:
          finalValidationScarce ??
          (cuisineIntent &&
          focusCategoryStops.length === 0 &&
          requestedCategoryStops.length === 0
            ? { cuisine: cuisineIntent.requested, area: data.city, found: 0 }
            : null),

        unresolvedStartPlace: requestedStartPlace && !startConstraint ? requestedStartPlace : null,
        requiredPlacesExceedTime,
        visitMinutes: planVisitMinutes,
        freeBreakMinutes: planFreeBreakMinutes,
        // Roteiro válido mas abaixo do alvo de ritmo (só modo genérico):
        // apresentado com nota de tempo livre, nunca bloqueado.
        itineraryPaceNotice,
        interestFillNotice,
        calmSmallPlaceNotice,
        // Reserva para "Trocar sítio": candidatos já validados nesta geração
        // que não entraram na rota. Trocar no ecrã custa 0 (sem Google/IA).
        spareStops: (() => {
          const used = new Set(finalStops.map((s) => (s.placeId ?? s.name).toLowerCase()));
          return dedupeStops(candidateStops)
            .filter(
              (s) =>
                typeof s.lat === "number" &&
                typeof s.lng === "number" &&
                !used.has((s.placeId ?? s.name).toLowerCase()) &&
                !isStopExcluded(s) &&
                (wantsFood || !isFoodLikeCategory(`${s.category} ${s.name}`)),
            )
            .slice(0, 30)
            .map((s) => ({
              name: s.name,
              category: s.category,
              blurb: s.blurb,
              priceLocal: s.priceLocal,
              rating: s.rating,
              placeId: s.placeId,
              photoUrl: s.photoUrl,
              lat: s.lat,
              lng: s.lng,
            })) as AIStop[];
        })(),
        transportExecution: {
          betweenZones: data.mode,
          withinZone: data.localMode ?? data.mode,
        },
      };

    } catch (e) {
      console.error("generateStops failed", e);
      // Regista internamente um código de falha seguro (sem stack trace nem dados
      // sensíveis) para diagnóstico, mantendo a mensagem genérica ao utilizador.
      const safeCode = safeFailureCode(e);
      trace.phase("generation_escape", { origin: "server_exception", code: safeCode });
      return releaseFailedGeneration(safeCode);
    }
}

/* -------- Restaurant recommendation for lunch/dinner break -------- */

const RestaurantInput = z.object({
  city: z.string().min(1).max(120),
  country: z.string().min(1).max(120),
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
  radiusMeters: z.number().min(500).max(25000).optional(),
  language: z.string().min(2).max(8),
  currency: z.string().min(3).max(4),
  mealType: z.enum(["lunch", "dinner", "breakfast", "brunch"]),
  /** Janela do bloco de pequeno-almoço/brunch ("HH:MM"), quando aplicável. */
  breakfastWindow: z.object({ start: z.string(), end: z.string() }).optional(),
  /**
   * Janela horária do almoço/jantar ("HH:MM") e dia da semana (0=domingo):
   * só com estes dados é possível afirmar "aberto" com horários confirmados.
   */
  mealWindow: z.object({ start: z.string(), end: z.string() }).optional(),
  mealWeekday: z.number().int().min(0).max(6).optional(),
  nearStop: z.string().max(160).optional(),
  nextLat: z.number().min(-90).max(90).optional(),
  nextLng: z.number().min(-180).max(180).optional(),
  maxBudget: z.number().min(1).max(10000).optional(),
  anonId: z.string().max(80).default(""),
});

export type AIRestaurant = {
  name: string;
  cuisine: string;
  blurb: string;
  priceLocal: number;
  website?: string;
  phone?: string;
  placeId?: string;
  googleMapsUri?: string;
  lat?: number;
  lng?: number;
  /**
   * "Aberto" só com horários confirmados pelo Google Places para a janela
   * real da refeição. `false`/ausente → o UI NUNCA afirma "aberto".
   */
  openConfirmed?: boolean;
};

const googlePriceToAverage: Record<string, number> = {
  PRICE_LEVEL_FREE: 0,
  PRICE_LEVEL_INEXPENSIVE: 12,
  PRICE_LEVEL_MODERATE: 24,
  PRICE_LEVEL_EXPENSIVE: 45,
  PRICE_LEVEL_VERY_EXPENSIVE: 75,
};

type MealType = "lunch" | "dinner" | "breakfast" | "brunch";

const isBreakfastMeal = (mealType: MealType) => mealType === "breakfast" || mealType === "brunch";

const MEAL_WORD_PT: Record<MealType, string> = {
  lunch: "almoçar",
  dinner: "jantar",
  breakfast: "tomar o pequeno-almoço",
  brunch: "brunch",
};

const restaurantBlurb = (name: string, city: string, mealType: MealType, language: string) => {
  if (language.toLowerCase().startsWith("pt")) {
    return `${name} é uma opção local em ${city} para ${MEAL_WORD_PT[mealType]} sem sair da zona do roteiro.`;
  }
  return `${name} is a local option in ${city} for ${mealType} without leaving the route area.`;
};

type GoogleRestaurantPlace = {
  id?: string;
  displayName?: { text?: string };
  formattedAddress?: string;
  location?: { latitude?: number; longitude?: number };
  rating?: number;
  userRatingCount?: number;
  priceLevel?: string;
  websiteUri?: string;
  internationalPhoneNumber?: string;
  nationalPhoneNumber?: string;
  googleMapsUri?: string;
  primaryType?: string;
  types?: string[];
  editorialSummary?: { text?: string };
  regularOpeningHours?: {
    periods?: Array<{
      open?: { day?: number; hour?: number; minute?: number };
      close?: { day?: number; hour?: number; minute?: number };
    }>;
  };
};

/** Jantar exclui cafés/pastelarias: primaryType real, nunca nome/texto. */
const DINNER_EXCLUDED_PRIMARY_TYPES: ReadonlySet<string> = new Set([
  "cafe",
  "coffee_shop",
  "bakery",
]);

/**
 * Intervalos de abertura do Places para um dia da semana (0=domingo).
 * - `null`  → o Places NÃO devolveu horários: nunca se afirma "aberto".
 * - `[]`    → há horários, mas nenhum para este dia: fechado.
 */
function openIntervalsForDay(
  place: GoogleRestaurantPlace,
  weekday?: number,
): OpenInterval[] | null {
  const periods = place.regularOpeningHours?.periods;
  if (!periods?.length) return null;
  const hasDayInfo = periods.some((p) => typeof p.open?.day === "number");
  const intervals: OpenInterval[] = [];
  for (const period of periods) {
    const open = period.open;
    if (!open || typeof open.hour !== "number") continue;
    if (hasDayInfo && typeof weekday === "number" && open.day !== weekday) continue;
    const openMin = open.hour * 60 + (open.minute ?? 0);
    const close = period.close;
    const closeMin =
      close && typeof close.hour === "number"
        ? close.hour * 60 + (close.minute ?? 0)
        : 24 * 60;
    intervals.push({ openMin, closeMin: closeMin <= openMin ? 24 * 60 : closeMin });
  }
  return hasDayInfo && typeof weekday === "number" ? intervals : intervals.length ? intervals : null;
}

/** A janela da refeição está confirmada pelos horários reais do Places? */
function mealWindowConfirmed(
  place: GoogleRestaurantPlace,
  data: { mealWindow?: { start: string; end: string }; mealWeekday?: number },
): boolean {
  if (!data.mealWindow) return false;
  const intervals = openIntervalsForDay(place, data.mealWeekday);
  if (!intervals || intervals.length === 0) return false;
  const start = hhmm(data.mealWindow.start, -1);
  const end = hhmm(data.mealWindow.end, -1);
  if (start < 0 || end < 0 || end <= start) return false;
  return intervals.some((iv) => iv.openMin < end && iv.closeMin > start);
}

/**
 * "Aberto" comprovado: com janela conhecida (pequeno-almoço/brunch ou
 * mealWindow do almoço/jantar), exige sobreposição real; sem janela conhecida,
 * exige apenas horários utilizáveis devolvidos pelo Places — nunca se afirma
 * "aberto" sem horários.
 */
function openConfirmedFor(
  place: GoogleRestaurantPlace,
  data: z.infer<typeof RestaurantInput>,
): boolean {
  const window = isBreakfastMeal(data.mealType)
    ? breakfastWindowOf(data)
    : (data.mealWindow ?? null);
  if (!window) {
    const intervals = openIntervalsForDay(place, data.mealWeekday);
    return intervals != null && intervals.length > 0;
  }
  return mealWindowConfirmed(place, { mealWindow: window, mealWeekday: data.mealWeekday });
}

function toBreakfastCandidate(place: GoogleRestaurantPlace): BreakfastCandidate {
  return {
    name: place.displayName?.text ?? null,
    types: place.types ?? [],
    summary: place.editorialSummary?.text ?? null,
    openIntervals: openIntervalsForDay(place),
  };
}

function breakfastWindowOf(data: { mealType: string; breakfastWindow?: BreakfastWindow }): BreakfastWindow {
  return (
    data.breakfastWindow ??
    (data.mealType === "brunch" ? DEFAULT_BRUNCH_WINDOW : DEFAULT_BREAKFAST_WINDOW)
  );
}

function placeToRestaurant(
  place: GoogleRestaurantPlace,
  data: z.infer<typeof RestaurantInput>,
  relaxBudget = false,
): AIRestaurant | null {
  if (place.location?.latitude == null || place.location.longitude == null) return null;
  const primary = (place.primaryType ?? "").toLowerCase();
  if (isBreakfastMeal(data.mealType)) {
    if (!matchesBreakfastCategory(toBreakfastCandidate(place), breakfastWindowOf(data))) return null;
  } else {
    if (!isFoodPlaceType(place.types || [])) return null;
    // Jantar: exclui cafés/pastelarias pelo primaryType real.
    if (data.mealType === "dinner" && DINNER_EXCLUDED_PRIMARY_TYPES.has(primary)) return null;
  }
  const name = place.displayName?.text?.trim();
  if (!name) return null;
  const avg = place.priceLevel ? googlePriceToAverage[place.priceLevel] : undefined;
  if (!relaxBudget && data.maxBudget && avg != null && avg > data.maxBudget) return null;

  return {
    name,
    cuisine: isBreakfastMeal(data.mealType)
      ? (data.language.toLowerCase().startsWith("pt") ? "Pequeno-almoço" : "Breakfast")
      : (data.language.toLowerCase().startsWith("pt") ? "Restaurante" : "Restaurant"),
    blurb: restaurantBlurb(name, data.city, data.mealType, data.language),
    priceLocal: data.maxBudget ? Math.min(avg || data.maxBudget, data.maxBudget) : (avg || 20),
    website: place.websiteUri,
    phone: place.internationalPhoneNumber || place.nationalPhoneNumber,
    placeId: place.id,
    googleMapsUri: place.googleMapsUri,
    openConfirmed: openConfirmedFor(place, data),
    lat: place.location.latitude,
    lng: place.location.longitude,
  };
}

/** Diagnóstico seguro (sem chaves nem dados pessoais) da procura de refeição. */
export type MealSearchDiag = {
  center: { lat: number; lng: number };
  passes: Array<{
    radius: number;
    raw: number;
    rejectedType: number;
    rejectedBudget: number;
    rejectedDistance: number;
    apiStatus: number | "exception" | "ok";
  }>;
  chosen?: { placeId?: string; detourMeters: number; radius: number };
  outcome: "chosen" | "empty" | "api_error" | "no_key";
};

async function findRestaurantNearRoute(
  data: z.infer<typeof RestaurantInput>,
): Promise<{ restaurant: AIRestaurant | null; diag: MealSearchDiag }> {
  const key = placesServerKey();
  const center0 = { lat: data.lat as number, lng: data.lng as number };
  const emptyDiag = (outcome: MealSearchDiag["outcome"]): MealSearchDiag => ({
    center: center0,
    passes: [],
    outcome,
  });
  if (!key || typeof data.lat !== "number" || typeof data.lng !== "number") {
    return { restaurant: null, diag: emptyDiag("no_key") };
  }
  const nextPoint =
    typeof data.nextLat === "number" && typeof data.nextLng === "number"
      ? { lat: data.nextLat, lng: data.nextLng }
      : null;
  // Ponto da refeição: no corredor entre a paragem anterior e a seguinte.
  const center = nextPoint
    ? { lat: (center0.lat + nextPoint.lat) / 2, lng: (center0.lng + nextPoint.lng) / 2 }
    : center0;
  const base = Math.min(2500, Math.max(700, data.radiusMeters ?? 1400));
  // Cascata automática na mesma geração — sem exigir ação do utilizador.
  const radii = [base, Math.round(base * 1.8), Math.min(6000, Math.round(base * 3))].filter(
    (r, i, a) => a.indexOf(r) === i,
  );
  const languageCode = data.language.toLowerCase().startsWith("pt") ? "pt-PT" : data.language;
  const diag: MealSearchDiag = { center, passes: [], outcome: "empty" };
  let sawApiError = false;

  for (let pass = 0; pass < radii.length; pass++) {
    const radius = radii[pass];
    // Último nível: relaxa o orçamento em vez de devolver "sem restaurantes".
    const relaxBudget = pass === radii.length - 1;
    const entry = {
      radius,
      raw: 0,
      rejectedType: 0,
      rejectedBudget: 0,
      rejectedDistance: 0,
      apiStatus: "ok" as number | "exception" | "ok",
    };
    try {
      const response = await cachedPlacesFetch(`${PLACES_BASE}/places:searchNearby`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": key,
          // `editorialSummary` é escalão Atmosphere (o mais caro) e só é usado
          // na elegibilidade de pequeno-almoço/brunch: nas restantes refeições
          // não se pede.
          "X-Goog-FieldMask":
            "places.id,places.displayName,places.formattedAddress,places.location,places.rating,places.userRatingCount,places.priceLevel,places.websiteUri,places.internationalPhoneNumber,places.nationalPhoneNumber,places.googleMapsUri,places.types,places.primaryType,places.regularOpeningHours" +
            (isBreakfastMeal(data.mealType) ? ",places.editorialSummary" : ""),
        },
        body: JSON.stringify({
          includedTypes: isBreakfastMeal(data.mealType)
            ? ["cafe", "bakery", "coffee_shop", "breakfast_restaurant", "brunch_restaurant", "restaurant"]
            : ["restaurant"],
          maxResultCount: 20,
          rankPreference: "POPULARITY",
          locationRestriction: {
            circle: { center: { latitude: center.lat, longitude: center.lng }, radius },
          },
          languageCode,
        }),
      }, { kind: "dining_nearby", ttlMs: PLACES_TTL.dining, origin: "route_meal_search", sku: "places_nearby_search" });
      if (!response.ok) {
        entry.apiStatus = response.status;
        sawApiError = true;
        console.error(
          "Restaurant Places nearby failed",
          response.status,
          await response.text().catch(() => ""),
        );
        diag.passes.push(entry);
        continue;
      }
      const json = (await response.json()) as { places?: GoogleRestaurantPlace[] };
      const places = (json.places ?? []).filter(
        (p) => p.location?.latitude != null && p.location?.longitude != null,
      );
      entry.raw = places.length;
      const directToNext = nextPoint ? haversineMeters(center0, nextPoint) : 0;
      const scored = places
        .map((p) => {
          const loc = { lat: p.location!.latitude!, lng: p.location!.longitude! };
          const dist = haversineMeters(center, loc);
          const fromPrev = haversineMeters(center0, loc);
          const avg = p.priceLevel ? googlePriceToAverage[p.priceLevel] : undefined;
          const reviews = Math.min(1500, p.userRatingCount ?? 0);
          const nextDist = nextPoint ? haversineMeters(nextPoint, loc) : fromPrev;
          const detour = nextPoint ? Math.max(0, fromPrev + nextDist - directToNext) : fromPrev;
          const score =
            (p.rating ?? 3.5) * 100 + Math.log10(reviews + 1) * 20 - detour / 5 - dist / 25;
          return { place: p, dist, detour, avg, score };
        })
        .filter(({ place, dist, avg }) => {
          if (dist > radius + 150) {
            entry.rejectedDistance++;
            return false;
          }
          if (isBreakfastMeal(data.mealType)) {
            // Decisão final e única: nunca rejeitar só pelo tipo `restaurant`.
            if (!isBreakfastEligible(toBreakfastCandidate(place), breakfastWindowOf(data))) {
              entry.rejectedType++;
              return false;
            }
          } else {
            if (!isFoodPlaceType(place.types || [])) {
              entry.rejectedType++;
              return false;
            }
            // Jantar: cafés/pastelarias ficam de fora pelo primaryType real.
            if (
              data.mealType === "dinner" &&
              DINNER_EXCLUDED_PRIMARY_TYPES.has((place.primaryType ?? "").toLowerCase())
            ) {
              entry.rejectedType++;
              return false;
            }
          }
          if (!relaxBudget && data.maxBudget && avg != null && avg > data.maxBudget) {
            entry.rejectedBudget++;
            return false;
          }
          return true;
        })
        .sort((a, b) => b.score - a.score || a.dist - b.dist);
      diag.passes.push(entry);
      for (const cand of scored) {
        const restaurant = placeToRestaurant(cand.place, data, relaxBudget);
        if (restaurant) {
          diag.outcome = "chosen";
          diag.chosen = {
            placeId: restaurant.placeId,
            detourMeters: Math.round(cand.detour),
            radius,
          };
          return { restaurant, diag };
        }
      }
    } catch (e) {
      entry.apiStatus = "exception";
      sawApiError = true;
      diag.passes.push(entry);
      console.error("findRestaurantNearRoute failed", e);
    }
  }
  diag.outcome = sawApiError ? "api_error" : "empty";
  return { restaurant: null, diag };
}


async function validateRestaurantNearRoute(
  restaurant: AIRestaurant | null,
  data: z.infer<typeof RestaurantInput>,
): Promise<AIRestaurant | null> {
  if (!restaurant) return null;
  const key = placesServerKey();
  if (!key || typeof data.lat !== "number" || typeof data.lng !== "number") return restaurant;

  const center = { lat: data.lat, lng: data.lng };
  const exactRadius = data.radiusMeters ?? (data.nearStop ? 2500 : 12000);
  const fallbackRadius = Math.min(5000, Math.max(1500, exactRadius));
  const languageCode = data.language.toLowerCase().startsWith("pt") ? "pt-PT" : data.language;
  const budgetCap = data.maxBudget;

  const toOutput = (place: GoogleRestaurantPlace, source: "exact" | "fallback"): AIRestaurant | null => {
    if (place.location?.latitude == null || place.location.longitude == null) return null;
    const dist = haversineMeters(center, { lat: place.location.latitude, lng: place.location.longitude });
    const maxDist = source === "exact" ? exactRadius : fallbackRadius;
    if (dist > maxDist) return null;
    if (isBreakfastMeal(data.mealType)) {
      if (!matchesBreakfastCategory(toBreakfastCandidate(place), breakfastWindowOf(data))) return null;
    } else {
      if (!isFoodPlaceType(place.types || [])) return null;
      // Jantar: cafés/pastelarias ficam de fora pelo primaryType real.
      if (data.mealType === "dinner" && DINNER_EXCLUDED_PRIMARY_TYPES.has((place.primaryType ?? "").toLowerCase())) return null;
    }
    const avg = place.priceLevel ? googlePriceToAverage[place.priceLevel] : undefined;
    if (budgetCap && avg != null && avg > budgetCap) return null;
    const name = place.displayName?.text || restaurant.name;
    return {
      ...restaurant,
      name,
      cuisine: restaurant.cuisine || (data.language.toLowerCase().startsWith("pt") ? "Restaurante" : "Restaurant"),
      blurb: source === "exact" ? restaurant.blurb : restaurantBlurb(name, data.city, data.mealType, data.language),
      priceLocal: budgetCap ? Math.min(restaurant.priceLocal || avg || budgetCap, budgetCap) : (restaurant.priceLocal || avg || 20),
      website: place.websiteUri || restaurant.website,
      phone: place.internationalPhoneNumber || place.nationalPhoneNumber || restaurant.phone,
      placeId: place.id,
      googleMapsUri: place.googleMapsUri,
      openConfirmed: openConfirmedFor(place, data),
      lat: place.location.latitude,
      lng: place.location.longitude,
    };
  };

  const readPlaces = async (response: {
    ok: boolean;
    status: number;
    json: () => Promise<unknown>;
    text: () => Promise<string>;
  }): Promise<GoogleRestaurantPlace[]> => {
    if (!response.ok) {
      console.error("Restaurant Places validation failed", response.status, await response.text().catch(() => ""));
      return [];
    }
    const json = (await response.json()) as { places?: GoogleRestaurantPlace[] };
    return json.places ?? [];
  };

  try {
    const exactResponse = await cachedPlacesFetch(`${PLACES_BASE}/places:searchText`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": key,
        "X-Goog-FieldMask": "places.id,places.displayName,places.formattedAddress,places.location,places.rating,places.priceLevel,places.websiteUri,places.internationalPhoneNumber,places.nationalPhoneNumber,places.googleMapsUri,places.types,places.primaryType,places.regularOpeningHours",
      },
      body: JSON.stringify({
        textQuery: `${restaurant.name} ${isBreakfastMeal(data.mealType) ? "cafe bakery breakfast" : "restaurant"} ${data.city} ${data.country}`,
        ...(isBreakfastMeal(data.mealType) ? {} : { includedType: "restaurant" }),
        maxResultCount: 5,
        locationRestriction: { rectangle: locationRectangle(center.lat, center.lng, exactRadius) },
        languageCode,
      }),
    }, { kind: "dining_exact", ttlMs: PLACES_TTL.dining, origin: "route_meal_search", sku: "places_text_search" });
    const exactPlaces = await readPlaces(exactResponse);
    const exact = exactPlaces
      .filter((p) => p.location?.latitude != null && p.location?.longitude != null)
      .map((p) => ({
        place: p,
        dist: haversineMeters(center, { lat: p.location!.latitude!, lng: p.location!.longitude! }),
      }))
      .filter(({ place, dist }) => dist <= exactRadius && placeNamesMatch(restaurant.name, place.displayName?.text || ""))
      .sort((a, b) => a.dist - b.dist)[0]?.place;
    const exactOutput = exact ? toOutput(exact, "exact") : null;
    if (exactOutput) return exactOutput;

    console.warn(`Rejected restaurant outside route area for ${data.city}: ${restaurant.name}`);

    const fallbackResponse = await cachedPlacesFetch(`${PLACES_BASE}/places:searchNearby`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": key,
        "X-Goog-FieldMask": "places.id,places.displayName,places.formattedAddress,places.location,places.rating,places.priceLevel,places.websiteUri,places.internationalPhoneNumber,places.nationalPhoneNumber,places.googleMapsUri,places.types,places.primaryType,places.regularOpeningHours",
      },
      body: JSON.stringify({
        includedTypes: ["restaurant"],
        maxResultCount: 8,
        rankPreference: "POPULARITY",
        locationRestriction: {
          circle: {
            center: { latitude: center.lat, longitude: center.lng },
            radius: fallbackRadius,
          },
        },
        languageCode,
      }),
    }, { kind: "dining_fallback", ttlMs: PLACES_TTL.dining, origin: "route_meal_search", sku: "places_nearby_search" });
    const fallbackPlaces = await readPlaces(fallbackResponse);
    const fallback = fallbackPlaces
      .filter((p) => p.location?.latitude != null && p.location?.longitude != null)
      .map((p) => ({
        place: p,
        dist: haversineMeters(center, { lat: p.location!.latitude!, lng: p.location!.longitude! }),
      }))
      .filter(({ place, dist }) => {
        const avg = place.priceLevel ? googlePriceToAverage[place.priceLevel] : undefined;
        return dist <= fallbackRadius && (!budgetCap || avg == null || avg <= budgetCap);
      })
      .sort((a, b) => (b.place.rating ?? 0) - (a.place.rating ?? 0) || a.dist - b.dist)[0]?.place;
    return fallback ? toOutput(fallback, "fallback") : null;
  } catch (e) {
    console.error("validateRestaurantNearRoute failed", e);
    return null;
  }
}

export const recommendRestaurant = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => RestaurantInput.parse(d))
  .handler(async ({ data }) => {
    // 1) identidade + rate limit ANTES de qualquer chamada paga à Google.
    const { checkGoogleApiRateLimit } = await import("./quota.server");
    if (!(await checkGoogleApiRateLimit("placeRecommendation")).ok) {
      return { restaurant: null as AIRestaurant | null, error: "rate_limited" };
    }
    // 2) Prefer Google Places nearby: real coordinates, no AI credits, and it
    // guarantees the restaurant is close to the meal break route segment.
    const { restaurant: nearby, diag } = await findRestaurantNearRoute(data);
    if (nearby) return { restaurant: nearby, error: null, diag };


    const { consumeQuotaInternal } = await import("./quota.server");
    const quota = await consumeQuotaInternal(data.anonId);
    if (!quota.ok) return { restaurant: null as AIRestaurant | null, error: "quota_exceeded" };

    const apiKey = process.env.OPENAI_API_KEY;
    if (!apiKey) return { restaurant: null as AIRestaurant | null, error: "AI not configured" };

    const budgetLine = data.maxBudget
      ? ` The average price per person MUST NOT exceed ${data.maxBudget} ${data.currency}. Choose a place that genuinely fits this budget (street food, tasca, casual bistro, etc.) — never recommend something more expensive.`
      : "";
    const geoLine = typeof data.lat === "number" && typeof data.lng === "number"
      ? ` It must be physically close to these route coordinates: ${data.lat.toFixed(5)}, ${data.lng.toFixed(5)} (same city/area only). Do not pick places with a similar name in another town.`
      : "";
    const sys = `You are a local food guide. Recommend ONE real, well-known ${isBreakfastMeal(data.mealType) ? "café, bakery or brunch venue that genuinely serves " + data.mealType : "restaurant"} in the given city, suitable for ${data.mealType}.${budgetLine}${geoLine} If you are highly confident about its official website URL and/or phone number (international format), include them; otherwise OMIT those fields rather than guessing. Reply ONLY through the tool call.`;
    const user = `City: ${data.city}, ${data.country}. Meal: ${data.mealType}.${
      data.nearStop ? ` Should be conveniently near: ${data.nearStop}.` : ""
    }${data.maxBudget ? ` Max budget per person: ${data.maxBudget} ${data.currency}.` : ""} Reply in language code "${data.language}". Use currency ${data.currency} for average price per person.`;

    try {
      const res = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "gpt-4o-mini",
          messages: [
            { role: "system", content: sys },
            { role: "user", content: user },
          ],
          tools: [
            {
              type: "function",
              function: {
                name: "return_restaurant",
                parameters: {
                  type: "object",
                  properties: {
                    name: { type: "string" },
                    cuisine: { type: "string" },
                    blurb: { type: "string", description: "1 short sentence in the requested language" },
                    priceLocal: { type: "number" },
                    website: { type: "string", description: "Official website URL — include ONLY if highly confident it is real" },
                    phone: { type: "string", description: "Phone number in international format — include ONLY if highly confident it is real" },
                  },
                  required: ["name", "cuisine", "blurb", "priceLocal"],
                  additionalProperties: false,
                },
              },
            },
          ],
          tool_choice: { type: "function", function: { name: "return_restaurant" } },
        }),
      });
      if (!res.ok) {
        console.error("OpenAI restaurant error", res.status, await res.text().catch(() => ""));
        return { restaurant: null, error: "ai_error" };
      }
      const json = await res.json();
      const args = json.choices?.[0]?.message?.tool_calls?.[0]?.function?.arguments;
      const parsed = args ? (JSON.parse(args) as AIRestaurant) : null;
      const validated = await validateRestaurantNearRoute(parsed, data);
      return { restaurant: validated, error: validated ? null : "outside_route_area" };
    } catch (e) {
      console.error("recommendRestaurant failed", e);
      return { restaurant: null, error: "exception" };
    }
  });

/* -------- Premium: deep history / curiosities / figures / best time -------- */

const DetailsInput = z.object({
  name: z.string().min(1).max(200),
  category: z.string().max(80).default(""),
  city: z.string().min(1).max(120),
  country: z.string().min(1).max(120),
  language: z.string().min(2).max(8),
  placeId: z.string().max(200).optional(),
});

export type AIStopDetails = {
  history: string;
  funFacts: string[];
  historicalFigures: { name: string; note: string }[];
  bestTimeToVisit: string;
};

export const generateStopDetails = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => DetailsInput.parse(d))
  .handler(async ({ data, context }) => {
    // Server-side gate: subscribers OR credit-pack holders with balance get details
    // (the credits page promises "premium details included").
    const { supabase } = context;
    const [live, sandbox] = await Promise.all([
      supabase.rpc("has_active_subscription", { user_uuid: context.userId, check_env: "live" }),
      supabase.rpc("has_active_subscription", { user_uuid: context.userId, check_env: "sandbox" }),
    ]);
    let allowed = live.data === true || sandbox.data === true;
    if (!allowed) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: credits } = await supabaseAdmin
        .from("itinerary_credits")
        .select("balance")
        .eq("user_id", context.userId)
        .maybeSingle();
      allowed = ((credits as { balance: number } | null)?.balance ?? 0) > 0;
    }
    if (!allowed) {
      return { details: null as AIStopDetails | null, error: "premium_required" };
    }
    // Cache lookup — history doesn't change, so we cache forever per place+language.
    const lang = data.language.toLowerCase();
    const placeKey = data.placeId
      ? data.placeId
      : `${data.city.toLowerCase().trim()}|${data.name.toLowerCase().trim()}`;
    const cacheKey = `details|${lang}|${placeKey}`;
    let supabaseAdmin: any = null;
    try {
      ({ supabaseAdmin } = await import("@/integrations/supabase/client.server"));
      const { data: cached } = await supabaseAdmin
        .from("place_enrichment_cache")
        .select("payload,hits,expires_at")
        .eq("cache_key", cacheKey)
        .maybeSingle();
      const exp = (cached as any)?.expires_at as string | null | undefined;
      const isExpired = exp ? new Date(exp).getTime() < Date.now() : false;
      if (cached?.payload && !isExpired) {
        void supabaseAdmin
          .from("place_enrichment_cache")
          .update({ last_used_at: new Date().toISOString(), hits: (cached.hits ?? 0) + 1 })
          .eq("cache_key", cacheKey);
        return { details: cached.payload as AIStopDetails, error: null };
      }

    } catch (e) {
      console.warn("details cache read failed", e);
    }

    const apiKey = process.env.OPENAI_API_KEY;
    if (!apiKey) return { details: null as AIStopDetails | null, error: "AI not configured" };

    const sys = `You are an expert local historian and travel writer. Provide rich, accurate, engaging information about a specific place. Reply ONLY through the tool call, in language code "${data.language}".`;
    const user = `Place: "${data.name}" (${data.category || "tourist site"}) in ${data.city}, ${data.country}.
Return:
- history: 2-3 short paragraphs of historical/cultural background (mention dates, events).
- funFacts: 3-5 short, surprising curiosities.
- historicalFigures: 1-3 people linked to this place (name + 1-sentence note). Empty array if none.
- bestTimeToVisit: 1-2 sentences with the best hours, season and practical tip.`;

    try {
      const res = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "gpt-4o-mini",
          messages: [
            { role: "system", content: sys },
            { role: "user", content: user },
          ],
          tools: [
            {
              type: "function",
              function: {
                name: "return_details",
                parameters: {
                  type: "object",
                  properties: {
                    history: { type: "string" },
                    funFacts: { type: "array", items: { type: "string" }, minItems: 3, maxItems: 5 },
                    historicalFigures: {
                      type: "array",
                      maxItems: 3,
                      items: {
                        type: "object",
                        properties: {
                          name: { type: "string" },
                          note: { type: "string" },
                        },
                        required: ["name", "note"],
                        additionalProperties: false,
                      },
                    },
                    bestTimeToVisit: { type: "string" },
                  },
                  required: ["history", "funFacts", "historicalFigures", "bestTimeToVisit"],
                  additionalProperties: false,
                },
              },
            },
          ],
          tool_choice: { type: "function", function: { name: "return_details" } },
        }),
      });
      if (!res.ok) {
        console.error("OpenAI details error", res.status, await res.text().catch(() => ""));
        return { details: null, error: "ai_error" };
      }
      const json = await res.json();
      const args = json.choices?.[0]?.message?.tool_calls?.[0]?.function?.arguments;
      const parsed = args ? (JSON.parse(args) as AIStopDetails) : null;
      if (parsed && supabaseAdmin) {
        // 180 days — historical/cultural content rarely changes.
        const expiresAt = new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString();
        void supabaseAdmin.from("place_enrichment_cache").upsert({
          cache_key: cacheKey,
          kind: "details",
          language: lang,
          place_id: data.placeId ?? null,
          payload: parsed,
          expires_at: expiresAt,
        }, { onConflict: "cache_key" });
      }
      return { details: parsed, error: null };

    } catch (e) {
      console.error("generateStopDetails failed", e);
      return { details: null, error: "exception" };
    }
  });


/* -------- Suggest extra stops (avoid duplicates with current itinerary) -------- */

const ExtraInput = z.object({
  city: z.string().min(1).max(120),
  country: z.string().min(1).max(120),
  lat: z.number(),
  lng: z.number(),
  language: z.string().min(2).max(8),
  currency: z.string().min(3).max(4),
  interests: z.array(z.string().min(1).max(40)).max(10).default([]),
  excludeNames: z.array(z.string().min(1).max(160)).max(50).default([]),
  count: z.number().min(1).max(10).default(6),
  anonId: z.string().max(80).default(""),
  /** Notas/prompt original — usado só para detetar exclusões duras. */
  notes: z.string().max(1200).default(""),
  /**
   * Garantia A: sem ação explícita de comida nesta sessão, o motor NUNCA
   * sugere paragens alimentares ("Queres incluir gastronomia?" não existe
   * mais — quem decide é a pessoa, com texto/chips/refeições).
   */
  foodIntent: z.boolean().default(false),
  genericVisitMode: z.boolean().default(false),
});

export const suggestExtraStops = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => ExtraInput.parse(d))
  .handler(async ({ data }) => {
    const { consumeQuotaInternal } = await import("./quota.server");
    const quota = await consumeQuotaInternal(data.anonId);
    if (!quota.ok) return { stops: [] as AIStop[], error: "quota_exceeded", quota };
    const apiKey = process.env.OPENAI_API_KEY;
    if (!apiKey) return { stops: [] as AIStop[], error: "AI not configured" };

    const interestsLine = data.interests.length ? data.interests.join(", ") : "any (balanced mix)";
    const excludeLine = data.excludeNames.length
      ? data.excludeNames.map((n) => `- ${n}`).join("\n")
      : "(none)";
    const replyLanguage = languageInstruction(data.language);
    const sys = `You are a local tourism concierge. Suggest exactly ${data.count} ADDITIONAL real tourist stops inside ${data.city}, ${data.country}. The lat/lng below are the city anchor used to validate that places are in the correct city.
Rules:
1. Every stop MUST be a real place physically INSIDE the administrative boundary of ${data.city}. Never suggest places in neighbouring towns/villages/municipalities, wider regions, countries, or continents — "near ${data.city}" is not acceptable.
2. DO NOT repeat or paraphrase any of the places already in the itinerary listed below — pick fresh, different attractions.
3. Use the LOCAL/OFFICIAL name (findable on Google Maps).
4. Vary categories (mix landmarks, museums, parks, viewpoints, local gems) and respect the traveler's interests.
5. ${
  data.foodIntent
    ? "The traveler explicitly requested food/restaurant visits, so food stops are allowed."
    : "ABSOLUTELY DO NOT suggest restaurants, cafés, bars, bakeries, pastry shops or any place whose main purpose is eating/drinking. Food was NOT requested."
}
5. All category and blurb text MUST be written in ${replyLanguage}.
Reply ONLY through the tool call.`;
    const user = `City: ${data.city}, ${data.country}.
City anchor for validation: lat ${data.lat.toFixed(5)}, lng ${data.lng.toFixed(5)}.
Interests: ${interestsLine}.
Already in the itinerary (DO NOT REPEAT):
${excludeLine}
Reply in ${replyLanguage}. Use currency ${data.currency} for priceLocal (0 if free). 1-sentence vivid blurb with a fact.`;

    try {
      const res = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "gpt-4o-mini",
          messages: [
            { role: "system", content: sys },
            { role: "user", content: user },
          ],
          tools: [
            {
              type: "function",
              function: {
                name: "return_stops",
                description: `Return ${data.count} additional tourist stops`,
                parameters: {
                  type: "object",
                  properties: {
                    stops: {
                      type: "array",
                      items: {
                        type: "object",
                        properties: {
                          name: { type: "string" },
                          category: { type: "string" },
                          blurb: { type: "string" },
                          priceLocal: { type: "number" },
                          rating: { type: "number", minimum: 4, maximum: 5 },
                        },
                        required: ["name", "category", "blurb", "priceLocal", "rating"],
                        additionalProperties: false,
                      },
                    },
                  },
                  required: ["stops"],
                  additionalProperties: false,
                },
              },
            },
          ],
          tool_choice: { type: "function", function: { name: "return_stops" } },
        }),
      });
      if (!res.ok) {
        console.error("OpenAI extra error", res.status, await res.text().catch(() => ""));
        return { stops: [] as AIStop[], error: "ai_error" };
      }
      const json = await res.json();
      const args = json.choices?.[0]?.message?.tool_calls?.[0]?.function?.arguments;
      const parsed = args ? JSON.parse(args) : null;
      const extraNegativePrefs = parseNegativePreferences(data.notes);
      const isExtraStopExcluded = (stop: { name: string; category?: string }) => {
        const guess = guessCategoryFromText(stop.category) ?? guessCategoryFromText(stop.name);
        return !!guess && isCategoryExcluded(extraNegativePrefs, guess);
      };
      const rawStops = ((parsed?.stops ?? []) as AIStop[])
        .filter((s) => !data.excludeNames.some((n) => n.toLowerCase() === s.name.toLowerCase()))
        .filter((s) => !isExtraStopExcluded(s))
        // Barreira final de comida nas sugestões extra (garantia B).
        .filter((s) => data.foodIntent || !isFoodLikeCategory(`${s.category} ${s.name}`))
        .slice(0, data.count);
      const extraScope = { radiusMeters: 15000, requireAdministrativeLocalityMatch: true };
      const stops = await validateStopsInsideCity(rawStops, {
        city: data.city,
        country: data.country,
        lat: data.lat,
        lng: data.lng,
        language: data.language,
        allowFoodStops: data.foodIntent,
        ...extraScope,
      });
      const filled = stops.length < data.count
        ? [
            ...stops,
            ...(await searchLocalFallbackStops(
              { city: data.city, country: data.country, lat: data.lat, lng: data.lng, language: data.language, interests: data.interests, ...extraScope },
              data.count - stops.length,
              stops,
            )),
          ]
        : stops;
      return { stops: filled.filter((s) => !isExtraStopExcluded(s)).slice(0, data.count), error: null };
    } catch (e) {
      console.error("suggestExtraStops failed", e);
      return { stops: [] as AIStop[], error: "exception" };
    }
  });

/* -------- Hotel recommendation for end-of-day stays (multi-day trips) -------- */

const HotelInput = z.object({
  city: z.string().min(1).max(120),
  country: z.string().min(1).max(120),
  language: z.string().min(2).max(8),
  currency: z.string().min(3).max(4),
  nearStop: z.string().max(160).optional(),
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
  radiusMeters: z.number().min(500).max(20000).optional(),
  maxBudget: z.number().min(1).max(100000).optional(),
  anonId: z.string().max(80).default(""),
});

export type AIHotel = {
  name: string;
  category: string;
  blurb: string;
  priceLocal: number;
  website?: string;
  phone?: string;
  placeId?: string;
  googleMapsUri?: string;
  lat?: number;
  lng?: number;
};

// Rough per-night price estimate (double room) by Google priceLevel — used only when we
// don't have a better signal. Values are intentionally conservative averages.
const googleHotelPriceToAverage: Record<string, number> = {
  PRICE_LEVEL_FREE: 0,
  PRICE_LEVEL_INEXPENSIVE: 55,
  PRICE_LEVEL_MODERATE: 110,
  PRICE_LEVEL_EXPENSIVE: 200,
  PRICE_LEVEL_VERY_EXPENSIVE: 350,
};

type GoogleHotelPlace = {
  id?: string;
  displayName?: { text?: string };
  formattedAddress?: string;
  location?: { latitude?: number; longitude?: number };
  rating?: number;
  userRatingCount?: number;
  priceLevel?: string;
  websiteUri?: string;
  internationalPhoneNumber?: string;
  nationalPhoneNumber?: string;
  googleMapsUri?: string;
  types?: string[];
};

function hotelCategoryFromPlace(place: GoogleHotelPlace, language: string): string {
  const isPt = language.toLowerCase().startsWith("pt");
  const t = place.types || [];
  if (t.includes("hostel")) return isPt ? "Hostel" : "Hostel";
  if (t.includes("bed_and_breakfast")) return isPt ? "B&B" : "B&B";
  if (t.includes("guest_house")) return isPt ? "Guesthouse" : "Guesthouse";
  if (t.includes("resort_hotel")) return isPt ? "Resort" : "Resort";
  if (t.includes("motel")) return isPt ? "Motel" : "Motel";
  const rating = place.rating ?? 0;
  if (rating >= 4.5) return isPt ? "Hotel 4★+" : "4★+ Hotel";
  if (rating >= 4.0) return isPt ? "Hotel 3–4★" : "3–4★ Hotel";
  return isPt ? "Hotel" : "Hotel";
}

function hotelBlurb(name: string, city: string, language: string): string {
  if (language.toLowerCase().startsWith("pt")) {
    return `${name} — opção bem localizada em ${city} para dormir sem grandes deslocações no dia seguinte.`;
  }
  return `${name} — well-located option in ${city} for an overnight stay close to the next day's route.`;
}

function placeToHotel(
  place: GoogleHotelPlace,
  data: z.infer<typeof HotelInput>,
): AIHotel | null {
  if (place.location?.latitude == null || place.location?.longitude == null) return null;
  const name = place.displayName?.text?.trim();
  if (!name) return null;
  const priceLevelAvg = place.priceLevel ? googleHotelPriceToAverage[place.priceLevel] : undefined;
  const price = priceLevelAvg && priceLevelAvg > 0
    ? priceLevelAvg
    : (data.maxBudget ?? 100);
  return {
    name,
    category: hotelCategoryFromPlace(place, data.language),
    blurb: hotelBlurb(name, data.city, data.language),
    priceLocal: price,
    website: place.websiteUri,
    phone: place.internationalPhoneNumber || place.nationalPhoneNumber,
    placeId: place.id,
    googleMapsUri: place.googleMapsUri,
    lat: place.location.latitude,
    lng: place.location.longitude,
  };
}

async function findHotelsNearMidpoint(data: z.infer<typeof HotelInput>): Promise<AIHotel[]> {
  const key = placesServerKey();
  if (!key || typeof data.lat !== "number" || typeof data.lng !== "number") return [];
  const center = { lat: data.lat, lng: data.lng };
  const radius = Math.min(20000, Math.max(800, data.radiusMeters ?? 3500));
  const languageCode = data.language.toLowerCase().startsWith("pt") ? "pt-PT" : data.language;

  try {
    const res = await cachedPlacesFetch(`${PLACES_BASE}/places:searchNearby`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": key,
        "X-Goog-FieldMask":
          "places.id,places.displayName,places.formattedAddress,places.location,places.rating,places.userRatingCount,places.priceLevel,places.websiteUri,places.internationalPhoneNumber,places.nationalPhoneNumber,places.googleMapsUri,places.types",
      },
      body: JSON.stringify({
        includedTypes: ["lodging"],
        excludedTypes: ["campground", "extended_stay_hotel"],
        maxResultCount: 20,
        rankPreference: "POPULARITY",
        locationRestriction: {
          circle: {
            center: { latitude: center.lat, longitude: center.lng },
            radius,
          },
        },
        languageCode,
      }),
    }, { kind: "lodging_nearby", ttlMs: PLACES_TTL.lodging, origin: "route_hotel_search", sku: "places_nearby_search" });
    if (!res.ok) {
      console.error("Hotel Places searchNearby failed", res.status, await res.text().catch(() => ""));
      return [];
    }
    const json = (await res.json()) as { places?: GoogleHotelPlace[] };
    const places = json.places ?? [];

    // Score: prioritise well-rated hotels with enough reviews, filter by budget if given
    const budgetCap = data.maxBudget;
    const scored = places
      .filter((p) => p.location?.latitude != null && p.location?.longitude != null)
      .filter((p) => {
        const avg = p.priceLevel ? googleHotelPriceToAverage[p.priceLevel] : undefined;
        if (budgetCap && avg != null && avg > budgetCap * 1.4) return false;
        return true;
      })
      .map((p) => {
        const dist = haversineMeters(center, { lat: p.location!.latitude!, lng: p.location!.longitude! });
        const rating = p.rating ?? 3.5;
        const reviews = Math.min(1000, p.userRatingCount ?? 0);
        // higher is better
        const score = rating * 100 + Math.log10(reviews + 1) * 30 - dist / 200;
        return { place: p, score, dist };
      })
      .sort((a, b) => b.score - a.score);

    const results: AIHotel[] = [];
    const seen = new Set<string>();
    for (const { place } of scored) {
      const hotel = placeToHotel(place, data);
      if (!hotel || !hotel.placeId || seen.has(hotel.placeId)) continue;
      seen.add(hotel.placeId);
      results.push(hotel);
      if (results.length >= 4) break;
    }
    return results;
  } catch (e) {
    console.error("findHotelsNearMidpoint failed", e);
    return [];
  }
}

export const recommendHotel = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => HotelInput.parse(d))
  .handler(async ({ data }) => {
    // 1) identidade + rate limit ANTES de qualquer chamada paga à Google.
    const { checkGoogleApiRateLimit } = await import("./quota.server");
    if (!(await checkGoogleApiRateLimit("placeRecommendation")).ok) {
      return { hotel: null as AIHotel | null, alternatives: [] as AIHotel[], error: "rate_limited" };
    }
    // 2) Google Places first (real coords, real ratings).
    const hotels = await findHotelsNearMidpoint(data);
    if (hotels.length > 0) {
      return { hotel: hotels[0], alternatives: hotels.slice(1, 4), error: null };
    }

    const { consumeQuotaInternal } = await import("./quota.server");
    const quota = await consumeQuotaInternal(data.anonId);
    if (!quota.ok) {
      return { hotel: null as AIHotel | null, alternatives: [] as AIHotel[], error: "quota_exceeded" };
    }

    // Fallback: ask the LLM for a name only (no coords available on map, but at least a suggestion).
    const apiKey = process.env.OPENAI_API_KEY;
    if (!apiKey) return { hotel: null, alternatives: [], error: "AI not configured" };

    const budgetLine = data.maxBudget
      ? ` The average price per night MUST NOT exceed ${data.maxBudget} ${data.currency} for a standard double room.`
      : "";
    const sys = `You are a local hotel concierge. Recommend ONE real, well-rated hotel/guesthouse in the given city for an overnight stay.${budgetLine} Reply ONLY through the tool call.`;
    const user = `City: ${data.city}, ${data.country}.${
      data.nearStop ? ` Should be conveniently near: ${data.nearStop}.` : ""
    } Reply in language code "${data.language}". Use currency ${data.currency} for the average price per night (standard double room).`;

    try {
      const res = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "gpt-4o-mini",
          messages: [
            { role: "system", content: sys },
            { role: "user", content: user },
          ],
          tools: [{
            type: "function",
            function: {
              name: "return_hotel",
              parameters: {
                type: "object",
                properties: {
                  name: { type: "string" },
                  category: { type: "string" },
                  blurb: { type: "string" },
                  priceLocal: { type: "number" },
                },
                required: ["name", "category", "blurb", "priceLocal"],
                additionalProperties: false,
              },
            },
          }],
          tool_choice: { type: "function", function: { name: "return_hotel" } },
        }),
      });
      if (!res.ok) return { hotel: null, alternatives: [], error: "ai_error" };
      const json = await res.json();
      const args = json.choices?.[0]?.message?.tool_calls?.[0]?.function?.arguments;
      const parsed = args ? (JSON.parse(args) as AIHotel) : null;
      return { hotel: parsed, alternatives: [] as AIHotel[], error: null };
    } catch (e) {
      console.error("recommendHotel fallback failed", e);
      return { hotel: null, alternatives: [], error: "exception" };
    }
  });


